import { EAdditiveItem, BatchDecodeResult } from '../types';

/**
 * Curated and OpenFoodFacts/EFSA verified database of 645 food additives.
 * Covers all official E-numbers (Sweeteners, Colors, Preservatives, Antioxidants, Emulsifiers, etc.).
 */
export const E_NUMBERS_DATABASE: EAdditiveItem[] = [
  {
    "code": "E965",
    "name": "Maltitol / Maltitol Syrup",
    "alternateNames": [
      "Maltitol",
      "Maltitol syrup",
      "Hydrogenated maltose",
      "Мальтит",
      "Мальтитол",
      "Мальтитний сироп",
      "E965i",
      "E965ii"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Низькокалорійний цукрозамінник та вологоутримуючий агент (75–90% солодкості сахарози).",
      "en": "Low-calorie bulk sweetener and humectant (75–90% sweetness of sucrose)."
    },
    "description": {
      "ua": "Поліол (цукровий спирт), отриманий шляхом гідрогенізації мальтози (з кукурудзяного або пшеничного крохмалю). Має приємний солодкий смак без післясмаку, низьку калорійність (~2.4 ккал/г) і не викликає карієсу зубів.",
      "en": "Polyol (sugar alcohol) produced by catalytic hydrogenation of maltose derived from starch. Imparts clean sugar-like sweetness, reduced caloric density (~2.4 kcal/g), and does not promote dental caries."
    },
    "sideEffects": {
      "ua": "При вживанні у кількостях понад 20–30 г/добу викликає надмірне газоутворення, здуття живота, кишкові спазми та осмотичний проносний ефект. Має глікемічний індекс ~35, що може помірно підвищувати рівень глюкози та інсуліну в крові.",
      "en": "Excessive intake (>20–30g/day) causes abdominal bloating, flatulence, cramps, and osmotic laxative effects. Has a glycemic index of ~35, which moderately impacts blood glucose and insulin levels."
    },
    "healthWarnings": [
      "Laxative in Excess",
      "IBS / Bloating Risk",
      "Mild Glycemic Impact"
    ],
    "regulatoryStatus": {
      "ua": "Схвалено в ЄС (EFSA) та США (FDA GRAS). Обов’язкове маркування попередження про проносний ефект при вмісті понад 10%.",
      "en": "Approved in EU (EFSA) & USA (FDA GRAS). Mandatory warning \"excessive consumption may produce laxative effects\" if content exceeds 10%."
    },
    "commonProducts": {
      "ua": "Шоколад без цукру, протеїнові батончики, жувальні гумки, випічка для діабетиків, низьковуглеводні десерти",
      "en": "Sugar-free chocolate, protein bars, chewing gums, diabetic confectionery, low-carb baked goods"
    }
  },
  {
    "code": "E960",
    "name": "Steviol Glycosides (Stevia)",
    "alternateNames": [
      "Stevia extract",
      "Rebaudioside A",
      "Стевіолглікозиди",
      "Стевія",
      "Екстракт стевії"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Натуральний безкалорійний високоінтенсивний підсолоджувач (у 200–300 разів солодший за цукор).",
      "en": "Natural zero-calorie high-intensity sweetener (200–300 times sweeter than sucrose)."
    },
    "description": {
      "ua": "Очищений екстракт із листя рослини Stevia rebaudiana. Не містить калорій, має нульовий глікемічний індекс і не впливає на рівень цукру в крові.",
      "en": "Purified extract from the leaves of Stevia rebaudiana. Zero calories, zero glycemic index, with no impact on blood glucose or insulin."
    },
    "sideEffects": {
      "ua": "Вкрай безпечний у рекомендованих дозах (ADI 4 мг/кг ваги тіла). При високих концентраціях може відчуватися гіркуватий трав’яний післясмак.",
      "en": "Extremely safe within acceptable daily intake (ADI 4 mg/kg body weight). May exhibit a slight bitter or licorice aftertaste at high concentrations."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Повністю схвалено EFSA, FDA та ВООЗ як безпечний натуральний підсолоджувач.",
      "en": "Fully approved by EFSA, FDA, and WHO as a safe natural sweetener."
    },
    "commonProducts": {
      "ua": "Дієтичні напої, спортивне харчування, йогурти без цукру, чаї, сиропи",
      "en": "Diet beverages, sports nutrition powders, sugar-free yogurts, herbal teas, table sweeteners"
    }
  },
  {
    "code": "E967",
    "name": "Xylitol",
    "alternateNames": [
      "Wood sugar",
      "Ксиліт",
      "Ксилітол"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Натуральний цукрозамінник із захисною дією проти карієсу (солодкість 100% від цукру).",
      "en": "Natural bulk sweetener with proven anti-caries dental benefits (equal sweetness to sucrose)."
    },
    "description": {
      "ua": "Цукровий спирт природного походження (з березової кори або кукурудзи). Пригнічує розмноження бактерій Streptococcus mutans у порожнині рота, захищаючи зубну емаль.",
      "en": "Natural polyol extracted from birch bark or corn cobs. Actively inhibits Streptococcus mutans oral bacteria, protecting tooth enamel."
    },
    "sideEffects": {
      "ua": "Безпечний для людей, але при передозуванні (>30-40 г) викликає проносний ефект і здуття. СМЕРТЕЛЬНО ТОКСИЧНИЙ ДЛЯ СОБАК навіть у крихітних дозах!",
      "en": "Safe for humans with mild laxative effect at high doses. EXTREMELY TOXIC AND FATAL TO DOGS even in microscopic amounts (triggers severe hypoglycemia)."
    },
    "healthWarnings": [
      "Laxative in Excess",
      "Fatal to Dogs / Pets"
    ],
    "regulatoryStatus": {
      "ua": "Схвалено EFSA, FDA та стоматологічними асоціаціями по всьому світу.",
      "en": "Approved globally by EFSA, FDA, and dental associations."
    },
    "commonProducts": {
      "ua": "Жувальні гумки для захисту зубів, льодяники, зубні пасти, цукерки без цукру",
      "en": "Dental chewing gums, mints, oral care toothpastes, diabetic confectionery"
    }
  },
  {
    "code": "E968",
    "name": "Erythritol",
    "alternateNames": [
      "Еритритол",
      "Еритріт",
      "Динячий цукор"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "caution",
    "origin": "biotech",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Натуральний безкалорійний цукрозамінник із найкращою кишковою переносимістю серед поліолів.",
      "en": "Zero-calorie natural polyol sweetener with the highest digestive tolerance of all sugar alcohols."
    },
    "description": {
      "ua": "Виробляється шляхом ферментації кукурудзяної або пшеничної глюкози дріжджами. 90% всмоктується в тонкому кишечнику та виводиться нирками у незмінному вигляді.",
      "en": "Produced by yeast fermentation of glucose. 90% absorbed in small intestine and excreted unchanged in urine, avoiding colonic fermentation."
    },
    "sideEffects": {
      "ua": "Значно менше викликає здуття, ніж мальтит. Нещодавні клінічні дослідження (Nature Medicine 2023) вивчають можливий зв’язок надвисоких рівнів еритритолу в плазмі з тромбоутворенням.",
      "en": "Well-tolerated digestive profile. Recent 2023 studies are investigating potential associations between very high circulating plasma levels and platelet reactivity."
    },
    "healthWarnings": [
      "Cardiovascular Scrutiny (High Doses)",
      "Mild Digestive Sensitivity"
    ],
    "regulatoryStatus": {
      "ua": "Схвалено EFSA та FDA без обмеження добової дози (ADI not specified).",
      "en": "Approved in EU (EFSA) and USA (FDA GRAS)."
    },
    "commonProducts": {
      "ua": "Кето-випічка, дієтичні лимонади, шоколад без цукру, столові замінники цукру",
      "en": "Keto bakery products, diet sodas, sugar-free chocolates, monk fruit blends"
    }
  },
  {
    "code": "E951",
    "name": "Aspartame",
    "alternateNames": [
      "NutraSweet",
      "Equal",
      "Аспартам"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "caution",
    "origin": "synthetic",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Синтетичний низькокалорійний підсолоджувач (у 200 разів солодший за цукор).",
      "en": "Synthetic low-calorie artificial sweetener (200x sweeter than sugar)."
    },
    "description": {
      "ua": "Дипептид метилового ефіру аспартату та фенілаланіну. Широко використовується у газованих дієтичних напоях понад 40 років.",
      "en": "Methyl ester dipeptide of aspartic acid and phenylalanine. Extensively used in diet soft drinks for over four decades."
    },
    "sideEffects": {
      "ua": "СУВОРО ПРОТИПОКАЗАНИЙ людям із фенілкетонурією (PKU). ВООЗ (IARC) у 2023 віднесла до категорії 2B («можливо канцерогенний»), проте EFSA та FDA підтверджують безпеку в межах ADI 40 мг/кг.",
      "en": "STRICTLY CONTRAINDICATED for individuals with Phenylketonuria (PKU). Classified as Group 2B by IARC (2023), though EFSA and FDA maintain safety within ADI 40-50 mg/kg."
    },
    "healthWarnings": [
      "Phenylketonuria (PKU) Hazard",
      "IARC Group 2B Scrutiny"
    ],
    "regulatoryStatus": {
      "ua": "Схвалено з обов’язковим маркуванням «Містить джерело фенілаланіну».",
      "en": "Approved with mandatory \"Contains a source of phenylalanine\" warning."
    },
    "commonProducts": {
      "ua": "Cola Zero, дієтичні газовані води, жувальні гумки Orbit/Dirol, йогурти light",
      "en": "Diet sodas (Diet Coke, Pepsi Max), sugar-free gums, diet yogurts"
    }
  },
  {
    "code": "E950",
    "name": "Acesulfame Potassium (Acesulfame K)",
    "alternateNames": [
      "Ace-K",
      "Sunett",
      "Ацесульфам калію"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "caution",
    "origin": "synthetic",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Термостабільний штучний підсолоджувач (у 200 разів солодший за цукор).",
      "en": "Heat-stable artificial sweetener (200x sweeter than sucrose)."
    },
    "description": {
      "ua": "Синтетична калієва сіль, стійка до випікання та нагрівання. Зазвичай комбінується з аспартамом або сукралозою для синергетичного маскування післясмаку.",
      "en": "Synthetic organic potassium salt. Heat stable and often blended with aspartame or sucralose to create a rounded sweetness profile."
    },
    "sideEffects": {
      "ua": "Виводиться нирками у незмінному вигляді. Потенційно може впливати на чутливість до інсуліну та склад кишкової мікробіоти при надмірному хронічному вживанні.",
      "en": "Excreted unchanged by kidneys. Debated potential effects on gut microbiome diversity and insulin sensitivity under chronic high consumption."
    },
    "healthWarnings": [
      "Gut Microbiome Disruption (High Intake)"
    ],
    "regulatoryStatus": {
      "ua": "Схвалено EFSA та FDA з ADI 9 мг/кг маси тіла.",
      "en": "Approved by EFSA and FDA with ADI of 9 mg/kg bw."
    },
    "commonProducts": {
      "ua": "Енергетичні напої без цукру, протеїнові шейки, сиропи, випічка для схуднення",
      "en": "Zero energy drinks (Monster Ultra, Red Bull Zero), protein powders, syrups"
    }
  },
  {
    "code": "E955",
    "name": "Sucralose",
    "alternateNames": [
      "Splenda",
      "Сукралоза"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "synthetic",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Високоякісний термостабільний цукрозамінник (у 600 разів солодший за сахарозу).",
      "en": "Zero-calorie high-intensity sweetener derived from sucrose (600x sweeter)."
    },
    "description": {
      "ua": "Виготовляється шляхом селективного хлорування сахарози. Має смаковий профіль, максимально наближений до звичайного цукру.",
      "en": "Organochlorine sweetener made by selective chlorination of table sugar. Retains pure sweet taste without metallic aftertaste."
    },
    "sideEffects": {
      "ua": "Не метаболізується організмом. При нагріванні понад 120°C у присутності жирів може виділяти хлорорганічні сполуки (не рекомендується для високотемпературного смаження).",
      "en": "Virtually non-metabolized. Can degrade into chloropropanols if heated above 120°C in fatty matrices (avoid for deep frying)."
    },
    "healthWarnings": [
      "Heat Degradation >120C Warning"
    ],
    "regulatoryStatus": {
      "ua": "Схвалено у понад 80 країнах світу (EFSA, FDA, Health Canada).",
      "en": "Approved worldwide by EFSA, US FDA, and Health Canada."
    },
    "commonProducts": {
      "ua": "Спортивне харчування, сиропи без цукру, протеїн, напої, протеїнове печиво",
      "en": "Sports nutrition powders, sugar-free syrups, meal replacements, protein snacks"
    }
  },
  {
    "code": "E952",
    "name": "Sodium Cyclamate",
    "alternateNames": [
      "Cyclamic acid",
      "Цикламат натрію"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "avoid",
    "origin": "synthetic",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Дешевий синтетичний підсолоджувач (у 30–50 разів солодший за цукор).",
      "en": "Low-cost synthetic sweetener (30–50x sweeter than sugar)."
    },
    "description": {
      "ua": "Синтетичний підсолоджувач, що метаболізується бактеріями кишечника у циклогексиламін.",
      "en": "Synthetic artificial sweetener metabolized by intestinal flora into cyclohexylamine."
    },
    "sideEffects": {
      "ua": "Метаболіт циклогексиламін викликав пошкодження яєчок та пухлини сечового міхура у дослідах на гризунах.",
      "en": "Cyclohexylamine metabolite demonstrated testicular toxicity and bladder tumors in rodent models."
    },
    "healthWarnings": [
      "Banned in USA",
      "Potential Reproductive Toxicity"
    ],
    "regulatoryStatus": {
      "ua": "ЗАБОРОНЕНО В США (FDA) з 1969 року. Дозволено в ЄС з суворим обмеженням дозування.",
      "en": "Banned in the USA by FDA since 1969. Permitted in EU with strict maximum limits."
    },
    "commonProducts": {
      "ua": "Дешеві газовані напої, консервовані фрукти, таблетовані підсолоджувачі",
      "en": "Budget soft drinks, canned fruits in syrup, tabletop sweetener tablets"
    }
  },
  {
    "code": "E954",
    "name": "Saccharin",
    "alternateNames": [
      "Sodium saccharin",
      "Сахарин"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "caution",
    "origin": "synthetic",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Найстаріший синтетичний підсолоджувач (у 300–400 разів солодший за цукор).",
      "en": "First commercial artificial sweetener (300–400x sweeter than sugar)."
    },
    "description": {
      "ua": "Відкритий у 1879 році. Має відчутний гіркувато-металевий післясмак при високих концентраціях.",
      "en": "Discovered in 1879. Imparts a distinctive metallic or bitter aftertaste at moderate doses."
    },
    "sideEffects": {
      "ua": "Раніше підозрювався у канцерогенності, проте сучасні дослідження спростували цей ризик для людини при помірному вживанні.",
      "en": "Previously suspected of bladder carcinogenicity in rodents; exonerated for human consumption at normal intakes."
    },
    "healthWarnings": [
      "Metallic Aftertaste"
    ],
    "regulatoryStatus": {
      "ua": "Схвалено EFSA та FDA з ADI 5 мг/кг.",
      "en": "Approved by EFSA and FDA with ADI of 5 mg/kg."
    },
    "commonProducts": {
      "ua": "Таблетки цукрозамінників для чаю/кави, зубні пасти, дієтичні джеми",
      "en": "Tabletop sweetener pills, medicated syrups, toothpastes, jams"
    }
  },
  {
    "code": "E420",
    "name": "Sorbitol / Sorbitol Syrup",
    "alternateNames": [
      "D-glucitol",
      "Сорбіт",
      "Сорбітол",
      "Сорбітний сироп"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Цукрозамінник, вологоутримувач, текстуратор і пом’якшувач випічки.",
      "en": "Sugar substitute, humectant, and moisture-retaining stabilizer."
    },
    "description": {
      "ua": "Природний цукровий спирт, що зустрічається в яблуках, грушах і чорносливі. 60% солодкості цукру.",
      "en": "Natural sugar alcohol found in fruits (plums, apples). 60% as sweet as sugar with 2.6 kcal/g."
    },
    "sideEffects": {
      "ua": "Погано засвоюється кишечником. Доза понад 15-20 г спричиняє діарею, гази та важкий дискомфорт у пацієнтів з IBS / Low-FODMAP.",
      "en": "Strong osmotic action. Intakes over 15-20g trigger diarrhea and severe cramps; strictly restricted on Low-FODMAP diet."
    },
    "healthWarnings": [
      "Strong Osmotic Laxative",
      "High FODMAP"
    ],
    "regulatoryStatus": {
      "ua": "Схвалено EFSA та FDA з обов’язковим застереженням про проносний ефект.",
      "en": "Approved in EU and USA with mandatory laxative warning."
    },
    "commonProducts": {
      "ua": "Жувальні гумки без цукру, зубні пасти, сушені фрукти, діабетичні цукерки",
      "en": "Sugar-free gums, toothpastes, confectionery, processed dried fruits"
    }
  },
  {
    "code": "E421",
    "name": "Mannitol",
    "alternateNames": [
      "Маніт",
      "Манітол"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувач та агент проти злежування (покриття для жувальних гумок).",
      "en": "Sweetener, anti-caking agent, and dry coating for gums."
    },
    "description": {
      "ua": "Ізомер сорбіту, не гігроскопічний, не вбирає вологу з повітря.",
      "en": "Non-hygroscopic sugar alcohol used for powdery confectionery coatings."
    },
    "sideEffects": {
      "ua": "Потужний осмотичний діуретик та проносний агент при дозах >10-20 г.",
      "en": "Strong osmotic diuretic and laxative at doses over 10-20g."
    },
    "healthWarnings": [
      "Laxative Risk",
      "High FODMAP"
    ],
    "regulatoryStatus": {
      "ua": "Схвалено EFSA та FDA з попереджувальним маркуванням.",
      "en": "Approved in EU and US with required warning."
    },
    "commonProducts": {
      "ua": "Покриття жувальних гумок, льодяники, фармацевтичні таблетки",
      "en": "Chewing gum dusting, compressed mints, pharmaceutical tablets"
    }
  },
  {
    "code": "E422",
    "name": "Glycerol (Glycerin)",
    "alternateNames": [
      "Glycerine",
      "Гліцерин"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Вологоутримуючий агент, розчинник екстрактів та загусник.",
      "en": "Humectant, solvent for flavorings, and thickening agent."
    },
    "description": {
      "ua": "Триосновний спирт рослинного або біотехнологічного походження. Зберігає свіжість та м’якість випічки та батончиків.",
      "en": "Trihydroxy sugar alcohol derived from vegetable oils. Keeps protein bars and baked goods moist."
    },
    "sideEffects": {
      "ua": "Цілком безпечний харчовий компонент. У рідкісних випадках надлишок може викликати легке послаблення шлунка.",
      "en": "Extremely safe dietary component with negligible side effects."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Схвалено у всьому світі без обмеження дози (Quantum Satis).",
      "en": "Approved globally as Quantum Satis."
    },
    "commonProducts": {
      "ua": "Протеїнові батончики, кекси, глазур, лікери, екстракти",
      "en": "Protein bars, soft-baked cookies, icings, flavor emulsions"
    }
  },
  {
    "code": "E917",
    "name": "Potassium iodate",
    "alternateNames": [
      "Йодат калію",
      "Wikidata: Q414599"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Йодат калію.",
      "en": "Other: Potassium iodate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E917 (Йодат калію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E917 (Potassium iodate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E905CI",
    "name": "Microcrystalline wax",
    "alternateNames": [
      "Wikidata: Q579764"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Microcrystalline wax.",
      "en": "Other: Microcrystalline wax."
    },
    "description": {
      "ua": "Харчова добавка E905CI (Microcrystalline wax), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E905CI (Microcrystalline wax)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E350II",
    "name": "Sodium hydrogen malate",
    "alternateNames": [
      "Wikidata: Q42807545"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Sodium hydrogen malate.",
      "en": "Antioxidants: Sodium hydrogen malate."
    },
    "description": {
      "ua": "Харчова добавка E350II (Sodium hydrogen malate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E350II (Sodium hydrogen malate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E919",
    "name": "Nitrosyl chloride",
    "alternateNames": [
      "Нітрозилхлорид",
      "Wikidata: Q419266"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Нітрозилхлорид.",
      "en": "Other: Nitrosyl chloride."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E919 (Нітрозилхлорид).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E919 (Nitrosyl chloride)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E465",
    "name": "Ethyl methyl cellulose",
    "alternateNames": [
      "Wikidata: Q18579463"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Ethyl methyl cellulose.",
      "en": "Thickeners & Emulsifiers: Ethyl methyl cellulose."
    },
    "description": {
      "ua": "Харчова добавка E465 (Ethyl methyl cellulose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E465 (Ethyl methyl cellulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E505",
    "name": "Ferrous carbonate",
    "alternateNames": [
      "Wikidata: Q18967227"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ferrous carbonate.",
      "en": "Other: Ferrous carbonate."
    },
    "description": {
      "ua": "Харчова добавка E505 (Ferrous carbonate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E505 (Ferrous carbonate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E307A",
    "name": "D-Alpha-tocopherol",
    "alternateNames": [
      "Wikidata: Q158348"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: D-Alpha-tocopherol.",
      "en": "Antioxidants: D-Alpha-tocopherol."
    },
    "description": {
      "ua": "Харчова добавка E307A (D-Alpha-tocopherol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E307A (D-Alpha-tocopherol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E545",
    "name": "Ammonium polyphosphate",
    "alternateNames": [
      "Поліфосфат амонію",
      "Wikidata: Q2770473"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Поліфосфат амонію.",
      "en": "Other: Ammonium polyphosphate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E545 (Поліфосфат амонію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E545 (Ammonium polyphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E479B",
    "name": "Thermally oxidised soya bean oil interacted with mono- and diglycerides of fatty acids",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Thermally oxidised soya bean oil interacted with mono- and diglycerides of fatty acids.",
      "en": "Thickeners & Emulsifiers: Thermally oxidised soya bean oil interacted with mono- and diglycerides of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E479B (Thermally oxidised soya bean oil interacted with mono- and diglycerides of fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E479B (Thermally oxidised soya bean oil interacted with mono- and diglycerides of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E399",
    "name": "Calcium lactobionate",
    "alternateNames": [
      "Wikidata: Q6138969"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium lactobionate.",
      "en": "Antioxidants: Calcium lactobionate."
    },
    "description": {
      "ua": "Харчова добавка E399 (Calcium lactobionate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E399 (Calcium lactobionate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E940",
    "name": "Dichlorodifluoromethane",
    "alternateNames": [
      "Wikidata: Q423021"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Dichlorodifluoromethane.",
      "en": "Other: Dichlorodifluoromethane."
    },
    "description": {
      "ua": "Харчова добавка E940 (Dichlorodifluoromethane), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E940 (Dichlorodifluoromethane)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E900",
    "name": "Dimethylpolysiloxane and Methylphenylpolysiloxane",
    "alternateNames": [
      "Wikidata: Q411955"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Dimethylpolysiloxane and Methylphenylpolysiloxane.",
      "en": "Other: Dimethylpolysiloxane and Methylphenylpolysiloxane."
    },
    "description": {
      "ua": "Харчова добавка E900 (Dimethylpolysiloxane and Methylphenylpolysiloxane), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E900 (Dimethylpolysiloxane and Methylphenylpolysiloxane)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E313",
    "name": "Ethyl gallate",
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ethyl gallate.",
      "en": "Antioxidants: Ethyl gallate."
    },
    "description": {
      "ua": "Харчова добавка E313 (Ethyl gallate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E313 (Ethyl gallate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E942",
    "name": "Nitrous oxide",
    "alternateNames": [
      "Оксид азотуvi:Dinitơ monoxid",
      "Wikidata: Q905750"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Оксид азотуvi:Dinitơ monoxid.",
      "en": "Antioxidants: Nitrous oxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E942 (Оксид азотуvi:Dinitơ monoxid).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E942 (Nitrous oxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E425",
    "name": "Konjac",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Konjac.",
      "en": "Thickeners & Emulsifiers: Konjac."
    },
    "description": {
      "ua": "Харчова добавка E425 (Konjac), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E425 (Konjac)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E902",
    "name": "Candelilla wax",
    "alternateNames": [
      "Wikidata: Q898937"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Candelilla wax.",
      "en": "Thickeners & Emulsifiers: Candelilla wax."
    },
    "description": {
      "ua": "Харчова добавка E902 (Candelilla wax), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E902 (Candelilla wax)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E340II",
    "name": "Dipotassium phosphate",
    "alternateNames": [
      "Wikidata: Q403721"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Dipotassium phosphate.",
      "en": "Antioxidants: Dipotassium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E340II (Dipotassium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E340II (Dipotassium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E629",
    "name": "Calcium guanylate",
    "alternateNames": [
      "Wikidata: Q5018832"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Calcium guanylate.",
      "en": "Flavor Enhancers: Calcium guanylate."
    },
    "description": {
      "ua": "Харчова добавка E629 (Calcium guanylate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E629 (Calcium guanylate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E627",
    "name": "Disodium guanylate",
    "alternateNames": [
      "Wikidata: Q905776"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Disodium guanylate.",
      "en": "Flavor Enhancers: Disodium guanylate."
    },
    "description": {
      "ua": "Харчова добавка E627 (Disodium guanylate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E627 (Disodium guanylate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E352II",
    "name": "Calcium hydrogen malate",
    "alternateNames": [
      "Wikidata: Q1356722"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium hydrogen malate.",
      "en": "Antioxidants: Calcium hydrogen malate."
    },
    "description": {
      "ua": "Харчова добавка E352II (Calcium hydrogen malate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E352II (Calcium hydrogen malate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E475",
    "name": "Polyglycerol esters of fatty acids",
    "alternateNames": [
      "Wikidata: Q4037764"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyglycerol esters of fatty acids.",
      "en": "Thickeners & Emulsifiers: Polyglycerol esters of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E475 (Polyglycerol esters of fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E475 (Polyglycerol esters of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E555",
    "name": "Potassium aluminium silicate",
    "alternateNames": [
      "Wikidata: Q18944658"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Potassium aluminium silicate.",
      "en": "Other: Potassium aluminium silicate."
    },
    "description": {
      "ua": "Харчова добавка E555 (Potassium aluminium silicate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E555 (Potassium aluminium silicate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E943A",
    "name": "Butane",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Butane.",
      "en": "Other: Butane."
    },
    "description": {
      "ua": "Харчова добавка E943A (Butane), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E943A (Butane)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E486",
    "name": "Calcium stearoyl fumarate",
    "alternateNames": [
      "Кальцій стеароїл фумарат",
      "Wikidata: Q18606662"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Кальцій стеароїл фумарат.",
      "en": "Thickeners & Emulsifiers: Calcium stearoyl fumarate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E486 (Кальцій стеароїл фумарат).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E486 (Calcium stearoyl fumarate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E539",
    "name": "Sodium thiosulfate",
    "alternateNames": [
      "Wikidata: Q339866"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Sodium thiosulfate.",
      "en": "Antioxidants: Sodium thiosulfate."
    },
    "description": {
      "ua": "Харчова добавка E539 (Sodium thiosulfate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E539 (Sodium thiosulfate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E302",
    "name": "Calcium ascorbate",
    "alternateNames": [
      "Wikidata: Q283033"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium ascorbate.",
      "en": "Antioxidants: Calcium ascorbate."
    },
    "description": {
      "ua": "Харчова добавка E302 (Calcium ascorbate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E302 (Calcium ascorbate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1203",
    "name": "Polyvinyl alcohol",
    "alternateNames": [
      "Wikidata: Q146339"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyvinyl alcohol.",
      "en": "Thickeners & Emulsifiers: Polyvinyl alcohol."
    },
    "description": {
      "ua": "Харчова добавка E1203 (Polyvinyl alcohol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1203 (Polyvinyl alcohol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E342",
    "name": "Ammonium phosphate",
    "alternateNames": [
      "Wikidata: Q18967184"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ammonium phosphate.",
      "en": "Antioxidants: Ammonium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E342 (Ammonium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E342 (Ammonium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E537",
    "name": "Ferrous hexacyanomanganate",
    "alternateNames": [
      "Wikidata: Q18602070"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ferrous hexacyanomanganate.",
      "en": "Other: Ferrous hexacyanomanganate."
    },
    "description": {
      "ua": "Харчова добавка E537 (Ferrous hexacyanomanganate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E537 (Ferrous hexacyanomanganate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E913",
    "name": "Lanolin",
    "alternateNames": [
      "Ланолін",
      "Wikidata: Q911098"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ланолін.",
      "en": "Other: Lanolin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E913 (Ланолін).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E913 (Lanolin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161A",
    "name": "Flavoxanthin",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Flavoxanthin.",
      "en": "Colors: Flavoxanthin."
    },
    "description": {
      "ua": "Харчова добавка E161A (Flavoxanthin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E161A (Flavoxanthin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E350",
    "name": "Sodium malates",
    "alternateNames": [
      "Малати натрію",
      "Wikidata: Q836788"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Малати натрію.",
      "en": "Antioxidants: Sodium malates."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E350 (Малати натрію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E350 (Sodium malates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E553B",
    "name": "Talc",
    "alternateNames": [
      "Тальк",
      "Wikidata: Q134583"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Тальк.",
      "en": "Thickeners & Emulsifiers: Talc."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E553B (Тальк).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E553B (Talc)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E319",
    "name": "Tertiary-butylhydroquinone (tbhq)",
    "alternateNames": [
      "Wikidata: Q662443"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Tertiary-butylhydroquinone (tbhq).",
      "en": "Antioxidants: Tertiary-butylhydroquinone (tbhq)."
    },
    "description": {
      "ua": "Харчова добавка E319 (Tertiary-butylhydroquinone (tbhq)), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E319 (Tertiary-butylhydroquinone (tbhq))."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E163E",
    "name": "Peonidin",
    "alternateNames": [
      "Пеонідин",
      "Wikidata: Q3072948"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Пеонідин.",
      "en": "Colors: Peonidin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E163E (Пеонідин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E163E (Peonidin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E317",
    "name": "Erythorbin acid",
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Erythorbin acid.",
      "en": "Antioxidants: Erythorbin acid."
    },
    "description": {
      "ua": "Харчова добавка E317 (Erythorbin acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E317 (Erythorbin acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E623",
    "name": "Calcium diglutamate",
    "alternateNames": [
      "Кальцію диглутамат",
      "Wikidata: Q591440"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Кальцію диглутамат.",
      "en": "Flavor Enhancers: Calcium diglutamate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E623 (Кальцію диглутамат).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E623 (Calcium diglutamate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E352",
    "name": "Calcium malates",
    "alternateNames": [
      "Wikidata: Q1356722"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium malates.",
      "en": "Antioxidants: Calcium malates."
    },
    "description": {
      "ua": "Харчова добавка E352 (Calcium malates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E352 (Calcium malates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161D",
    "name": "Rubixanthin",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Rubixanthin.",
      "en": "Colors: Rubixanthin."
    },
    "description": {
      "ua": "Харчова добавка E161D (Rubixanthin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E161D (Rubixanthin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E300",
    "name": "Ascorbic acid",
    "alternateNames": [
      "Wikidata: Q193598"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ascorbic acid.",
      "en": "Antioxidants: Ascorbic acid."
    },
    "description": {
      "ua": "Харчова добавка E300 (Ascorbic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E300 (Ascorbic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E340",
    "name": "Potassium phosphates",
    "alternateNames": [
      "Wikidata: Q3381491"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Potassium phosphates.",
      "en": "Antioxidants: Potassium phosphates."
    },
    "description": {
      "ua": "Харчова добавка E340 (Potassium phosphates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E340 (Potassium phosphates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1208",
    "name": "Polyvinylpyrrolidone-vinyl acetate copolymer",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Polyvinylpyrrolidone-vinyl acetate copolymer.",
      "en": "Other: Polyvinylpyrrolidone-vinyl acetate copolymer."
    },
    "description": {
      "ua": "Харчова добавка E1208 (Polyvinylpyrrolidone-vinyl acetate copolymer), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1208 (Polyvinylpyrrolidone-vinyl acetate copolymer)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E999",
    "name": "Quillaia extract",
    "alternateNames": [
      "Wikidata: Q118843"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Quillaia extract.",
      "en": "Thickeners & Emulsifiers: Quillaia extract."
    },
    "description": {
      "ua": "Харчова добавка E999 (Quillaia extract), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E999 (Quillaia extract)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E322I",
    "name": "Lecithin",
    "alternateNames": [
      "Лецитин",
      "Wikidata: Q241595"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Лецитин.",
      "en": "Antioxidants: Lecithin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E322I (Лецитин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E322I (Lecithin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E425II",
    "name": "Konjac glucomannan",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Konjac glucomannan.",
      "en": "Thickeners & Emulsifiers: Konjac glucomannan."
    },
    "description": {
      "ua": "Харчова добавка E425II (Konjac glucomannan), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E425II (Konjac glucomannan)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E530",
    "name": "Magnesium oxide",
    "alternateNames": [
      "Оксид магнію",
      "Wikidata: Q214769"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Оксид магнію.",
      "en": "Other: Magnesium oxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E530 (Оксид магнію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E530 (Magnesium oxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E357",
    "name": "Potassium adipate",
    "alternateNames": [
      "Wikidata: Q931445"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Potassium adipate.",
      "en": "Antioxidants: Potassium adipate."
    },
    "description": {
      "ua": "Харчова добавка E357 (Potassium adipate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E357 (Potassium adipate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E359",
    "name": "Ammonium adipate",
    "alternateNames": [
      "Wikidata: Q4747299"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ammonium adipate.",
      "en": "Antioxidants: Ammonium adipate."
    },
    "description": {
      "ua": "Харчова добавка E359 (Ammonium adipate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E359 (Ammonium adipate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E312",
    "name": "Dodecyl gallate",
    "alternateNames": [
      "Wikidata: Q418209"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Dodecyl gallate.",
      "en": "Antioxidants: Dodecyl gallate."
    },
    "description": {
      "ua": "Харчова добавка E312 (Dodecyl gallate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E312 (Dodecyl gallate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E903",
    "name": "Carnauba wax",
    "alternateNames": [
      "Карнаубський віск",
      "Wikidata: Q839494"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Карнаубський віск.",
      "en": "Other: Carnauba wax."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E903 (Карнаубський віск).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E903 (Carnauba wax)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E515II",
    "name": "Potassium hydrogen sulphate",
    "alternateNames": [
      "Wikidata: Q193054"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Potassium hydrogen sulphate.",
      "en": "Other: Potassium hydrogen sulphate."
    },
    "description": {
      "ua": "Харчова добавка E515II (Potassium hydrogen sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E515II (Potassium hydrogen sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E310",
    "name": "Propyl gallate",
    "alternateNames": [
      "Wikidata: Q608726"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Propyl gallate.",
      "en": "Antioxidants: Propyl gallate."
    },
    "description": {
      "ua": "Харчова добавка E310 (Propyl gallate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E310 (Propyl gallate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E343II",
    "name": "Dimagnesium phosphate",
    "alternateNames": [
      "Wikidata: Q14325507"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Dimagnesium phosphate.",
      "en": "Antioxidants: Dimagnesium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E343II (Dimagnesium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E343II (Dimagnesium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E309",
    "name": "Delta-tocopherol",
    "alternateNames": [
      "Дельта-токоферол",
      "Wikidata: Q155751"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Дельта-токоферол.",
      "en": "Antioxidants: Delta-tocopherol."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E309 (Дельта-токоферол).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E309 (Delta-tocopherol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E307",
    "name": "Alpha-tocopherol",
    "alternateNames": [
      "Wikidata: Q158348"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Alpha-tocopherol.",
      "en": "Antioxidants: Alpha-tocopherol."
    },
    "description": {
      "ua": "Харчова добавка E307 (Alpha-tocopherol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E307 (Alpha-tocopherol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E335II",
    "name": "Disodium tartrate",
    "alternateNames": [
      "Wikidata: Q17997701"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Disodium tartrate.",
      "en": "Antioxidants: Disodium tartrate."
    },
    "description": {
      "ua": "Харчова добавка E335II (Disodium tartrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E335II (Disodium tartrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E349",
    "name": "Ammonium malate",
    "alternateNames": [
      "Wikidata: Q4747303"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ammonium malate.",
      "en": "Antioxidants: Ammonium malate."
    },
    "description": {
      "ua": "Харчова добавка E349 (Ammonium malate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E349 (Ammonium malate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E953",
    "name": "Isomalt",
    "alternateNames": [
      "Ізомальт",
      "Wikidata: Q412068"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Ізомальт.",
      "en": "Sweeteners: Isomalt."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E953 (Ізомальт).",
      "en": "ISOMALT is a sugar substitute, a type of sugar alcohol used primarily for its sugar-like physical properties. It has little to no impact on blood sugar levels, and does not stimulate the release of insulin. Isomalt is widely used for the production of sugar-free candy, especially hard-boiled candy."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E900A",
    "name": "Dimethyl polysiloxane",
    "alternateNames": [
      "Wikidata: Q411955"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Dimethyl polysiloxane.",
      "en": "Thickeners & Emulsifiers: Dimethyl polysiloxane."
    },
    "description": {
      "ua": "Харчова добавка E900A (Dimethyl polysiloxane), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E900A (Dimethyl polysiloxane)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E515",
    "name": "Potassium sulphates",
    "alternateNames": [
      "Wikidata: Q193054"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Potassium sulphates.",
      "en": "Other: Potassium sulphates."
    },
    "description": {
      "ua": "Харчова добавка E515 (Potassium sulphates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E515 (Potassium sulphates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E121",
    "name": "Citrus Red 2",
    "alternateNames": [
      "Wikidata: Q2653981"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Citrus Red 2.",
      "en": "Colors: Citrus Red 2."
    },
    "description": {
      "ua": "Харчова добавка E121 (Citrus Red 2), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E121 (Citrus Red 2)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E154",
    "name": "Brown FK",
    "alternateNames": [
      "Wikidata: Q425437"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "banned",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Brown FK.",
      "en": "Colors: Brown FK."
    },
    "description": {
      "ua": "Харчова добавка E154 (Brown FK), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E154 (Brown FK)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E910",
    "name": "Wax ester",
    "alternateNames": [
      "Wikidata: Q7975685"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Wax ester.",
      "en": "Other: Wax ester."
    },
    "description": {
      "ua": "Харчова добавка E910 (Wax ester), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E910 (Wax ester)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E171",
    "name": "Titanium dioxide",
    "alternateNames": [
      "Білила титанові",
      "Wikidata: Q193521"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "banned",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Білила титанові.",
      "en": "Colors: Titanium dioxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E171 (Білила титанові).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E171 (Titanium dioxide)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E622",
    "name": "Monopotassium glutamate",
    "alternateNames": [
      "Wikidata: Q3029787"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Monopotassium glutamate.",
      "en": "Flavor Enhancers: Monopotassium glutamate."
    },
    "description": {
      "ua": "Харчова добавка E622 (Monopotassium glutamate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E622 (Monopotassium glutamate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E265",
    "name": "Dehydroacetic acid",
    "alternateNames": [
      "Wikidata: Q920725"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Dehydroacetic acid.",
      "en": "Preservatives: Dehydroacetic acid."
    },
    "description": {
      "ua": "Харчова добавка E265 (Dehydroacetic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E265 (Dehydroacetic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E353",
    "name": "Metatartaric acid",
    "alternateNames": [
      "Wikidata: Q17997708"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Metatartaric acid.",
      "en": "Antioxidants: Metatartaric acid."
    },
    "description": {
      "ua": "Харчова добавка E353 (Metatartaric acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E353 (Metatartaric acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E470B",
    "name": "Magnesium salts of fatty acids",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Magnesium salts of fatty acids.",
      "en": "Thickeners & Emulsifiers: Magnesium salts of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E470B (Magnesium salts of fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E470B (Magnesium salts of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E949",
    "name": "Hydrogen",
    "alternateNames": [
      "Wikidata: Q556"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Hydrogen.",
      "en": "Other: Hydrogen."
    },
    "description": {
      "ua": "Харчова добавка E949 (Hydrogen), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E949 (Hydrogen)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E907",
    "name": "Hydrogenated poly-1-decene",
    "alternateNames": [
      "Wikidata: Q18967207"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Hydrogenated poly-1-decene.",
      "en": "Other: Hydrogenated poly-1-decene."
    },
    "description": {
      "ua": "Харчова добавка E907 (Hydrogenated poly-1-decene), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E907 (Hydrogenated poly-1-decene)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E640I",
    "name": "Glycine",
    "alternateNames": [
      "Wikidata: Q18967228"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Glycine.",
      "en": "Other: Glycine."
    },
    "description": {
      "ua": "Харчова добавка E640I (Glycine), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E640I (Glycine)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E909",
    "name": "Spermaceti",
    "alternateNames": [
      "Спермацет",
      "Wikidata: Q369682"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Спермацет.",
      "en": "Other: Spermaceti."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E909 (Спермацет).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E909 (Spermaceti)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1450",
    "name": "Starch sodium octenyl succinate",
    "alternateNames": [
      "Wikidata: Q18967290"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Starch sodium octenyl succinate.",
      "en": "Thickeners & Emulsifiers: Starch sodium octenyl succinate."
    },
    "description": {
      "ua": "Харчова добавка E1450 (Starch sodium octenyl succinate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1450 (Starch sodium octenyl succinate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E450IV",
    "name": "Dipotassium dihydrogenpyrophosphate",
    "alternateNames": [
      "Дикалій дигідроген пірофосфат",
      "Wikidata: Q290828"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Дикалій дигідроген пірофосфат.",
      "en": "Thickeners & Emulsifiers: Dipotassium dihydrogenpyrophosphate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E450IV (Дикалій дигідроген пірофосфат).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E450IV (Dipotassium dihydrogenpyrophosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E392",
    "name": "Extracts of rosemary",
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Extracts of rosemary.",
      "en": "Antioxidants: Extracts of rosemary."
    },
    "description": {
      "ua": "Харчова добавка E392 (Extracts of rosemary), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E392 (Extracts of rosemary)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E225",
    "name": "Calcium Disulphite",
    "alternateNames": [
      "Wikidata: Q417109"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Calcium Disulphite.",
      "en": "Preservatives: Calcium Disulphite."
    },
    "description": {
      "ua": "Харчова добавка E225 (Calcium Disulphite), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E225 (Calcium Disulphite)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160E",
    "name": "Beta-apo-8′-carotenal (c30)",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Beta-apo-8′-carotenal (c30).",
      "en": "Colors: Beta-apo-8′-carotenal (c30)."
    },
    "description": {
      "ua": "Харчова добавка E160E (Beta-apo-8′-carotenal (c30)), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160E (Beta-apo-8′-carotenal (c30))."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E452IV",
    "name": "Calcium polyphosphate",
    "alternateNames": [
      "Wikidata: Q11867365"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Calcium polyphosphate.",
      "en": "Thickeners & Emulsifiers: Calcium polyphosphate."
    },
    "description": {
      "ua": "Харчова добавка E452IV (Calcium polyphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E452IV (Calcium polyphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E335",
    "name": "Sodium tartrates",
    "alternateNames": [
      "Wikidata: Q58641683"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Sodium tartrates.",
      "en": "Antioxidants: Sodium tartrates."
    },
    "description": {
      "ua": "Харчова добавка E335 (Sodium tartrates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E335 (Sodium tartrates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E620",
    "name": "Glutamic acid",
    "alternateNames": [
      "Wikidata: Q181136"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Glutamic acid.",
      "en": "Flavor Enhancers: Glutamic acid."
    },
    "description": {
      "ua": "Харчова добавка E620 (Glutamic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E620 (Glutamic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E472B",
    "name": "Lactic acid esters of mono- and diglycerides of fatty acids",
    "alternateNames": [
      "Естери гліцерину та молочної і жирних кислот",
      "Wikidata: Q18967238"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Естери гліцерину та молочної і жирних кислот.",
      "en": "Thickeners & Emulsifiers: Lactic acid esters of mono- and diglycerides of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E472B (Естери гліцерину та молочної і жирних кислот).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E472B (Lactic acid esters of mono- and diglycerides of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E104",
    "name": "Quinoline yellow",
    "alternateNames": [
      "Wikidata: Q420123"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Quinoline yellow.",
      "en": "Colors: Quinoline yellow."
    },
    "description": {
      "ua": "Харчова добавка E104 (Quinoline yellow), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E104 (Quinoline yellow)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E303",
    "name": "Potassium ascorbate",
    "alternateNames": [
      "Аскорбат калію",
      "Wikidata: Q56410901"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Аскорбат калію.",
      "en": "Antioxidants: Potassium ascorbate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E303 (Аскорбат калію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E303 (Potassium ascorbate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E488",
    "name": "Ethoxylated Mono- and Di-Glycerides",
    "alternateNames": [
      "Wikidata: Q18967225"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Ethoxylated Mono- and Di-Glycerides.",
      "en": "Thickeners & Emulsifiers: Ethoxylated Mono- and Di-Glycerides."
    },
    "description": {
      "ua": "Харчова добавка E488 (Ethoxylated Mono- and Di-Glycerides), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E488 (Ethoxylated Mono- and Di-Glycerides)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E141I",
    "name": "Copper complexes of chlorophylls",
    "alternateNames": [
      "Wikidata: Q18967203"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Copper complexes of chlorophylls.",
      "en": "Colors: Copper complexes of chlorophylls."
    },
    "description": {
      "ua": "Харчова добавка E141I (Copper complexes of chlorophylls), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E141I (Copper complexes of chlorophylls)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E343",
    "name": "Magnesium phosphates",
    "alternateNames": [
      "Wikidata: Q6731399"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Magnesium phosphates.",
      "en": "Antioxidants: Magnesium phosphates."
    },
    "description": {
      "ua": "Харчова добавка E343 (Magnesium phosphates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E343 (Magnesium phosphates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E912",
    "name": "Montanic acid esters",
    "alternateNames": [
      "Wikidata: Q18935695"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Montanic acid esters.",
      "en": "Other: Montanic acid esters."
    },
    "description": {
      "ua": "Харчова добавка E912 (Montanic acid esters), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E912 (Montanic acid esters)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E101I",
    "name": "Riboflavin",
    "alternateNames": [
      "Wikidata: Q130365"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Riboflavin.",
      "en": "Colors: Riboflavin."
    },
    "description": {
      "ua": "Харчова добавка E101I (Riboflavin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E101I (Riboflavin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160AII",
    "name": "Plant carotenes",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Plant carotenes.",
      "en": "Colors: Plant carotenes."
    },
    "description": {
      "ua": "Харчова добавка E160AII (Plant carotenes), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160AII (Plant carotenes)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161",
    "name": "Xanthophylls",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Xanthophylls.",
      "en": "Colors: Xanthophylls."
    },
    "description": {
      "ua": "Харчова добавка E161 (Xanthophylls), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E161 (Xanthophylls)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E959",
    "name": "Neohesperidine dihydrochalcone",
    "alternateNames": [
      "Wikidata: Q424595"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Neohesperidine dihydrochalcone.",
      "en": "Sweeteners: Neohesperidine dihydrochalcone."
    },
    "description": {
      "ua": "Харчова добавка E959 (Neohesperidine dihydrochalcone), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E959 (Neohesperidine dihydrochalcone)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E381",
    "name": "Ferric ammonium citrate",
    "alternateNames": [
      "Wikidata: Q473522"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ferric ammonium citrate.",
      "en": "Antioxidants: Ferric ammonium citrate."
    },
    "description": {
      "ua": "Харчова добавка E381 (Ferric ammonium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E381 (Ferric ammonium citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E957",
    "name": "Thaumatin",
    "alternateNames": [
      "Тауматин",
      "Wikidata: Q414048"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Тауматин.",
      "en": "Sweeteners: Thaumatin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E957 (Тауматин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E957 (Thaumatin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E905B",
    "name": "Petroleum jelly",
    "alternateNames": [
      "Вазелін",
      "Wikidata: Q457239"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Вазелін.",
      "en": "Other: Petroleum jelly."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E905B (Вазелін).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E905B (Petroleum jelly)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E163C",
    "name": "Malvidin",
    "alternateNames": [
      "Wikidata: Q137220"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Malvidin.",
      "en": "Colors: Malvidin."
    },
    "description": {
      "ua": "Харчова добавка E163C (Malvidin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E163C (Malvidin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E424",
    "name": "Curdlan",
    "alternateNames": [
      "Курдлан",
      "Wikidata: Q616242"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Курдлан.",
      "en": "Thickeners & Emulsifiers: Curdlan."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E424 (Курдлан).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E424 (Curdlan)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E451",
    "name": "Triphosphates",
    "alternateNames": [
      "Трифосфати",
      "Wikidata: Q29145"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Трифосфати.",
      "en": "Thickeners & Emulsifiers: Triphosphates."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E451 (Трифосфати).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E451 (Triphosphates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E571",
    "name": "E571",
    "alternateNames": [
      "Стеарат амонію"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Стеарат амонію.",
      "en": "Other: E571."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E571 (Стеарат амонію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E571 (E571)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E150B",
    "name": "Caustic sulphite caramel",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Caustic sulphite caramel.",
      "en": "Colors: Caustic sulphite caramel."
    },
    "description": {
      "ua": "Харчова добавка E150B (Caustic sulphite caramel), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E150B (Caustic sulphite caramel)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E626",
    "name": "Guanylic acid",
    "alternateNames": [
      "Wikidata: Q422473"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Guanylic acid.",
      "en": "Flavor Enhancers: Guanylic acid."
    },
    "description": {
      "ua": "Харчова добавка E626 (Guanylic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E626 (Guanylic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E474",
    "name": "Sucroglycerides",
    "alternateNames": [
      "Wikidata: Q18608931"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sucroglycerides.",
      "en": "Thickeners & Emulsifiers: Sucroglycerides."
    },
    "description": {
      "ua": "Харчова добавка E474 (Sucroglycerides), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E474 (Sucroglycerides)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E554",
    "name": "Sodium aluminium silicate",
    "alternateNames": [
      "Wikidata: Q18967277"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Sodium aluminium silicate.",
      "en": "Other: Sodium aluminium silicate."
    },
    "description": {
      "ua": "Харчова добавка E554 (Sodium aluminium silicate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E554 (Sodium aluminium silicate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1421",
    "name": "Acetylated starch",
    "alternateNames": [
      "Wikidata: Q18944665"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Acetylated starch.",
      "en": "Thickeners & Emulsifiers: Acetylated starch."
    },
    "description": {
      "ua": "Харчова добавка E1421 (Acetylated starch), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1421 (Acetylated starch)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E521",
    "name": "Aluminium sodium sulphate",
    "alternateNames": [
      "Wikidata: Q413559"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Aluminium sodium sulphate.",
      "en": "Other: Aluminium sodium sulphate."
    },
    "description": {
      "ua": "Харчова добавка E521 (Aluminium sodium sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E521 (Aluminium sodium sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1104",
    "name": "Lipase",
    "alternateNames": [
      "Ліпаза",
      "Wikidata: Q380967"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ліпаза.",
      "en": "Other: Lipase."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1104 (Ліпаза).",
      "en": "A LIPASE is any enzyme that catalyzes the hydrolysis of fats -lipids-. Lipases serve important roles in human practices as ancient as yogurt and cheese fermentation. Lipases are generally animal sourced, but can also be sourced microbially."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1001",
    "name": "Choline salt",
    "alternateNames": [
      "Холінова сіль",
      "Wikidata: Q18668392"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Холінова сіль.",
      "en": "Other: Choline salt."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1001 (Холінова сіль).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1001 (Choline salt)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1401",
    "name": "Acid-treated modified starch",
    "alternateNames": [
      "Модифікований крохмаль",
      "Wikidata: Q18967250"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Модифікований крохмаль.",
      "en": "Thickeners & Emulsifiers: Acid-treated modified starch."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1401 (Модифікований крохмаль).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1401 (Acid-treated modified starch)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E308",
    "name": "Gamme-tocopherol",
    "alternateNames": [
      "Гамма-токоферол",
      "Wikidata: Q155753"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Гамма-токоферол.",
      "en": "Antioxidants: Gamme-tocopherol."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E308 (Гамма-токоферол).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E308 (Gamme-tocopherol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E483",
    "name": "Stearyl tartrate",
    "alternateNames": [
      "Wikidata: Q673260"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Stearyl tartrate.",
      "en": "Thickeners & Emulsifiers: Stearyl tartrate."
    },
    "description": {
      "ua": "Харчова добавка E483 (Stearyl tartrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E483 (Stearyl tartrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E440AII",
    "name": "Sodium pectate",
    "alternateNames": [
      "Wikidata: Q188154"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium pectate.",
      "en": "Thickeners & Emulsifiers: Sodium pectate."
    },
    "description": {
      "ua": "Харчова добавка E440AII (Sodium pectate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E440AII (Sodium pectate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E541I",
    "name": "Acid sodium aluminium phosphate",
    "alternateNames": [
      "Wikidata: Q1220360"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Acid sodium aluminium phosphate.",
      "en": "Thickeners & Emulsifiers: Acid sodium aluminium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E541I (Acid sodium aluminium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E541I (Acid sodium aluminium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E916",
    "name": "Calcium iodate",
    "alternateNames": [
      "Wikidata: Q2185998"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Calcium iodate.",
      "en": "Other: Calcium iodate."
    },
    "description": {
      "ua": "Харчова добавка E916 (Calcium iodate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E916 (Calcium iodate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E441",
    "name": "Superglycerinated hydrogenated rapeseed oil",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Superglycerinated hydrogenated rapeseed oil.",
      "en": "Thickeners & Emulsifiers: Superglycerinated hydrogenated rapeseed oil."
    },
    "description": {
      "ua": "Харчова добавка E441 (Superglycerinated hydrogenated rapeseed oil), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E441 (Superglycerinated hydrogenated rapeseed oil)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E401",
    "name": "Sodium alginate",
    "alternateNames": [
      "Альгінат натрію",
      "Wikidata: Q1187513"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Альгінат натрію.",
      "en": "Thickeners & Emulsifiers: Sodium alginate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E401 (Альгінат натрію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E401 (Sodium alginate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E501I",
    "name": "Potassium carbonate",
    "alternateNames": [
      "Карбонат калію",
      "Wikidata: Q379885"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Карбонат калію.",
      "en": "Thickeners & Emulsifiers: Potassium carbonate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E501I (Карбонат калію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E501I (Potassium carbonate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E544",
    "name": "Calcium polyphosphate",
    "alternateNames": [
      "Поліфосфат кальцію",
      "Wikidata: Q18967198"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Поліфосфат кальцію.",
      "en": "Other: Calcium polyphosphate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E544 (Поліфосфат кальцію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E544 (Calcium polyphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E464",
    "name": "Hydroxypropyl methyl cellulose",
    "alternateNames": [
      "Wikidata: Q423846"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Hydroxypropyl methyl cellulose.",
      "en": "Thickeners & Emulsifiers: Hydroxypropyl methyl cellulose."
    },
    "description": {
      "ua": "Харчова добавка E464 (Hydroxypropyl methyl cellulose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E464 (Hydroxypropyl methyl cellulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E504",
    "name": "Magnesium carbonates",
    "alternateNames": [
      "Wikidata: Q407931"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Magnesium carbonates.",
      "en": "Other: Magnesium carbonates."
    },
    "description": {
      "ua": "Харчова добавка E504 (Magnesium carbonates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E504 (Magnesium carbonates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E211",
    "name": "Sodium benzoate",
    "alternateNames": [
      "Wikidata: Q423971"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium benzoate.",
      "en": "Preservatives: Sodium benzoate."
    },
    "description": {
      "ua": "Харчова добавка E211 (Sodium benzoate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "SODIUM BENZOATE is a substance which has the chemical formula NaC7H5O2"
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1521",
    "name": "Polyethylene glycol",
    "alternateNames": [
      "Wikidata: Q410083"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Polyethylene glycol.",
      "en": "Other: Polyethylene glycol."
    },
    "description": {
      "ua": "Харчова добавка E1521 (Polyethylene glycol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1521 (Polyethylene glycol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E316",
    "name": "Sodium erythorbate",
    "alternateNames": [
      "Wikidata: Q417003"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Sodium erythorbate.",
      "en": "Antioxidants: Sodium erythorbate."
    },
    "description": {
      "ua": "Харчова добавка E316 (Sodium erythorbate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "SODIUM ERYTHORBATE is the sodium salt of erythorbic acid. Sodium erythorbate is produced from sugars derived from different sources, such as beets, sugar cane, and corn. It is used predominantly in meats, poultry, and soft drinks."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1105",
    "name": "Lysozyme",
    "alternateNames": [
      "Лізоцим",
      "Wikidata: Q21114915"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Лізоцим.",
      "en": "Preservatives: Lysozyme."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1105 (Лізоцим).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1105 (Lysozyme)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E908",
    "name": "Rice bran wax",
    "alternateNames": [
      "Віск рисових висівок",
      "Wikidata: Q725234"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Віск рисових висівок.",
      "en": "Other: Rice bran wax."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E908 (Віск рисових висівок).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E908 (Rice bran wax)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E948",
    "name": "Oxygen",
    "alternateNames": [
      "Wikidata: Q629"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Oxygen.",
      "en": "Other: Oxygen."
    },
    "description": {
      "ua": "Харчова добавка E948 (Oxygen), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E948 (Oxygen)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E130",
    "name": "Indanthrene blue RS",
    "alternateNames": [
      "Wikidata: Q185929"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Indanthrene blue RS.",
      "en": "Colors: Indanthrene blue RS."
    },
    "description": {
      "ua": "Харчова добавка E130 (Indanthrene blue RS), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E130 (Indanthrene blue RS)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E451II",
    "name": "Pentapotassium triphosphate",
    "alternateNames": [
      "Wikidata: Q15632716"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Pentapotassium triphosphate.",
      "en": "Thickeners & Emulsifiers: Pentapotassium triphosphate."
    },
    "description": {
      "ua": "Харчова добавка E451II (Pentapotassium triphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E451II (Pentapotassium triphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E282",
    "name": "Calcium propionate",
    "alternateNames": [
      "Wikidata: Q417394"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Calcium propionate.",
      "en": "Preservatives: Calcium propionate."
    },
    "description": {
      "ua": "Харчова добавка E282 (Calcium propionate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "CALCIUM PROPIONATE has the formula Ca-C2H5COO-2. Calcium propionate is used as a preservative in a wide variety of products, including but not limited to:bread, other baked goods, processed meat, whey, and other dairy products."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E487",
    "name": "Sodium dodecyl sulfate",
    "alternateNames": [
      "Wikidata: Q422241"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium dodecyl sulfate.",
      "en": "Thickeners & Emulsifiers: Sodium dodecyl sulfate."
    },
    "description": {
      "ua": "Харчова добавка E487 (Sodium dodecyl sulfate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E487 (Sodium dodecyl sulfate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E280",
    "name": "Propionic acid",
    "alternateNames": [
      "Wikidata: Q422956"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Propionic acid.",
      "en": "Preservatives: Propionic acid."
    },
    "description": {
      "ua": "Харчова добавка E280 (Propionic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E280 (Propionic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E489",
    "name": "Methyl glucoside-coconut oil ester",
    "alternateNames": [
      "Wikidata: Q18967245"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Methyl glucoside-coconut oil ester.",
      "en": "Thickeners & Emulsifiers: Methyl glucoside-coconut oil ester."
    },
    "description": {
      "ua": "Харчова добавка E489 (Methyl glucoside-coconut oil ester), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E489 (Methyl glucoside-coconut oil ester)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E504II",
    "name": "Magnesium hydroxide carbonate",
    "alternateNames": [
      "Wikidata: Q407931"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Magnesium hydroxide carbonate.",
      "en": "Other: Magnesium hydroxide carbonate."
    },
    "description": {
      "ua": "Харчова добавка E504II (Magnesium hydroxide carbonate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E504II (Magnesium hydroxide carbonate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E132",
    "name": "Indigotine",
    "alternateNames": [
      "Wikidata: Q410120"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Indigotine.",
      "en": "Colors: Indigotine."
    },
    "description": {
      "ua": "Харчова добавка E132 (Indigotine), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E132 (Indigotine)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E958",
    "name": "Glycyrrhizin",
    "alternateNames": [
      "Гліциризин",
      "Wikidata: Q418705"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Гліциризин.",
      "en": "Sweeteners: Glycyrrhizin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E958 (Гліциризин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E958 (Glycyrrhizin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E536",
    "name": "Potassium ferrocyanide",
    "alternateNames": [
      "Wikidata: Q422017"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Potassium ferrocyanide.",
      "en": "Other: Potassium ferrocyanide."
    },
    "description": {
      "ua": "Харчова добавка E536 (Potassium ferrocyanide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E536 (Potassium ferrocyanide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160BI",
    "name": "Annatto bixin",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Annatto bixin.",
      "en": "Colors: Annatto bixin."
    },
    "description": {
      "ua": "Харчова добавка E160BI (Annatto bixin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160BI (Annatto bixin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E631",
    "name": "Disodium inosinate",
    "alternateNames": [
      "Wikidata: Q905782"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Disodium inosinate.",
      "en": "Flavor Enhancers: Disodium inosinate."
    },
    "description": {
      "ua": "Харчова добавка E631 (Disodium inosinate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E631 (Disodium inosinate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160C",
    "name": "Paprika extract",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Paprika extract.",
      "en": "Colors: Paprika extract."
    },
    "description": {
      "ua": "Харчова добавка E160C (Paprika extract), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160C (Paprika extract)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E491",
    "name": "Sorbitan monostearate",
    "alternateNames": [
      "Wikidata: Q907898"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sorbitan monostearate.",
      "en": "Thickeners & Emulsifiers: Sorbitan monostearate."
    },
    "description": {
      "ua": "Харчова добавка E491 (Sorbitan monostearate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E491 (Sorbitan monostearate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E500III",
    "name": "Sodium sesquicarbonate",
    "alternateNames": [
      "Wikidata: Q2234978"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium sesquicarbonate.",
      "en": "Thickeners & Emulsifiers: Sodium sesquicarbonate."
    },
    "description": {
      "ua": "Харчова добавка E500III (Sodium sesquicarbonate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E500III (Sodium sesquicarbonate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1405",
    "name": "Enzyme treated starch",
    "alternateNames": [
      "Wikidata: Q18967222"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Enzyme treated starch.",
      "en": "Thickeners & Emulsifiers: Enzyme treated starch."
    },
    "description": {
      "ua": "Харчова добавка E1405 (Enzyme treated starch), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1405 (Enzyme treated starch)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E306",
    "name": "Tocopherol-rich extract",
    "alternateNames": [
      "Wikidata: Q141180"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Tocopherol-rich extract.",
      "en": "Antioxidants: Tocopherol-rich extract."
    },
    "description": {
      "ua": "Харчова добавка E306 (Tocopherol-rich extract), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E306 (Tocopherol-rich extract)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E918",
    "name": "Nitrogen oxides",
    "alternateNames": [
      "Wikidata: Q18967254"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Nitrogen oxides.",
      "en": "Other: Nitrogen oxides."
    },
    "description": {
      "ua": "Харчова добавка E918 (Nitrogen oxides), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E918 (Nitrogen oxides)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1412",
    "name": "Distarch phosphate",
    "alternateNames": [
      "Wikidata: Q18967218"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Distarch phosphate.",
      "en": "Thickeners & Emulsifiers: Distarch phosphate."
    },
    "description": {
      "ua": "Харчова добавка E1412 (Distarch phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1412 (Distarch phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E482",
    "name": "Calcium stearoyl-2-lactylate",
    "alternateNames": [
      "Wikidata: Q788535"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Calcium stearoyl-2-lactylate.",
      "en": "Thickeners & Emulsifiers: Calcium stearoyl-2-lactylate."
    },
    "description": {
      "ua": "Харчова добавка E482 (Calcium stearoyl-2-lactylate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E482 (Calcium stearoyl-2-lactylate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E472G",
    "name": "Succinylated monoglycerides",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Succinylated monoglycerides.",
      "en": "Thickeners & Emulsifiers: Succinylated monoglycerides."
    },
    "description": {
      "ua": "Харчова добавка E472G (Succinylated monoglycerides), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E472G (Succinylated monoglycerides)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1505",
    "name": "Triethyl citrate",
    "alternateNames": [
      "Wikidata: Q418057"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Triethyl citrate.",
      "en": "Thickeners & Emulsifiers: Triethyl citrate."
    },
    "description": {
      "ua": "Харчова добавка E1505 (Triethyl citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1505 (Triethyl citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E356",
    "name": "Sodium adipate",
    "alternateNames": [
      "Wikidata: Q423296"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Sodium adipate.",
      "en": "Antioxidants: Sodium adipate."
    },
    "description": {
      "ua": "Харчова добавка E356 (Sodium adipate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E356 (Sodium adipate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E905G",
    "name": "Mineral oil medium and low viscosity class III",
    "alternateNames": [
      "Wikidata: Q579764"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Mineral oil medium and low viscosity class III.",
      "en": "Other: Mineral oil medium and low viscosity class III."
    },
    "description": {
      "ua": "Харчова добавка E905G (Mineral oil medium and low viscosity class III), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E905G (Mineral oil medium and low viscosity class III)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E339III",
    "name": "Trisodium phosphate",
    "alternateNames": [
      "Wikidata: Q3249706"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Trisodium phosphate.",
      "en": "Preservatives: Trisodium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E339III (Trisodium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "TRISODIUM PHOSPHATE is the inorganic compound with the chemical formula Na3PO4."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E514II",
    "name": "Sodium hydrogen sulphate",
    "alternateNames": [
      "Wikidata: Q211737"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Sodium hydrogen sulphate.",
      "en": "Other: Sodium hydrogen sulphate."
    },
    "description": {
      "ua": "Харчова добавка E514II (Sodium hydrogen sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E514II (Sodium hydrogen sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E628",
    "name": "Dipotassium guanylate",
    "alternateNames": [
      "Wikidata: Q5280047"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Dipotassium guanylate.",
      "en": "Flavor Enhancers: Dipotassium guanylate."
    },
    "description": {
      "ua": "Харчова добавка E628 (Dipotassium guanylate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E628 (Dipotassium guanylate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E224",
    "name": "Potassium metabisulphite",
    "alternateNames": [
      "Wikidata: Q417881"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Potassium metabisulphite.",
      "en": "Preservatives: Potassium metabisulphite."
    },
    "description": {
      "ua": "Харчова добавка E224 (Potassium metabisulphite), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "POTASSIUM METABISULFITE, K2S2O5, also known as potassium pyrosulfite, is a white crystalline powder with a pungent sulfur odour. The main use for the chemical is as an antioxidant or chemical sterilant."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E251",
    "name": "Sodium nitrate",
    "alternateNames": [
      "Нітрат натрію",
      "Wikidata: Q184373"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Нітрат натрію.",
      "en": "Preservatives: Sodium nitrate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E251 (Нітрат натрію).",
      "en": "SODIUM NITRATE is the chemical compound with the formula NaNO3. Sodium nitrate is a food additive used as a preservative and colour fixative in cured meats and poultry"
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E105",
    "name": "Fast Yellow AB",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Fast Yellow AB.",
      "en": "Colors: Fast Yellow AB."
    },
    "description": {
      "ua": "Харчова добавка E105 (Fast Yellow AB), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E105 (Fast Yellow AB)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E334",
    "name": "L(+)-tartaric acid",
    "alternateNames": [
      "Винна кислота",
      "Wikidata: Q194322"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Винна кислота.",
      "en": "Antioxidants: L(+)-tartaric acid."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E334 (Винна кислота).",
      "en": "TARTARIC ACID is a white, crystalline organic acid that occurs naturally in many fruits, most notably in grapes, but also in bananas, tamarinds, and citrus"
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E133",
    "name": "Brilliant blue FCF",
    "alternateNames": [
      "Діамантовий синій",
      "Wikidata: Q420093"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Діамантовий синій.",
      "en": "Colors: Brilliant blue FCF."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E133 (Діамантовий синій).",
      "en": "BRILLIANT BLUE FCF -Blue 1- is an organic compound classified as a blue triarylmethane dye, reflecting its chemical structure. Known under various commercial names, it is a colorant for foods and other substances."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1210",
    "name": "Carbomer",
    "alternateNames": [
      "Карбомер"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Карбомер.",
      "en": "Other: Carbomer."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1210 (Карбомер).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1210 (Carbomer)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1504",
    "name": "Ethyl acetate",
    "alternateNames": [
      "Wikidata: Q407153"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ethyl acetate.",
      "en": "Other: Ethyl acetate."
    },
    "description": {
      "ua": "Харчова добавка E1504 (Ethyl acetate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1504 (Ethyl acetate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E331I",
    "name": "Monosodium citrate",
    "alternateNames": [
      "Wikidata: Q4179018"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Monosodium citrate.",
      "en": "Antioxidants: Monosodium citrate."
    },
    "description": {
      "ua": "Харчова добавка E331I (Monosodium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "MONOSODIUM CITRATE, more correctly, sodium dihydrogen citrate, is an acid salt of citric acid."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E538",
    "name": "Calcium ferrocyanide",
    "alternateNames": [
      "Wikidata: Q18967196"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Calcium ferrocyanide.",
      "en": "Other: Calcium ferrocyanide."
    },
    "description": {
      "ua": "Харчова добавка E538 (Calcium ferrocyanide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E538 (Calcium ferrocyanide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E956",
    "name": "Alitame",
    "alternateNames": [
      "Алітам",
      "Wikidata: Q424676"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Алітам.",
      "en": "Sweeteners: Alitame."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E956 (Алітам).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E956 (Alitame)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E283",
    "name": "Potassium propionate",
    "alternateNames": [
      "Wikidata: Q416435"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Potassium propionate.",
      "en": "Preservatives: Potassium propionate."
    },
    "description": {
      "ua": "Харчова добавка E283 (Potassium propionate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E283 (Potassium propionate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E514",
    "name": "Sodium sulphates",
    "alternateNames": [
      "Сульфати натрію",
      "Wikidata: Q211737"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Сульфати натрію.",
      "en": "Other: Sodium sulphates."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E514 (Сульфати натрію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E514 (Sodium sulphates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E241",
    "name": "Gum Guaicum",
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Gum Guaicum.",
      "en": "Preservatives: Gum Guaicum."
    },
    "description": {
      "ua": "Харчова добавка E241 (Gum Guaicum), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "GUM GUAICUM, or guaiac resin, is a substance produced from the tree species Guaiacum officinale."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E155",
    "name": "Brown ht",
    "alternateNames": [
      "Wikidata: Q424541"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Brown ht.",
      "en": "Colors: Brown ht."
    },
    "description": {
      "ua": "Харчова добавка E155 (Brown ht), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E155 (Brown ht)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E201",
    "name": "Sodium sorbate",
    "alternateNames": [
      "Wikidata: Q420152"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium sorbate.",
      "en": "Preservatives: Sodium sorbate."
    },
    "description": {
      "ua": "Харчова добавка E201 (Sodium sorbate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E201 (Sodium sorbate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1101",
    "name": "Protease",
    "alternateNames": [
      "Протеаза",
      "Wikidata: Q212410"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Протеаза.",
      "en": "Thickeners & Emulsifiers: Protease."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1101 (Протеаза).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1101 (Protease)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E333III",
    "name": "Tricalcium citrate",
    "alternateNames": [
      "Wikidata: Q420280"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Tricalcium citrate.",
      "en": "Antioxidants: Tricalcium citrate."
    },
    "description": {
      "ua": "Харчова добавка E333III (Tricalcium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E333III (Tricalcium citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E264",
    "name": "Ammonium acetate",
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Ammonium acetate.",
      "en": "Preservatives: Ammonium acetate."
    },
    "description": {
      "ua": "Харчова добавка E264 (Ammonium acetate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E264 (Ammonium acetate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E318",
    "name": "Calcium erythorbin",
    "alternateNames": [
      "Wikidata: Q13081784"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium erythorbin.",
      "en": "Antioxidants: Calcium erythorbin."
    },
    "description": {
      "ua": "Харчова добавка E318 (Calcium erythorbin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E318 (Calcium erythorbin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1404",
    "name": "Oxidised starch",
    "alternateNames": [
      "Wikidata: Q18967259"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Oxidised starch.",
      "en": "Thickeners & Emulsifiers: Oxidised starch."
    },
    "description": {
      "ua": "Харчова добавка E1404 (Oxidised starch), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1404 (Oxidised starch)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E906",
    "name": "Benzoin resin",
    "alternateNames": [
      "Бензойна смола",
      "Wikidata: Q793163"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Бензойна смола.",
      "en": "Other: Benzoin resin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E906 (Бензойна смола).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E906 (Benzoin resin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1440",
    "name": "Hydroxypropyl starch",
    "alternateNames": [
      "Wikidata: Q1640437"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Hydroxypropyl starch.",
      "en": "Thickeners & Emulsifiers: Hydroxypropyl starch."
    },
    "description": {
      "ua": "Харчова добавка E1440 (Hydroxypropyl starch), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1440 (Hydroxypropyl starch)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E946",
    "name": "Octafluorocyclobutane",
    "alternateNames": [
      "Октафлуороциклобутан",
      "Wikidata: Q413079"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Октафлуороциклобутан.",
      "en": "Other: Octafluorocyclobutane."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E946 (Октафлуороциклобутан).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E946 (Octafluorocyclobutane)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E411",
    "name": "E411 food additive",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: E411 food additive.",
      "en": "Thickeners & Emulsifiers: E411 food additive."
    },
    "description": {
      "ua": "Харчова добавка E411 (E411 food additive), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E411 (E411 food additive)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E367",
    "name": "Calcium fumarate",
    "alternateNames": [
      "Wikidata: Q836445"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium fumarate.",
      "en": "Antioxidants: Calcium fumarate."
    },
    "description": {
      "ua": "Харчова добавка E367 (Calcium fumarate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E367 (Calcium fumarate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E432",
    "name": "Polyoxyethylene sorbitan monolaurate",
    "alternateNames": [
      "Wikidata: Q413616"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyoxyethylene sorbitan monolaurate.",
      "en": "Thickeners & Emulsifiers: Polyoxyethylene sorbitan monolaurate."
    },
    "description": {
      "ua": "Харчова добавка E432 (Polyoxyethylene sorbitan monolaurate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E432 (Polyoxyethylene sorbitan monolaurate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E369",
    "name": "Alanin",
    "alternateNames": [
      "Аланін"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Аланін.",
      "en": "Antioxidants: Alanin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E369 (Аланін).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E369 (Alanin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E495",
    "name": "Sorbitan monopalmitate",
    "alternateNames": [
      "Wikidata: Q18601367"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sorbitan monopalmitate.",
      "en": "Thickeners & Emulsifiers: Sorbitan monopalmitate."
    },
    "description": {
      "ua": "Харчова добавка E495 (Sorbitan monopalmitate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E495 (Sorbitan monopalmitate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E960A",
    "name": "Steviol glycosides from Stevia",
    "alternateNames": [
      "Стевіол глікозиди зі Стевії"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Стевіол глікозиди зі Стевії.",
      "en": "Sweeteners: Steviol glycosides from Stevia."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E960A (Стевіол глікозиди зі Стевії).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E960A (Steviol glycosides from Stevia)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1452",
    "name": "Starch aluminium octenyl succinate",
    "alternateNames": [
      "Wikidata: Q18967287"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Starch aluminium octenyl succinate.",
      "en": "Thickeners & Emulsifiers: Starch aluminium octenyl succinate."
    },
    "description": {
      "ua": "Харчова добавка E1452 (Starch aluminium octenyl succinate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1452 (Starch aluminium octenyl succinate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E141II",
    "name": "Copper complexes of chlorophyllins",
    "alternateNames": [
      "Wikidata: Q18967203"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Copper complexes of chlorophyllins.",
      "en": "Colors: Copper complexes of chlorophyllins."
    },
    "description": {
      "ua": "Харчова добавка E141II (Copper complexes of chlorophyllins), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E141II (Copper complexes of chlorophyllins)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E923",
    "name": "Ammonium persulfate",
    "alternateNames": [
      "Персульфат амонію",
      "Wikidata: Q412085"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Персульфат амонію.",
      "en": "Other: Ammonium persulfate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E923 (Персульфат амонію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E923 (Ammonium persulfate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E101II",
    "name": "Riboflavin-5′-phosphate",
    "alternateNames": [
      "Wikidata: Q130365"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Riboflavin-5′-phosphate.",
      "en": "Colors: Riboflavin-5′-phosphate."
    },
    "description": {
      "ua": "Харчова добавка E101II (Riboflavin-5′-phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E101II (Riboflavin-5′-phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1103",
    "name": "Invertase",
    "alternateNames": [
      "Інвертаза",
      "Wikidata: Q419189"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Інвертаза.",
      "en": "Thickeners & Emulsifiers: Invertase."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1103 (Інвертаза).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1103 (Invertase)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E963",
    "name": "Tagatose",
    "alternateNames": [
      "Тагатоза",
      "Wikidata: Q414089"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Підсолоджувачі: Тагатоза.",
      "en": "Sweeteners: Tagatose."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E963 (Тагатоза).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E963 (Tagatose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E960D",
    "name": "Glucosylated steviol glycosides",
    "alternateNames": [
      "Глюкозильовані стевіол глікозиди"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Глюкозильовані стевіол глікозиди.",
      "en": "Sweeteners: Glucosylated steviol glycosides."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E960D (Глюкозильовані стевіол глікозиди).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E960D (Glucosylated steviol glycosides)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E327",
    "name": "Calcium lactate",
    "alternateNames": [
      "Wikidata: Q419693"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium lactate.",
      "en": "Antioxidants: Calcium lactate."
    },
    "description": {
      "ua": "Харчова добавка E327 (Calcium lactate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E327 (Calcium lactate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E329",
    "name": "Magnesium lactate",
    "alternateNames": [
      "Wikidata: Q594836"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Magnesium lactate.",
      "en": "Antioxidants: Magnesium lactate."
    },
    "description": {
      "ua": "Харчова добавка E329 (Magnesium lactate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E329 (Magnesium lactate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E640II",
    "name": "Sodiumglycinate",
    "alternateNames": [
      "Гліцинат натрію",
      "Wikidata: Q18967228"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Гліцинат натрію.",
      "en": "Other: Sodiumglycinate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E640II (Гліцинат натрію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E640II (Sodiumglycinate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E237",
    "name": "Sodium formiate",
    "alternateNames": [
      "Форміат натрію",
      "Wikidata: Q409209"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Форміат натрію.",
      "en": "Preservatives: Sodium formiate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E237 (Форміат натрію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E237 (Sodium formiate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E239",
    "name": "Hexamethylene tetramine",
    "alternateNames": [
      "Wikidata: Q71969"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Hexamethylene tetramine.",
      "en": "Preservatives: Hexamethylene tetramine."
    },
    "description": {
      "ua": "Харчова добавка E239 (Hexamethylene tetramine), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E239 (Hexamethylene tetramine)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E430",
    "name": "Polyoxyethylene (8) stearate",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyoxyethylene (8) stearate.",
      "en": "Thickeners & Emulsifiers: Polyoxyethylene (8) stearate."
    },
    "description": {
      "ua": "Харчова добавка E430 (Polyoxyethylene (8) stearate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E430 (Polyoxyethylene (8) stearate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E101",
    "name": "Riboflavin",
    "alternateNames": [
      "Wikidata: Q130365"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Riboflavin.",
      "en": "Colors: Riboflavin."
    },
    "description": {
      "ua": "Харчова добавка E101 (Riboflavin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E101 (Riboflavin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161I",
    "name": "Citranaxanthin",
    "alternateNames": [
      "Цитранксантин"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Цитранксантин.",
      "en": "Colors: Citranaxanthin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E161I (Цитранксантин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E161I (Citranaxanthin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E440B",
    "name": "Pectin amide",
    "alternateNames": [
      "Амідірованний пектин",
      "Wikidata: Q188154"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Амідірованний пектин.",
      "en": "Thickeners & Emulsifiers: Pectin amide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E440B (Амідірованний пектин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E440B (Pectin amide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E343I",
    "name": "Monomagnesium phosphate",
    "alternateNames": [
      "Wikidata: Q4161317"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Monomagnesium phosphate.",
      "en": "Antioxidants: Monomagnesium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E343I (Monomagnesium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E343I (Monomagnesium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E141",
    "name": "Copper complexes of chlorophylls and chlorophyllins",
    "alternateNames": [
      "Wikidata: Q18967203"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Copper complexes of chlorophylls and chlorophyllins.",
      "en": "Colors: Copper complexes of chlorophylls and chlorophyllins."
    },
    "description": {
      "ua": "Харчова добавка E141 (Copper complexes of chlorophylls and chlorophyllins), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E141 (Copper complexes of chlorophylls and chlorophyllins)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E363",
    "name": "Succinic acid",
    "alternateNames": [
      "Wikidata: Q213050"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Succinic acid.",
      "en": "Antioxidants: Succinic acid."
    },
    "description": {
      "ua": "Харчова добавка E363 (Succinic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E363 (Succinic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E650",
    "name": "Zinc acetate",
    "alternateNames": [
      "Цинк ацетат",
      "Wikidata: Q204639"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Цинк ацетат.",
      "en": "Other: Zinc acetate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E650 (Цинк ацетат).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E650 (Zinc acetate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E642",
    "name": "E642",
    "alternateNames": [
      "Лізину гідрохлорид"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Лізину гідрохлорид.",
      "en": "Other: E642."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E642 (Лізину гідрохлорид).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E642 (E642)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E384",
    "name": "Isopropyl citrates",
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Isopropyl citrates.",
      "en": "Preservatives: Isopropyl citrates."
    },
    "description": {
      "ua": "Харчова добавка E384 (Isopropyl citrates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E384 (Isopropyl citrates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E164",
    "name": "Saffron",
    "alternateNames": [
      "Шафран",
      "Wikidata: Q25434"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Шафран.",
      "en": "Colors: Saffron."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E164 (Шафран).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E164 (Saffron)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E929",
    "name": "Acetone peroxide",
    "alternateNames": [
      "Wikidata: Q329022"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Acetone peroxide.",
      "en": "Other: Acetone peroxide."
    },
    "description": {
      "ua": "Харчова добавка E929 (Acetone peroxide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E929 (Acetone peroxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E927",
    "name": "Azodicarbonamide and Carbamide",
    "alternateNames": [
      "Азодикарбонамід і Карбамід"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Азодикарбонамід і Карбамід.",
      "en": "Other: Azodicarbonamide and Carbamide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E927 (Азодикарбонамід і Карбамід).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E927 (Azodicarbonamide and Carbamide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E335I",
    "name": "Monosodium tartrate",
    "alternateNames": [
      "Wikidata: Q836451"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Monosodium tartrate.",
      "en": "Antioxidants: Monosodium tartrate."
    },
    "description": {
      "ua": "Харчова добавка E335I (Monosodium tartrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E335I (Monosodium tartrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E341III",
    "name": "Tricalcium phosphate",
    "alternateNames": [
      "Ортофосфат кальцію",
      "Wikidata: Q278387"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ортофосфат кальцію.",
      "en": "Antioxidants: Tricalcium phosphate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E341III (Ортофосфат кальцію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E341III (Tricalcium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E151",
    "name": "Brilliant black bn",
    "alternateNames": [
      "Wikidata: Q420079"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Brilliant black bn.",
      "en": "Colors: Brilliant black bn."
    },
    "description": {
      "ua": "Харчова добавка E151 (Brilliant black bn), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E151 (Brilliant black bn)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E969",
    "name": "Advantame",
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Advantame.",
      "en": "Sweeteners: Advantame."
    },
    "description": {
      "ua": "Харчова добавка E969 (Advantame), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E969 (Advantame)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E124",
    "name": "Ponceau 4r",
    "alternateNames": [
      "Wikidata: Q384709"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Ponceau 4r.",
      "en": "Colors: Ponceau 4r."
    },
    "description": {
      "ua": "Харчова добавка E124 (Ponceau 4r), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E124 (Ponceau 4r)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E640",
    "name": "Glycine and its sodium salt",
    "alternateNames": [
      "Wikidata: Q18967228"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Glycine and its sodium salt.",
      "en": "Other: Glycine and its sodium salt."
    },
    "description": {
      "ua": "Харчова добавка E640 (Glycine and its sodium salt), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E640 (Glycine and its sodium salt)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E322A",
    "name": "Oat lecithin",
    "alternateNames": [
      "Вівсяний лецитин"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Вівсяний лецитин.",
      "en": "Antioxidants: Oat lecithin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E322A (Вівсяний лецитин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E322A (Oat lecithin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E515I",
    "name": "Potassium sulphate",
    "alternateNames": [
      "Wikidata: Q193054"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Potassium sulphate.",
      "en": "Other: Potassium sulphate."
    },
    "description": {
      "ua": "Харчова добавка E515I (Potassium sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E515I (Potassium sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E323",
    "name": "Anoxomer",
    "alternateNames": [
      "Аноксомер"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Аноксомер.",
      "en": "Antioxidants: Anoxomer."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E323 (Аноксомер).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E323 (Anoxomer)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E415",
    "name": "Xanthan gum",
    "alternateNames": [
      "Wikidata: Q410768"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Xanthan gum.",
      "en": "Thickeners & Emulsifiers: Xanthan gum."
    },
    "description": {
      "ua": "Харчова добавка E415 (Xanthan gum), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "XANTHAN GUM is a polysaccharide with many industrial uses, including as a common food additive."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E233",
    "name": "Thiabendazole",
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Thiabendazole.",
      "en": "Preservatives: Thiabendazole."
    },
    "description": {
      "ua": "Харчова добавка E233 (Thiabendazole), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E233 (Thiabendazole)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E174",
    "name": "Silver",
    "alternateNames": [
      "Wikidata: Q1090"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Silver.",
      "en": "Colors: Silver."
    },
    "description": {
      "ua": "Харчова добавка E174 (Silver), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E174 (Silver)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E575",
    "name": "Glucono-delta-lactone",
    "alternateNames": [
      "Wikidata: Q114174"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Glucono-delta-lactone.",
      "en": "Other: Glucono-delta-lactone."
    },
    "description": {
      "ua": "Харчова добавка E575 (Glucono-delta-lactone), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E575 (Glucono-delta-lactone)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1209",
    "name": "Polyvinyl alcohol-polyethylene glycol-graft-co-polymer",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Polyvinyl alcohol-polyethylene glycol-graft-co-polymer.",
      "en": "Other: Polyvinyl alcohol-polyethylene glycol-graft-co-polymer."
    },
    "description": {
      "ua": "Харчова добавка E1209 (Polyvinyl alcohol-polyethylene glycol-graft-co-polymer), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1209 (Polyvinyl alcohol-polyethylene glycol-graft-co-polymer)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E322II",
    "name": "Partially hydrolyzed lecithin",
    "alternateNames": [
      "Wikidata: Q241595"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Partially hydrolyzed lecithin.",
      "en": "Antioxidants: Partially hydrolyzed lecithin."
    },
    "description": {
      "ua": "Харчова добавка E322II (Partially hydrolyzed lecithin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E322II (Partially hydrolyzed lecithin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E962",
    "name": "Salt of aspartame-acesulfame",
    "alternateNames": [
      "Wikidata: Q409847"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Salt of aspartame-acesulfame.",
      "en": "Sweeteners: Salt of aspartame-acesulfame."
    },
    "description": {
      "ua": "Харчова добавка E962 (Salt of aspartame-acesulfame), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E962 (Salt of aspartame-acesulfame)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E111",
    "name": "Orange GGN",
    "alternateNames": [
      "Wikidata: Q929052"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Orange GGN.",
      "en": "Colors: Orange GGN."
    },
    "description": {
      "ua": "Харчова добавка E111 (Orange GGN), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E111 (Orange GGN)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E525",
    "name": "Potassium hydroxide",
    "alternateNames": [
      "Гідроксид калію",
      "Wikidata: Q132298"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Гідроксид калію.",
      "en": "Other: Potassium hydroxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E525 (Гідроксид калію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E525 (Potassium hydroxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E425I",
    "name": "Konjac gum",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Konjac gum.",
      "en": "Thickeners & Emulsifiers: Konjac gum."
    },
    "description": {
      "ua": "Харчова добавка E425I (Konjac gum), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E425I (Konjac gum)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E920",
    "name": "L-cysteine",
    "alternateNames": [
      "L-Цистеїн",
      "Wikidata: Q186474"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: L-Цистеїн.",
      "en": "Other: L-cysteine."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E920 (L-Цистеїн).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E920 (L-cysteine)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E445",
    "name": "Glycerol esters of wood rosin",
    "alternateNames": [
      "Wikidata: Q836765"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Glycerol esters of wood rosin.",
      "en": "Thickeners & Emulsifiers: Glycerol esters of wood rosin."
    },
    "description": {
      "ua": "Харчова добавка E445 (Glycerol esters of wood rosin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E445 (Glycerol esters of wood rosin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E922",
    "name": "Potassium persulfate",
    "alternateNames": [
      "Персульфат калію",
      "Wikidata: Q415226"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Персульфат калію.",
      "en": "Other: Potassium persulfate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E922 (Персульфат калію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E922 (Potassium persulfate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E405",
    "name": "Propane-1‚2-diol alginate",
    "alternateNames": [
      "Wikidata: Q2474785"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Propane-1‚2-diol alginate.",
      "en": "Thickeners & Emulsifiers: Propane-1‚2-diol alginate."
    },
    "description": {
      "ua": "Харчова добавка E405 (Propane-1‚2-diol alginate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E405 (Propane-1‚2-diol alginate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E215",
    "name": "Sodium ethyl p-hydroxybenzoate",
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium ethyl p-hydroxybenzoate.",
      "en": "Preservatives: Sodium ethyl p-hydroxybenzoate."
    },
    "description": {
      "ua": "Харчова добавка E215 (Sodium ethyl p-hydroxybenzoate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E215 (Sodium ethyl p-hydroxybenzoate)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E450VII",
    "name": "Calcium dihydrogen diphosphate",
    "alternateNames": [
      "Wikidata: Q290828"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Calcium dihydrogen diphosphate.",
      "en": "Thickeners & Emulsifiers: Calcium dihydrogen diphosphate."
    },
    "description": {
      "ua": "Харчова добавка E450VII (Calcium dihydrogen diphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E450VII (Calcium dihydrogen diphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E433",
    "name": "Polyoxyethylene sorbitan monooleate",
    "alternateNames": [
      "Wikidata: Q420358"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyoxyethylene sorbitan monooleate.",
      "en": "Thickeners & Emulsifiers: Polyoxyethylene sorbitan monooleate."
    },
    "description": {
      "ua": "Харчова добавка E433 (Polyoxyethylene sorbitan monooleate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E433 (Polyoxyethylene sorbitan monooleate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E340I",
    "name": "Monopotassium phosphate",
    "alternateNames": [
      "Wikidata: Q415049"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Monopotassium phosphate.",
      "en": "Antioxidants: Monopotassium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E340I (Monopotassium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E340I (Monopotassium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E232",
    "name": "Sodium orthophenyl phenol",
    "alternateNames": [
      "Wikidata: Q6581446"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium orthophenyl phenol.",
      "en": "Preservatives: Sodium orthophenyl phenol."
    },
    "description": {
      "ua": "Харчова добавка E232 (Sodium orthophenyl phenol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E232 (Sodium orthophenyl phenol)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1403",
    "name": "Bleached starch",
    "alternateNames": [
      "Вибілений крохмаль"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Вибілений крохмаль.",
      "en": "Thickeners & Emulsifiers: Bleached starch."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1403 (Вибілений крохмаль).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1403 (Bleached starch)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E180",
    "name": "Litholrubine bk",
    "alternateNames": [
      "Wikidata: Q427230"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Litholrubine bk.",
      "en": "Colors: Litholrubine bk."
    },
    "description": {
      "ua": "Харчова добавка E180 (Litholrubine bk), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E180 (Litholrubine bk)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E322",
    "name": "Lecithins",
    "alternateNames": [
      "Wikidata: Q241595"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Lecithins.",
      "en": "Antioxidants: Lecithins."
    },
    "description": {
      "ua": "Харчова добавка E322 (Lecithins), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "is a generic term to designate any group of yellow-brownish fatty substances occurring in animal and plant tissues, and are used for smoothing food textures, dissolving powders -emulsifying-, homogeLECITHINnizing liquid mixtures, and repelling sticking materials. The term lecithin refers to a group of compounds found in every living organism, as they are part of the cell wall of all cells. Lecithin is commercially isolated -mainly- from soybeans or egg yolk."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E352I",
    "name": "Calcium malate",
    "alternateNames": [
      "Wikidata: Q1356722"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium malate.",
      "en": "Antioxidants: Calcium malate."
    },
    "description": {
      "ua": "Харчова добавка E352I (Calcium malate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E352I (Calcium malate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E350I",
    "name": "Sodium malate",
    "alternateNames": [
      "Wikidata: Q836788"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Sodium malate.",
      "en": "Antioxidants: Sodium malate."
    },
    "description": {
      "ua": "Харчова добавка E350I (Sodium malate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E350I (Sodium malate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E905C",
    "name": "Petroleum wax",
    "alternateNames": [
      "Нафтовий віск",
      "Wikidata: Q579764"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Нафтовий віск.",
      "en": "Other: Petroleum wax."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E905C (Нафтовий віск).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E905C (Petroleum wax)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E370",
    "name": "1‚4-Heptonolactone",
    "alternateNames": [
      "1‚4-Гептонолактон"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: 1‚4-Гептонолактон.",
      "en": "Antioxidants: 1‚4-Heptonolactone."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E370 (1‚4-Гептонолактон).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E370 (1‚4-Heptonolactone)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E101A",
    "name": "E101a food additive",
    "alternateNames": [
      "Wikidata: Q376061"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: E101a food additive.",
      "en": "Colors: E101a food additive."
    },
    "description": {
      "ua": "Харчова добавка E101A (E101a food additive), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E101A (E101a food additive)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1503",
    "name": "Castor oil",
    "alternateNames": [
      "Рицинова олія",
      "Wikidata: Q337492"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Рицинова олія.",
      "en": "Thickeners & Emulsifiers: Castor oil."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1503 (Рицинова олія).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1503 (Castor oil)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161J",
    "name": "Astaxanthin",
    "alternateNames": [
      "Астаксантин"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Астаксантин.",
      "en": "Colors: Astaxanthin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E161J (Астаксантин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E161J (Astaxanthin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E472C",
    "name": "Citric acid esters of mono- and diglycerides of fatty acids",
    "alternateNames": [
      "Естери гліцерину та лимонної і жирних кислот",
      "Wikidata: Q18967202"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Естери гліцерину та лимонної і жирних кислот.",
      "en": "Antioxidants: Citric acid esters of mono- and diglycerides of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E472C (Естери гліцерину та лимонної і жирних кислот).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E472C (Citric acid esters of mono- and diglycerides of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1423",
    "name": "Acetylated distarch glycerol",
    "alternateNames": [
      "Wikidata: Q18608829"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Acetylated distarch glycerol.",
      "en": "Thickeners & Emulsifiers: Acetylated distarch glycerol."
    },
    "description": {
      "ua": "Харчова добавка E1423 (Acetylated distarch glycerol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1423 (Acetylated distarch glycerol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E182",
    "name": "Orcein",
    "alternateNames": [
      "Орцеїн",
      "Wikidata: Q425645"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Орцеїн.",
      "en": "Colors: Orcein."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E182 (Орцеїн).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E182 (Orcein)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E586",
    "name": "4-hexylresorcinol",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: 4-hexylresorcinol.",
      "en": "Other: 4-hexylresorcinol."
    },
    "description": {
      "ua": "Харчова добавка E586 (4-hexylresorcinol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E586 (4-hexylresorcinol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E320",
    "name": "Butylated hydroxyanisole (bha)",
    "alternateNames": [
      "Wikidata: Q409401"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Butylated hydroxyanisole (bha).",
      "en": "Antioxidants: Butylated hydroxyanisole (bha)."
    },
    "description": {
      "ua": "Харчова добавка E320 (Butylated hydroxyanisole (bha)), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E320 (Butylated hydroxyanisole (bha))."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E635",
    "name": "Disodium 5'-ribonucleotide",
    "alternateNames": [
      "Wikidata: Q18967210"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Disodium 5'-ribonucleotide.",
      "en": "Flavor Enhancers: Disodium 5'-ribonucleotide."
    },
    "description": {
      "ua": "Харчова добавка E635 (Disodium 5'-ribonucleotide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E635 (Disodium 5'-ribonucleotide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E172III",
    "name": "Yellow iron oxide",
    "alternateNames": [
      "Wikidata: Q3045754"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Yellow iron oxide.",
      "en": "Colors: Yellow iron oxide."
    },
    "description": {
      "ua": "Харчова добавка E172III (Yellow iron oxide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E172III (Yellow iron oxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E927A",
    "name": "Azodicarbonamide",
    "alternateNames": [
      "Азодикарбонамід",
      "Wikidata: Q415646"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Азодикарбонамід.",
      "en": "Other: Azodicarbonamide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E927A (Азодикарбонамід).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E927A (Azodicarbonamide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160AIV",
    "name": "Algal carotenes",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Algal carotenes.",
      "en": "Colors: Algal carotenes."
    },
    "description": {
      "ua": "Харчова добавка E160AIV (Algal carotenes), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160AIV (Algal carotenes)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E230",
    "name": "Biphenyl",
    "alternateNames": [
      "Біфеніл",
      "Wikidata: Q410915"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Біфеніл.",
      "en": "Preservatives: Biphenyl."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E230 (Біфеніл).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E230 (Biphenyl)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E931",
    "name": "Nitrogen",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Nitrogen.",
      "en": "Other: Nitrogen."
    },
    "description": {
      "ua": "Харчова добавка E931 (Nitrogen), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E931 (Nitrogen)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E580",
    "name": "Magnesium Gluconate",
    "alternateNames": [
      "Глюконат магнію",
      "Wikidata: Q6731392"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Глюконат магнію.",
      "en": "Other: Magnesium Gluconate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E580 (Глюконат магнію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E580 (Magnesium Gluconate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1205",
    "name": "Basic methacrylate copolymer",
    "alternateNames": [
      "Wikidata: Q18967186"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Basic methacrylate copolymer.",
      "en": "Other: Basic methacrylate copolymer."
    },
    "description": {
      "ua": "Харчова добавка E1205 (Basic methacrylate copolymer), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1205 (Basic methacrylate copolymer)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E326",
    "name": "Potassium lactate",
    "alternateNames": [
      "Wikidata: Q411342"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Potassium lactate.",
      "en": "Antioxidants: Potassium lactate."
    },
    "description": {
      "ua": "Харчова добавка E326 (Potassium lactate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "POTASSIUM LACTATE is a compound with formula KC3H5O3. It is produced by neutralizing lactic acid which is fermented from a sugar source. Potassium lactate is commonly used in meat and poultry products to extend shelf life and increase food safety as it has a broad antimicrobial action. lactic acid and lactates can be consumed by all religious groups, vegans and vegetarians. Although the name refers to milk, it is not made from milk and thus suitable for people with milk allergy or lactose intolerance."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E150C",
    "name": "Ammonia caramel",
    "alternateNames": [
      "Wikidata: Q11735907"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Ammonia caramel.",
      "en": "Colors: Ammonia caramel."
    },
    "description": {
      "ua": "Харчова добавка E150C (Ammonia caramel), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E150C (Ammonia caramel)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E236",
    "name": "Formic acid",
    "alternateNames": [
      "Wikidata: Q161233"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Formic acid.",
      "en": "Preservatives: Formic acid."
    },
    "description": {
      "ua": "Харчова добавка E236 (Formic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E236 (Formic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E450III",
    "name": "Tetrasodium diphosphate",
    "alternateNames": [
      "Wikidata: Q418504"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Tetrasodium diphosphate.",
      "en": "Thickeners & Emulsifiers: Tetrasodium diphosphate."
    },
    "description": {
      "ua": "Харчова добавка E450III (Tetrasodium diphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "TETRASODIUM PYROPHOSPHATE, also called sodium pyrophosphate, tetrasodium phosphate or TSPP, is a colorless transparent crystalline chemical compound with the formula Na4P2O7."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E163B",
    "name": "Delphinidin",
    "alternateNames": [
      "Wikidata: Q367258"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Delphinidin.",
      "en": "Colors: Delphinidin."
    },
    "description": {
      "ua": "Харчова добавка E163B (Delphinidin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E163B (Delphinidin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E440AIV",
    "name": "Ammonium Pectate",
    "alternateNames": [
      "Wikidata: Q188154"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Ammonium Pectate.",
      "en": "Thickeners & Emulsifiers: Ammonium Pectate."
    },
    "description": {
      "ua": "Харчова добавка E440AIV (Ammonium Pectate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E440AIV (Ammonium Pectate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E366",
    "name": "Potassium fumarate",
    "alternateNames": [
      "Wikidata: Q723321"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Potassium fumarate.",
      "en": "Antioxidants: Potassium fumarate."
    },
    "description": {
      "ua": "Харчова добавка E366 (Potassium fumarate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E366 (Potassium fumarate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E452III",
    "name": "Sodium calcium polyphosphate",
    "alternateNames": [
      "Wikidata: Q18967198"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium calcium polyphosphate.",
      "en": "Thickeners & Emulsifiers: Sodium calcium polyphosphate."
    },
    "description": {
      "ua": "Харчова добавка E452III (Sodium calcium polyphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E452III (Sodium calcium polyphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E713",
    "name": "Tylosin",
    "alternateNames": [
      "Тилозин",
      "Wikidata: Q411462"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Тилозин.",
      "en": "Other: Tylosin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E713 (Тилозин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E713 (Tylosin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E494",
    "name": "Sorbitan monooleate",
    "alternateNames": [
      "Wikidata: Q18944664"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sorbitan monooleate.",
      "en": "Thickeners & Emulsifiers: Sorbitan monooleate."
    },
    "description": {
      "ua": "Харчова добавка E494 (Sorbitan monooleate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E494 (Sorbitan monooleate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1442",
    "name": "Hydroxypropyl distarch phosphate",
    "alternateNames": [
      "Wikidata: Q10528966"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Hydroxypropyl distarch phosphate.",
      "en": "Thickeners & Emulsifiers: Hydroxypropyl distarch phosphate."
    },
    "description": {
      "ua": "Харчова добавка E1442 (Hydroxypropyl distarch phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1442 (Hydroxypropyl distarch phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E553AII",
    "name": "Magnesium trisilicate",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Magnesium trisilicate.",
      "en": "Thickeners & Emulsifiers: Magnesium trisilicate."
    },
    "description": {
      "ua": "Харчова добавка E553AII (Magnesium trisilicate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E553AII (Magnesium trisilicate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E331II",
    "name": "Disodium citrate",
    "alternateNames": [
      "Wikidata: Q4177124"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Disodium citrate.",
      "en": "Antioxidants: Disodium citrate."
    },
    "description": {
      "ua": "Харчова добавка E331II (Disodium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "DISODIUM CITRATE, more properly, disodium hydrogen citrate, is an acid salt of citric acid with the chemical formula Na2C6H6O7. It is used as an antioxidant in food and to improve the effects of other antioxidants. It is also used as an acidity regulator and sequestrant. Typical products include gelatin, jam, sweets, ice cream, carbonated beverages, milk powder, wine, and processed cheeses."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1517",
    "name": "Glyceryl diacetate",
    "alternateNames": [
      "Wikidata: Q904648"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Glyceryl diacetate.",
      "en": "Other: Glyceryl diacetate."
    },
    "description": {
      "ua": "Харчова добавка E1517 (Glyceryl diacetate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1517 (Glyceryl diacetate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E511",
    "name": "Magnesium chloride",
    "alternateNames": [
      "Wikidata: Q265414"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Magnesium chloride.",
      "en": "Thickeners & Emulsifiers: Magnesium chloride."
    },
    "description": {
      "ua": "Харчова добавка E511 (Magnesium chloride), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E511 (Magnesium chloride)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E966",
    "name": "Lactitol",
    "alternateNames": [
      "Wikidata: Q415020"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Lactitol.",
      "en": "Sweeteners: Lactitol."
    },
    "description": {
      "ua": "Харчова добавка E966 (Lactitol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E966 (Lactitol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E125",
    "name": "Scarlet GN",
    "alternateNames": [
      "Wikidata: Q934310"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Scarlet GN.",
      "en": "Colors: Scarlet GN."
    },
    "description": {
      "ua": "Харчова добавка E125 (Scarlet GN), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E125 (Scarlet GN)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E414",
    "name": "Acacia gum",
    "alternateNames": [
      "Гуміарабік",
      "Wikidata: Q535814"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Гуміарабік.",
      "en": "Thickeners & Emulsifiers: Acacia gum."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E414 (Гуміарабік).",
      "en": "GUM ARABIC, also known as acacia gum, arabic gum, gum acacia, acacia, Senegal gum and Indian gum"
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E514I",
    "name": "Sodium sulphate",
    "alternateNames": [
      "Wikidata: Q211737"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Sodium sulphate.",
      "en": "Other: Sodium sulphate."
    },
    "description": {
      "ua": "Харчова добавка E514I (Sodium sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E514I (Sodium sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1410",
    "name": "Monostarch phosphate",
    "alternateNames": [
      "Wikidata: Q18631008"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Monostarch phosphate.",
      "en": "Thickeners & Emulsifiers: Monostarch phosphate."
    },
    "description": {
      "ua": "Харчова добавка E1410 (Monostarch phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1410 (Monostarch phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E261",
    "name": "Potassium acetate",
    "alternateNames": [
      "Ацетат калію",
      "Wikidata: Q409199"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Ацетат калію.",
      "en": "Preservatives: Potassium acetate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E261 (Ацетат калію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E261 (Potassium acetate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E175",
    "name": "Gold",
    "alternateNames": [
      "Wikidata: Q897"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Gold.",
      "en": "Colors: Gold."
    },
    "description": {
      "ua": "Харчова добавка E175 (Gold), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E175 (Gold)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E924B",
    "name": "Calcium bromate",
    "alternateNames": [
      "Бромат кальцію",
      "Wikidata: Q409241"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Бромат кальцію.",
      "en": "Other: Calcium bromate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E924B (Бромат кальцію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E924B (Calcium bromate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E331",
    "name": "Sodium citrates",
    "alternateNames": [
      "Wikidata: Q6460572"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Sodium citrates.",
      "en": "Antioxidants: Sodium citrates."
    },
    "description": {
      "ua": "Харчова добавка E331 (Sodium citrates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E331 (Sodium citrates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E221",
    "name": "Sodium sulphite",
    "alternateNames": [
      "Сульфіт натрію",
      "Wikidata: Q407806"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Сульфіт натрію.",
      "en": "Preservatives: Sodium sulphite."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E221 (Сульфіт натрію).",
      "en": "SODIUM SULFITE is a soluble sodium salt of sulfurous acid -sulfite- with the chemical formula Na2SO3."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E385",
    "name": "Calcium disodium ethylenediaminetetraacetate",
    "alternateNames": [
      "Wikidata: Q9189390"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Calcium disodium ethylenediaminetetraacetate.",
      "en": "Preservatives: Calcium disodium ethylenediaminetetraacetate."
    },
    "description": {
      "ua": "Харчова добавка E385 (Calcium disodium ethylenediaminetetraacetate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E385 (Calcium disodium ethylenediaminetetraacetate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1510",
    "name": "Ethanol",
    "alternateNames": [
      "Wikidata: Q153"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ethanol.",
      "en": "Other: Ethanol."
    },
    "description": {
      "ua": "Харчова добавка E1510 (Ethanol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1510 (Ethanol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1204",
    "name": "Pullulan",
    "alternateNames": [
      "Wikidata: Q419779"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Pullulan.",
      "en": "Thickeners & Emulsifiers: Pullulan."
    },
    "description": {
      "ua": "Харчова добавка E1204 (Pullulan), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1204 (Pullulan)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E165",
    "name": "Gardenia Blue",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Gardenia Blue.",
      "en": "Colors: Gardenia Blue."
    },
    "description": {
      "ua": "Харчова добавка E165 (Gardenia Blue), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E165 (Gardenia Blue)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E926",
    "name": "Chlorine dioxide",
    "alternateNames": [
      "Діоксид хлору",
      "Wikidata: Q422080"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Діоксид хлору.",
      "en": "Other: Chlorine dioxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E926 (Діоксид хлору).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E926 (Chlorine dioxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E905E",
    "name": "Mineral oil medium viscosity",
    "alternateNames": [
      "Wikidata: Q579764"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Mineral oil medium viscosity.",
      "en": "Other: Mineral oil medium viscosity."
    },
    "description": {
      "ua": "Харчова добавка E905E (Mineral oil medium viscosity), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E905E (Mineral oil medium viscosity)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E504I",
    "name": "Magnesium carbonate",
    "alternateNames": [
      "Карбонат магнію",
      "Wikidata: Q407931"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Карбонат магнію.",
      "en": "Other: Magnesium carbonate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E504I (Карбонат магнію).",
      "en": "MAGNESIUM CARBONATE, MgCO3 -archaic name magnesia alba-, is an inorganic salt that is a white solid."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E404",
    "name": "Calcium alginate",
    "alternateNames": [
      "Wikidata: Q2034064"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Calcium alginate.",
      "en": "Thickeners & Emulsifiers: Calcium alginate."
    },
    "description": {
      "ua": "Харчова добавка E404 (Calcium alginate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E404 (Calcium alginate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E444",
    "name": "Sucrose acetate isobutyrate",
    "alternateNames": [
      "Wikidata: Q410537"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sucrose acetate isobutyrate.",
      "en": "Thickeners & Emulsifiers: Sucrose acetate isobutyrate."
    },
    "description": {
      "ua": "Харчова добавка E444 (Sucrose acetate isobutyrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E444 (Sucrose acetate isobutyrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E472E",
    "name": "Mono- and diacetyltartaric acid esters of mono- and diglycerides of fatty acids",
    "alternateNames": [
      "Естери гліцерину та діацетилвинної і жирних кислот",
      "Wikidata: Q409406"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Естери гліцерину та діацетилвинної і жирних кислот.",
      "en": "Thickeners & Emulsifiers: Mono- and diacetyltartaric acid esters of mono- and diglycerides of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E472E (Естери гліцерину та діацетилвинної і жирних кислот).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E472E (Mono- and diacetyltartaric acid esters of mono- and diglycerides of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E214",
    "name": "Ethyl p-hydroxybenzoate",
    "alternateNames": [
      "Wikidata: Q229976"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Ethyl p-hydroxybenzoate.",
      "en": "Preservatives: Ethyl p-hydroxybenzoate."
    },
    "description": {
      "ua": "Харчова добавка E214 (Ethyl p-hydroxybenzoate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E214 (Ethyl p-hydroxybenzoate)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E368",
    "name": "Ammonium fumarate",
    "alternateNames": [
      "Wikidata: Q2882202"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ammonium fumarate.",
      "en": "Antioxidants: Ammonium fumarate."
    },
    "description": {
      "ua": "Харчова добавка E368 (Ammonium fumarate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E368 (Ammonium fumarate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E501",
    "name": "Potassium carbonates",
    "alternateNames": [
      "Wikidata: Q379885"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Potassium carbonates.",
      "en": "Thickeners & Emulsifiers: Potassium carbonates."
    },
    "description": {
      "ua": "Харчова добавка E501 (Potassium carbonates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E501 (Potassium carbonates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E461",
    "name": "Methyl cellulose",
    "alternateNames": [
      "Метилцелюлоза",
      "Wikidata: Q416312"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Метилцелюлоза.",
      "en": "Thickeners & Emulsifiers: Methyl cellulose."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E461 (Метилцелюлоза).",
      "en": "METHYL CELLULOSE is a chemical compound derived from cellulose. Methyl cellulose is added to food products, to generate their characteristic thick consistency, for example ice cream or croquette."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160B",
    "name": "Annatto",
    "alternateNames": [
      "Wikidata: Q425902"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Annatto.",
      "en": "Colors: Annatto."
    },
    "description": {
      "ua": "Харчова добавка E160B (Annatto), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160B (Annatto)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E541",
    "name": "Sodium aluminium phosphate",
    "alternateNames": [
      "Wikidata: Q1220360"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium aluminium phosphate.",
      "en": "Thickeners & Emulsifiers: Sodium aluminium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E541 (Sodium aluminium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E541 (Sodium aluminium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E328",
    "name": "Ammonium lactate",
    "alternateNames": [
      "Wikidata: Q663358"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ammonium lactate.",
      "en": "Antioxidants: Ammonium lactate."
    },
    "description": {
      "ua": "Харчова добавка E328 (Ammonium lactate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E328 (Ammonium lactate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E710",
    "name": "Spiramycin",
    "alternateNames": [
      "Спіраміцин",
      "Wikidata: Q422265"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Спіраміцин.",
      "en": "Other: Spiramycin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E710 (Спіраміцин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E710 (Spiramycin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E574",
    "name": "Gluconic acid",
    "alternateNames": [
      "Wikidata: Q407569"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Gluconic acid.",
      "en": "Other: Gluconic acid."
    },
    "description": {
      "ua": "Харчова добавка E574 (Gluconic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E574 (Gluconic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1201",
    "name": "Polyvinylpyrrolidone",
    "alternateNames": [
      "Wikidata: Q413433"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyvinylpyrrolidone.",
      "en": "Thickeners & Emulsifiers: Polyvinylpyrrolidone."
    },
    "description": {
      "ua": "Харчова добавка E1201 (Polyvinylpyrrolidone), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1201 (Polyvinylpyrrolidone)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E238",
    "name": "Calcium formiate",
    "alternateNames": [
      "Форміат кальцію",
      "Wikidata: Q221123"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Форміат кальцію.",
      "en": "Preservatives: Calcium formiate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E238 (Форміат кальцію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E238 (Calcium formiate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161F",
    "name": "Rhodoxanthin",
    "alternateNames": [
      "Родоксантин"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Родоксантин.",
      "en": "Colors: Rhodoxanthin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E161F (Родоксантин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E161F (Rhodoxanthin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E524",
    "name": "Sodium hydroxide",
    "alternateNames": [
      "Гідроксид натрію",
      "Wikidata: Q102769"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Гідроксид натрію.",
      "en": "Other: Sodium hydroxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E524 (Гідроксид натрію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E524 (Sodium hydroxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E551",
    "name": "Silicon dioxide",
    "alternateNames": [
      "Діоксид кремнію",
      "Wikidata: Q116269"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Діоксид кремнію.",
      "en": "Other: Silicon dioxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E551 (Діоксид кремнію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E551 (Silicon dioxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E471",
    "name": "Mono- and diglycerides of fatty acids",
    "alternateNames": [
      "Wikidata: Q416711"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Mono- and diglycerides of fatty acids.",
      "en": "Thickeners & Emulsifiers: Mono- and diglycerides of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E471 (Mono- and diglycerides of fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "MONO- AND DIGLYCERIDES OF FATTY ACIDS -E471- refers to a food additive composed of diglycerides and monoglycerides which is used as an emulsifier."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E470AII",
    "name": "Potassium salts of fatty acids",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Potassium salts of fatty acids.",
      "en": "Thickeners & Emulsifiers: Potassium salts of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E470AII (Potassium salts of fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E470AII (Potassium salts of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E451I",
    "name": "Pentasodium triphosphate",
    "alternateNames": [
      "Wikidata: Q29145"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Pentasodium triphosphate.",
      "en": "Thickeners & Emulsifiers: Pentasodium triphosphate."
    },
    "description": {
      "ua": "Харчова добавка E451I (Pentasodium triphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E451I (Pentasodium triphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E541II",
    "name": "Alkaline sodium aluminium phosphate",
    "alternateNames": [
      "Wikidata: Q1220360"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Alkaline sodium aluminium phosphate.",
      "en": "Thickeners & Emulsifiers: Alkaline sodium aluminium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E541II (Alkaline sodium aluminium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E541II (Alkaline sodium aluminium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E501II",
    "name": "Potassium hydrogen carbonate",
    "alternateNames": [
      "Wikidata: Q410529"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Potassium hydrogen carbonate.",
      "en": "Thickeners & Emulsifiers: Potassium hydrogen carbonate."
    },
    "description": {
      "ua": "Харчова добавка E501II (Potassium hydrogen carbonate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E501II (Potassium hydrogen carbonate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E928",
    "name": "Benzole peroxide",
    "alternateNames": [
      "Бензоїл пероксид",
      "Wikidata: Q411424"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Бензоїл пероксид.",
      "en": "Preservatives: Benzole peroxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E928 (Бензоїл пероксид).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E928 (Benzole peroxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E634",
    "name": "Calcium 5'-ribonucleotide",
    "alternateNames": [
      "Wikidata: Q18967192"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Calcium 5'-ribonucleotide.",
      "en": "Flavor Enhancers: Calcium 5'-ribonucleotide."
    },
    "description": {
      "ua": "Харчова добавка E634 (Calcium 5'-ribonucleotide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E634 (Calcium 5'-ribonucleotide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E436",
    "name": "Polyoxyethylene sorbitan tristearate",
    "alternateNames": [
      "Wikidata: Q2103107"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyoxyethylene sorbitan tristearate.",
      "en": "Thickeners & Emulsifiers: Polyoxyethylene sorbitan tristearate."
    },
    "description": {
      "ua": "Харчова добавка E436 (Polyoxyethylene sorbitan tristearate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E436 (Polyoxyethylene sorbitan tristearate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E414A",
    "name": "E414a food additive",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: E414a food additive.",
      "en": "Thickeners & Emulsifiers: E414a food additive."
    },
    "description": {
      "ua": "Харчова добавка E414A (E414a food additive), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E414A (E414a food additive)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E452I",
    "name": "Sodium polyphosphate",
    "alternateNames": [
      "Wikidata: Q11867365"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium polyphosphate.",
      "en": "Thickeners & Emulsifiers: Sodium polyphosphate."
    },
    "description": {
      "ua": "Харчова добавка E452I (Sodium polyphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E452I (Sodium polyphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E472",
    "name": "Acid esters of mono- and diglycerides of fatty acids",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Acid esters of mono- and diglycerides of fatty acids.",
      "en": "Thickeners & Emulsifiers: Acid esters of mono- and diglycerides of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E472 (Acid esters of mono- and diglycerides of fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E472 (Acid esters of mono- and diglycerides of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E552",
    "name": "Calcium silicate",
    "alternateNames": [
      "Wikidata: Q416365"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Calcium silicate.",
      "en": "Other: Calcium silicate."
    },
    "description": {
      "ua": "Харчова добавка E552 (Calcium silicate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E552 (Calcium silicate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1100",
    "name": "Alpha-Amylase",
    "alternateNames": [
      "Wikidata: Q17153"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Alpha-Amylase.",
      "en": "Other: Alpha-Amylase."
    },
    "description": {
      "ua": "Харчова добавка E1100 (Alpha-Amylase), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1100 (Alpha-Amylase)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E517",
    "name": "Ammonium sulphate",
    "alternateNames": [
      "Wikidata: Q191831"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ammonium sulphate.",
      "en": "Other: Ammonium sulphate."
    },
    "description": {
      "ua": "Харчова добавка E517 (Ammonium sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E517 (Ammonium sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E519",
    "name": "Copper sulphate",
    "alternateNames": [
      "Wikidata: Q107184"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Copper sulphate.",
      "en": "Other: Copper sulphate."
    },
    "description": {
      "ua": "Харчова добавка E519 (Copper sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E519 (Copper sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1441",
    "name": "Hydroxy propyl distarch glycerine",
    "alternateNames": [
      "Wikidata: Q18967235"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Hydroxy propyl distarch glycerine.",
      "en": "Thickeners & Emulsifiers: Hydroxy propyl distarch glycerine."
    },
    "description": {
      "ua": "Харчова добавка E1441 (Hydroxy propyl distarch glycerine), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1441 (Hydroxy propyl distarch glycerine)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E965I",
    "name": "D-Maltitol",
    "alternateNames": [
      "Wikidata: Q423882"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: D-Maltitol.",
      "en": "Sweeteners: D-Maltitol."
    },
    "description": {
      "ua": "Харчова добавка E965I (D-Maltitol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E965I (D-Maltitol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E905",
    "name": "Synthetic wax",
    "alternateNames": [
      "Wikidata: Q579764"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Synthetic wax.",
      "en": "Other: Synthetic wax."
    },
    "description": {
      "ua": "Харчова добавка E905 (Synthetic wax), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E905 (Synthetic wax)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E945",
    "name": "Chloropentafluoroethane",
    "alternateNames": [
      "Хлоропентафторетан",
      "Wikidata: Q419702"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Хлоропентафторетан.",
      "en": "Other: Chloropentafluoroethane."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E945 (Хлоропентафторетан).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E945 (Chloropentafluoroethane)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E267",
    "name": "Buffered vinegar",
    "alternateNames": [
      "Wikidata: Q130547560"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Buffered vinegar.",
      "en": "Preservatives: Buffered vinegar."
    },
    "description": {
      "ua": "Харчова добавка E267 (Buffered vinegar), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E267 (Buffered vinegar)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161B",
    "name": "Lutein",
    "alternateNames": [
      "Лютеїн",
      "Wikidata: Q422067"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Лютеїн.",
      "en": "Colors: Lutein."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E161B (Лютеїн).",
      "en": "LUTEIN is a xanthophyll and one of 600 known naturally occurring carotenoids. Lutein is extracted from the petals of African marigold -Tagetes erecta-. It is approved for use in the EU and Australia and New Zealand. In the United States lutein may not be used as a food coloring for foods intended for human consumption, but can be added to animal feed."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E440I",
    "name": "Non-amidated pectines",
    "alternateNames": [
      "Wikidata: Q188154"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Non-amidated pectines.",
      "en": "Thickeners & Emulsifiers: Non-amidated pectines."
    },
    "description": {
      "ua": "Харчова добавка E440I (Non-amidated pectines), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E440I (Non-amidated pectines)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E540",
    "name": "Dicalcium diphosphate",
    "alternateNames": [
      "Wikidata: Q18967208"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Dicalcium diphosphate.",
      "en": "Other: Dicalcium diphosphate."
    },
    "description": {
      "ua": "Харчова добавка E540 (Dicalcium diphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E540 (Dicalcium diphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E500",
    "name": "Sodium carbonates",
    "alternateNames": [
      "Wikidata: Q190227"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium carbonates.",
      "en": "Thickeners & Emulsifiers: Sodium carbonates."
    },
    "description": {
      "ua": "Харчова добавка E500 (Sodium carbonates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "SODIUM CARBONATES is the generic term for a Na and CO3 combination."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E473A",
    "name": "Oligoesters of sucrose type I",
    "alternateNames": [
      "Wikidata: Q32418"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Oligoesters of sucrose type I.",
      "en": "Thickeners & Emulsifiers: Oligoesters of sucrose type I."
    },
    "description": {
      "ua": "Харчова добавка E473A (Oligoesters of sucrose type I), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E473A (Oligoesters of sucrose type I)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E460",
    "name": "Cellulose",
    "alternateNames": [
      "Целюлоза",
      "Wikidata: Q80294"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Целюлоза.",
      "en": "Thickeners & Emulsifiers: Cellulose."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E460 (Целюлоза).",
      "en": "CELLULOSE is an organic compound with the formula -C6H10O5-n, a polysaccharide consisting of a linear chain of several hundred to many thousands of linked D-glucose units. Cellulose is used as emulsifier, thickener and stabilizer in processed foods. Cellulose powder is, for example, used in processed cheese to prevent caking inside the package."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E553A",
    "name": "Synthetic magnesium silicates",
    "alternateNames": [
      "Синтетичні силікати магнію"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Синтетичні силікати магнію.",
      "en": "Thickeners & Emulsifiers: Synthetic magnesium silicates."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E553A (Синтетичні силікати магнію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E553A (Synthetic magnesium silicates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E106",
    "name": "Flavin mononucleotide",
    "alternateNames": [
      "Wikidata: Q376061"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Flavin mononucleotide.",
      "en": "Colors: Flavin mononucleotide."
    },
    "description": {
      "ua": "Харчова добавка E106 (Flavin mononucleotide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E106 (Flavin mononucleotide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E542",
    "name": "Bone phosphate",
    "alternateNames": [
      "Кiстковий фосфат",
      "Wikidata: Q18967189"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Кiстковий фосфат.",
      "en": "Thickeners & Emulsifiers: Bone phosphate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E542 (Кiстковий фосфат).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E542 (Bone phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E337",
    "name": "Potassium sodium tartrate",
    "alternateNames": [
      "Wikidata: Q303489"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Potassium sodium tartrate.",
      "en": "Antioxidants: Potassium sodium tartrate."
    },
    "description": {
      "ua": "Харчова добавка E337 (Potassium sodium tartrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E337 (Potassium sodium tartrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E339",
    "name": "Sodium phosphates",
    "alternateNames": [
      "Wikidata: Q3249706"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium phosphates.",
      "en": "Preservatives: Sodium phosphates."
    },
    "description": {
      "ua": "Харчова добавка E339 (Sodium phosphates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "SODIUM PHOSPHATES is a generic term for a variety of salts of sodium -Na+- and phosphate -PO43−-."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E462",
    "name": "Ethyl cellulose",
    "alternateNames": [
      "Wikidata: Q420113"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Ethyl cellulose.",
      "en": "Thickeners & Emulsifiers: Ethyl cellulose."
    },
    "description": {
      "ua": "Харчова добавка E462 (Ethyl cellulose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E462 (Ethyl cellulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E502",
    "name": "Carbonates",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Carbonates.",
      "en": "Other: Carbonates."
    },
    "description": {
      "ua": "Харчова добавка E502 (Carbonates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E502 (Carbonates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E227",
    "name": "Calcium bisulphite",
    "alternateNames": [
      "Wikidata: Q419619"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Calcium bisulphite.",
      "en": "Preservatives: Calcium bisulphite."
    },
    "description": {
      "ua": "Харчова добавка E227 (Calcium bisulphite), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E227 (Calcium bisulphite)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160F",
    "name": "Ethyl ester of beta-apo-8'-carotenic acid (C 30)",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Ethyl ester of beta-apo-8'-carotenic acid (C 30).",
      "en": "Colors: Ethyl ester of beta-apo-8'-carotenic acid (C 30)."
    },
    "description": {
      "ua": "Харчова добавка E160F (Ethyl ester of beta-apo-8'-carotenic acid (C 30)), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160F (Ethyl ester of beta-apo-8'-carotenic acid (C 30))."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E450I",
    "name": "Disodium diphosphate",
    "alternateNames": [
      "Wikidata: Q418498"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Disodium diphosphate.",
      "en": "Thickeners & Emulsifiers: Disodium diphosphate."
    },
    "description": {
      "ua": "Харчова добавка E450I (Disodium diphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "SODIUM ACID PYROPHOSPHATE is an additive used to prevent the formation of crystals and discoloration of the crab meat."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E550",
    "name": "Sodium silicate",
    "alternateNames": [
      "Силікат натрію",
      "Wikidata: Q18944661"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Силікат натрію.",
      "en": "Other: Sodium silicate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E550 (Силікат натрію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E550 (Sodium silicate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E470",
    "name": "Sodium/potassium/calcium and magnesium salts of fatty acids",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium/potassium/calcium and magnesium salts of fatty acids.",
      "en": "Thickeners & Emulsifiers: Sodium/potassium/calcium and magnesium salts of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E470 (Sodium/potassium/calcium and magnesium salts of fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E470 (Sodium/potassium/calcium and magnesium salts of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E333II",
    "name": "Dicalcium citrate",
    "alternateNames": [
      "Wikidata: Q420280"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Dicalcium citrate.",
      "en": "Antioxidants: Dicalcium citrate."
    },
    "description": {
      "ua": "Харчова добавка E333II (Dicalcium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E333II (Dicalcium citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E460II",
    "name": "Powdered cellulose",
    "alternateNames": [
      "Wikidata: Q80294"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Powdered cellulose.",
      "en": "Thickeners & Emulsifiers: Powdered cellulose."
    },
    "description": {
      "ua": "Харчова добавка E460II (Powdered cellulose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E460II (Powdered cellulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E500II",
    "name": "Sodium hydrogen carbonate",
    "alternateNames": [
      "Гідрокарбонат натрію",
      "Wikidata: Q179731"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Гідрокарбонат натрію.",
      "en": "Thickeners & Emulsifiers: Sodium hydrogen carbonate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E500II (Гідрокарбонат натрію).",
      "en": "SODIUM BICARBONATE -sodium hydrogen carbonate-, commonly known as baking soda, is a chemical compound with the formula NaHCO3."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E939",
    "name": "Helium",
    "alternateNames": [
      "Wikidata: Q560"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Helium.",
      "en": "Other: Helium."
    },
    "description": {
      "ua": "Харчова добавка E939 (Helium), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E939 (Helium)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E513",
    "name": "Sulphuric acid",
    "alternateNames": [
      "Wikidata: Q4118"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Sulphuric acid.",
      "en": "Other: Sulphuric acid."
    },
    "description": {
      "ua": "Харчова добавка E513 (Sulphuric acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E513 (Sulphuric acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E284",
    "name": "Boric acid",
    "alternateNames": [
      "Wikidata: Q187045"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Boric acid.",
      "en": "Preservatives: Boric acid."
    },
    "description": {
      "ua": "Харчова добавка E284 (Boric acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E284 (Boric acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1430",
    "name": "Distarch glycerine",
    "alternateNames": [
      "Wikidata: Q18967212"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Distarch glycerine.",
      "en": "Thickeners & Emulsifiers: Distarch glycerine."
    },
    "description": {
      "ua": "Харчова добавка E1430 (Distarch glycerine), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1430 (Distarch glycerine)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E355",
    "name": "Adipic acid",
    "alternateNames": [
      "Wikidata: Q357415"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Adipic acid.",
      "en": "Antioxidants: Adipic acid."
    },
    "description": {
      "ua": "Харчова добавка E355 (Adipic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E355 (Adipic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E960C",
    "name": "Enzymatically produced steviol glycosides",
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Enzymatically produced steviol glycosides.",
      "en": "Sweeteners: Enzymatically produced steviol glycosides."
    },
    "description": {
      "ua": "Харчова добавка E960C (Enzymatically produced steviol glycosides), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E960C (Enzymatically produced steviol glycosides)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E307B",
    "name": "Concentrated tocopherol",
    "alternateNames": [
      "Wikidata: Q158348"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Concentrated tocopherol.",
      "en": "Antioxidants: Concentrated tocopherol."
    },
    "description": {
      "ua": "Харчова добавка E307B (Concentrated tocopherol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E307B (Concentrated tocopherol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E263",
    "name": "Calcium acetate",
    "alternateNames": [
      "Wikidata: Q409251"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Calcium acetate.",
      "en": "Preservatives: Calcium acetate."
    },
    "description": {
      "ua": "Харчова добавка E263 (Calcium acetate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "CALCIUM ACETATE is a chemical compound which is a calcium salt of acetic acid. Calcium acetate is used as a food additive, as a stabilizer, buffer and sequestrant, mainly in candy products."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E333",
    "name": "Calcium citrates",
    "alternateNames": [
      "Wikidata: Q420280"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium citrates.",
      "en": "Antioxidants: Calcium citrates."
    },
    "description": {
      "ua": "Харчова добавка E333 (Calcium citrates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E333 (Calcium citrates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E223",
    "name": "Sodium metabisulphite",
    "alternateNames": [
      "Wikidata: Q284549"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium metabisulphite.",
      "en": "Preservatives: Sodium metabisulphite."
    },
    "description": {
      "ua": "Харчова добавка E223 (Sodium metabisulphite), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "SODIUM METABISULFITE or sodium pyrosulfite -IUPAC spelling; Br. E. sodium metabisulphite or sodium pyrosulphite- is an inorganic compound of chemical formula Na2S2O5."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E420II",
    "name": "Sorbitol syrup",
    "alternateNames": [
      "Wikidata: Q245280"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Sorbitol syrup.",
      "en": "Sweeteners: Sorbitol syrup."
    },
    "description": {
      "ua": "Харчова добавка E420II (Sorbitol syrup), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E420II (Sorbitol syrup)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E943B",
    "name": "Isobutane",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Isobutane.",
      "en": "Other: Isobutane."
    },
    "description": {
      "ua": "Харчова добавка E943B (Isobutane), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E943B (Isobutane)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E339II",
    "name": "Disodium phosphate",
    "alternateNames": [
      "Wikidata: Q418448"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Disodium phosphate.",
      "en": "Preservatives: Disodium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E339II (Disodium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "DISODIUM PHOSPHATE, or sodium hydrogen phosphate, or sodium phosphate dibasic, is the inorganic compound with the formula Na2HPO4."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E345",
    "name": "Magnesium citrate",
    "alternateNames": [
      "Wikidata: Q45044"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Magnesium citrate.",
      "en": "Antioxidants: Magnesium citrate."
    },
    "description": {
      "ua": "Харчова добавка E345 (Magnesium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E345 (Magnesium citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E332II",
    "name": "Tripotassium citrate",
    "alternateNames": [
      "Wikidata: Q419921"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Tripotassium citrate.",
      "en": "Antioxidants: Tripotassium citrate."
    },
    "description": {
      "ua": "Харчова добавка E332II (Tripotassium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "POTASSIUM CITRATE -also known as tripotassium citrate- is a potassium salt of citric acid with the molecular formula K3C6H5O7. As a food additive, potassium citrate is used to regulate acidity. It is also used in many soft drinks as a buffering agent."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E535",
    "name": "Sodium ferrocyanide",
    "alternateNames": [
      "Wikidata: Q418474"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Sodium ferrocyanide.",
      "en": "Other: Sodium ferrocyanide."
    },
    "description": {
      "ua": "Харчова добавка E535 (Sodium ferrocyanide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E535 (Sodium ferrocyanide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E472D",
    "name": "Tartaric acid esters of mono- and diglycerides of fatty acids",
    "alternateNames": [
      "Естери моно- та дигліцеридів та винної і жирних кислот"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Естери моно- та дигліцеридів та винної і жирних кислот.",
      "en": "Thickeners & Emulsifiers: Tartaric acid esters of mono- and diglycerides of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E472D (Естери моно- та дигліцеридів та винної і жирних кислот).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E472D (Tartaric acid esters of mono- and diglycerides of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E930",
    "name": "Calcium peroxide",
    "alternateNames": [
      "Пероксид кальцію",
      "Wikidata: Q94770"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Пероксид кальцію.",
      "en": "Other: Calcium peroxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E930 (Пероксид кальцію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E930 (Calcium peroxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E484",
    "name": "Stearyl citrate",
    "alternateNames": [
      "Стеарил цитрат",
      "Wikidata: Q18608830"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Стеарил цитрат.",
      "en": "Antioxidants: Stearyl citrate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E484 (Стеарил цитрат).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E484 (Stearyl citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "EXXX",
    "name": "Exxx - Exxx food additive",
    "alternateNames": [
      "Wikidata: Q118843"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Exxx - Exxx food additive.",
      "en": "Thickeners & Emulsifiers: Exxx - Exxx food additive."
    },
    "description": {
      "ua": "Харчова добавка EXXX (Exxx - Exxx food additive), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as EXXX (Exxx - Exxx food additive)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E905D",
    "name": "Mineral oil high viscosity",
    "alternateNames": [
      "Wikidata: Q579764"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Mineral oil high viscosity.",
      "en": "Other: Mineral oil high viscosity."
    },
    "description": {
      "ua": "Харчова добавка E905D (Mineral oil high viscosity), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E905D (Mineral oil high viscosity)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E543",
    "name": "E543 food additive",
    "alternateNames": [
      "Wikidata: Q18967200"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: E543 food additive.",
      "en": "Other: E543 food additive."
    },
    "description": {
      "ua": "Харчова добавка E543 (E543 food additive), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E543 (E543 food additive)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1202",
    "name": "Polyvinylpolypyrrolidone",
    "alternateNames": [
      "Wikidata: Q3003746"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyvinylpolypyrrolidone.",
      "en": "Thickeners & Emulsifiers: Polyvinylpolypyrrolidone."
    },
    "description": {
      "ua": "Харчова добавка E1202 (Polyvinylpolypyrrolidone), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1202 (Polyvinylpolypyrrolidone)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E463",
    "name": "Hydroxypropyl cellulose",
    "alternateNames": [
      "Wikidata: Q724426"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Hydroxypropyl cellulose.",
      "en": "Thickeners & Emulsifiers: Hydroxypropyl cellulose."
    },
    "description": {
      "ua": "Харчова добавка E463 (Hydroxypropyl cellulose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E463 (Hydroxypropyl cellulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E470A",
    "name": "Sodium/potassium and calcium salts of fatty acids",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium/potassium and calcium salts of fatty acids.",
      "en": "Thickeners & Emulsifiers: Sodium/potassium and calcium salts of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E470A (Sodium/potassium and calcium salts of fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E470A (Sodium/potassium and calcium salts of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E503",
    "name": "Ammonium carbonates",
    "alternateNames": [
      "Wikidata: Q204873"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ammonium carbonates.",
      "en": "Other: Ammonium carbonates."
    },
    "description": {
      "ua": "Харчова добавка E503 (Ammonium carbonates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E503 (Ammonium carbonates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E472A",
    "name": "Acetic acid esters of mono- and diglycerides of fatty acids",
    "alternateNames": [
      "Естери гліцерину та оцтової і жирних кислот",
      "Wikidata: Q26806065"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Естери гліцерину та оцтової і жирних кислот.",
      "en": "Thickeners & Emulsifiers: Acetic acid esters of mono- and diglycerides of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E472A (Естери гліцерину та оцтової і жирних кислот).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E472A (Acetic acid esters of mono- and diglycerides of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E163F",
    "name": "Petunidin",
    "alternateNames": [
      "Wikidata: Q27145212"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Petunidin.",
      "en": "Colors: Petunidin."
    },
    "description": {
      "ua": "Харчова добавка E163F (Petunidin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E163F (Petunidin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E315",
    "name": "Erythorbic acid",
    "alternateNames": [
      "Wikidata: Q424531"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Erythorbic acid.",
      "en": "Antioxidants: Erythorbic acid."
    },
    "description": {
      "ua": "Харчова добавка E315 (Erythorbic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E315 (Erythorbic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E932",
    "name": "Nitrogen oxide",
    "alternateNames": [
      "Оксид азоту"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Оксид азоту.",
      "en": "Other: Nitrogen oxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E932 (Оксид азоту).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E932 (Nitrogen oxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E905A",
    "name": "Mineral oil",
    "alternateNames": [
      "Мінеральні масла",
      "Wikidata: Q583582"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Мінеральні масла.",
      "en": "Other: Mineral oil."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E905A (Мінеральні масла).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E905A (Mineral oil)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E262II",
    "name": "Sodium diacetate",
    "alternateNames": [
      "Wikidata: Q409655"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium diacetate.",
      "en": "Preservatives: Sodium diacetate."
    },
    "description": {
      "ua": "Харчова добавка E262II (Sodium diacetate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E262II (Sodium diacetate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E473",
    "name": "Sucrose esters of fatty acids",
    "alternateNames": [
      "Wikidata: Q55648666"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sucrose esters of fatty acids.",
      "en": "Thickeners & Emulsifiers: Sucrose esters of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E473 (Sucrose esters of fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E473 (Sucrose esters of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E553",
    "name": "Magnesium silicates",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Magnesium silicates.",
      "en": "Thickeners & Emulsifiers: Magnesium silicates."
    },
    "description": {
      "ua": "Харчова добавка E553 (Magnesium silicates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E553 (Magnesium silicates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160BII",
    "name": "Annatto norbixin",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Annatto norbixin.",
      "en": "Colors: Annatto norbixin."
    },
    "description": {
      "ua": "Харчова добавка E160BII (Annatto norbixin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160BII (Annatto norbixin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E915",
    "name": "Esters of Colophane",
    "alternateNames": [
      "Wikidata: Q18935691"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Esters of Colophane.",
      "en": "Other: Esters of Colophane."
    },
    "description": {
      "ua": "Харчова добавка E915 (Esters of Colophane), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E915 (Esters of Colophane)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E503II",
    "name": "Ammonium hydrogen carbonate",
    "alternateNames": [
      "Wikidata: Q421123"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ammonium hydrogen carbonate.",
      "en": "Other: Ammonium hydrogen carbonate."
    },
    "description": {
      "ua": "Харчова добавка E503II (Ammonium hydrogen carbonate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "AMMONIUM BICARBONATE is an inorganic compound with formula -NH4-HCO3, simplified to NH5CO3. Ammonium bicarbonate is used in the food industry as a raising agent for flat baked goods, such as cookies and crackers, and in China in steamed buns and Chinese almond cookies. It was commonly used in the home before modern day baking powder was made available."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E440AIII",
    "name": "Potassium pectate",
    "alternateNames": [
      "Wikidata: Q188154"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Potassium pectate.",
      "en": "Thickeners & Emulsifiers: Potassium pectate."
    },
    "description": {
      "ua": "Харчова добавка E440AIII (Potassium pectate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E440AIII (Potassium pectate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1520",
    "name": "Propylene Glycol",
    "alternateNames": [
      "Wikidata: Q18967269"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Propylene Glycol.",
      "en": "Thickeners & Emulsifiers: Propylene Glycol."
    },
    "description": {
      "ua": "Харчова добавка E1520 (Propylene Glycol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1520 (Propylene Glycol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E510",
    "name": "Ammonium chloride",
    "alternateNames": [
      "Хлорид амонію",
      "Wikidata: Q188543"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Хлорид амонію.",
      "en": "Other: Ammonium chloride."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E510 (Хлорид амонію).",
      "en": "AMMONIUM CHLORIDE is an inorganic compound with the formula NH4Cl and a white crystalline salt that is highly soluble in water."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1400",
    "name": "Dextrin",
    "alternateNames": [
      "Декстрин",
      "Wikidata: Q191978"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Декстрин.",
      "en": "Thickeners & Emulsifiers: Dextrin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1400 (Декстрин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1400 (Dextrin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1000",
    "name": "Cholic acid",
    "alternateNames": [
      "Холева кислота",
      "Wikidata: Q287415"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Інші добавки: Холева кислота.",
      "en": "Other: Cholic acid."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1000 (Холева кислота).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1000 (Cholic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E222",
    "name": "Sodium bisulphite",
    "alternateNames": [
      "Wikidata: Q407891"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium bisulphite.",
      "en": "Preservatives: Sodium bisulphite."
    },
    "description": {
      "ua": "Харчова добавка E222 (Sodium bisulphite), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "SODIUM BISULFITE is a chemical compound with the chemical formula NaHSO3."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E507",
    "name": "Hydrochloric acid",
    "alternateNames": [
      "Wikidata: Q2409"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Hydrochloric acid.",
      "en": "Other: Hydrochloric acid."
    },
    "description": {
      "ua": "Харчова добавка E507 (Hydrochloric acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E507 (Hydrochloric acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E467",
    "name": "Ethulose",
    "alternateNames": [
      "Wikidata: Q5404436"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Ethulose.",
      "en": "Thickeners & Emulsifiers: Ethulose."
    },
    "description": {
      "ua": "Харчова добавка E467 (Ethulose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E467 (Ethulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E260",
    "name": "Acetic acid",
    "alternateNames": [
      "Оцтова кислота",
      "Wikidata: Q47512"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Оцтова кислота.",
      "en": "Preservatives: Acetic acid."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E260 (Оцтова кислота).",
      "en": "ACETIC ACID, systematically named ethanoic acid, is a colourless liquid organic compound with the chemical formula CH3COOH -also written as CH3CO2H or C2H4O2-."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E332",
    "name": "Potassium citrates",
    "alternateNames": [
      "Wikidata: Q419921"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Potassium citrates.",
      "en": "Antioxidants: Potassium citrates."
    },
    "description": {
      "ua": "Харчова добавка E332 (Potassium citrates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E332 (Potassium citrates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E509",
    "name": "Calcium chloride",
    "alternateNames": [
      "Кальцій хлористий",
      "Wikidata: Q208451"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Кальцій хлористий.",
      "en": "Thickeners & Emulsifiers: Calcium chloride."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E509 (Кальцій хлористий).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E509 (Calcium chloride)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E469",
    "name": "Enzymatically hydrolysed carboxymethylcellulose",
    "alternateNames": [
      "Wikidata: Q18935689"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Enzymatically hydrolysed carboxymethylcellulose.",
      "en": "Thickeners & Emulsifiers: Enzymatically hydrolysed carboxymethylcellulose."
    },
    "description": {
      "ua": "Харчова добавка E469 (Enzymatically hydrolysed carboxymethylcellulose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E469 (Enzymatically hydrolysed carboxymethylcellulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E262",
    "name": "Sodium acetates",
    "alternateNames": [
      "Wikidata: Q339940"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium acetates.",
      "en": "Preservatives: Sodium acetates."
    },
    "description": {
      "ua": "Харчова добавка E262 (Sodium acetates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E262 (Sodium acetates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E625",
    "name": "Magnesium diglutamate",
    "alternateNames": [
      "Wikidata: Q1821120"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Magnesium diglutamate.",
      "en": "Flavor Enhancers: Magnesium diglutamate."
    },
    "description": {
      "ua": "Харчова добавка E625 (Magnesium diglutamate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E625 (Magnesium diglutamate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E330",
    "name": "Citric acid",
    "alternateNames": [
      "Лимонная кислота",
      "Wikidata: Q159683"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Лимонная кислота.",
      "en": "Antioxidants: Citric acid."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E330 (Лимонная кислота).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E330 (Citric acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E427",
    "name": "Cassia gum",
    "alternateNames": [
      "Wikidata: Q3110379"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Cassia gum.",
      "en": "Thickeners & Emulsifiers: Cassia gum."
    },
    "description": {
      "ua": "Харчова добавка E427 (Cassia gum), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E427 (Cassia gum)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1420",
    "name": "Acetylated starch",
    "alternateNames": [
      "Wikidata: Q18935684"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Acetylated starch.",
      "en": "Thickeners & Emulsifiers: Acetylated starch."
    },
    "description": {
      "ua": "Харчова добавка E1420 (Acetylated starch), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1420 (Acetylated starch)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E429",
    "name": "Peptone",
    "alternateNames": [
      "Wikidata: Q424323"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Peptone.",
      "en": "Thickeners & Emulsifiers: Peptone."
    },
    "description": {
      "ua": "Харчова добавка E429 (Peptone), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E429 (Peptone)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E220",
    "name": "Sulphur dioxide",
    "alternateNames": [
      "Діоксид сірки",
      "Wikidata: Q5282"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Діоксид сірки.",
      "en": "Preservatives: Sulphur dioxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E220 (Діоксид сірки).",
      "en": "SULPHUR DIOXIDE is the chemical compound with the formula SO2."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E900B",
    "name": "Methylphenylpolysiloxane",
    "alternateNames": [
      "Wikidata: Q411955"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Methylphenylpolysiloxane.",
      "en": "Other: Methylphenylpolysiloxane."
    },
    "description": {
      "ua": "Харчова добавка E900B (Methylphenylpolysiloxane), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E900B (Methylphenylpolysiloxane)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E512",
    "name": "Stannous chloride",
    "alternateNames": [
      "Wikidata: Q204964"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Stannous chloride.",
      "en": "Antioxidants: Stannous chloride."
    },
    "description": {
      "ua": "Харчова добавка E512 (Stannous chloride), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E512 (Stannous chloride)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E559",
    "name": "Aluminium silicate",
    "alternateNames": [
      "Wikidata: Q1284687"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Aluminium silicate.",
      "en": "Other: Aluminium silicate."
    },
    "description": {
      "ua": "Харчова добавка E559 (Aluminium silicate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E559 (Aluminium silicate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E270",
    "name": "Lactic acid",
    "alternateNames": [
      "Молочна кислота",
      "Wikidata: Q56626856"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Молочна кислота.",
      "en": "Preservatives: Lactic acid."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E270 (Молочна кислота).",
      "en": "LACTIC ACID is an organic compound with the formula CH3CH-OH-CO2H. In the form of its conjugate base called lactate -this is thus different and kept as a separate ingredient-. Lactic acid is chiral, consisting of two optical isomers. One is known as L--+--lactic acid or -S--lactic acid and the other, its mirror image, is D--−--lactic acid or -R--lactic acid in food the L-variant is used"
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E477",
    "name": "Propane-1‚2-diol esters of fatty acids",
    "alternateNames": [
      "Wikidata: Q18944660"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Propane-1‚2-diol esters of fatty acids.",
      "en": "Thickeners & Emulsifiers: Propane-1‚2-diol esters of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E477 (Propane-1‚2-diol esters of fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E477 (Propane-1‚2-diol esters of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E557",
    "name": "Hemimorphite",
    "alternateNames": [
      "Wikidata: Q408008"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Hemimorphite.",
      "en": "Other: Hemimorphite."
    },
    "description": {
      "ua": "Харчова добавка E557 (Hemimorphite), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E557 (Hemimorphite)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E107",
    "name": "Yellow 2G",
    "alternateNames": [
      "Wikidata: Q424528"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Yellow 2G.",
      "en": "Colors: Yellow 2G."
    },
    "description": {
      "ua": "Харчова добавка E107 (Yellow 2G), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E107 (Yellow 2G)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E336",
    "name": "Potassium tartrates",
    "alternateNames": [
      "Wikidata: Q65770886"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Potassium tartrates.",
      "en": "Antioxidants: Potassium tartrates."
    },
    "description": {
      "ua": "Харчова добавка E336 (Potassium tartrates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E336 (Potassium tartrates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E304II",
    "name": "Ascorbyl stearate",
    "alternateNames": [
      "Wikidata: Q424521"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ascorbyl stearate.",
      "en": "Antioxidants: Ascorbyl stearate."
    },
    "description": {
      "ua": "Харчова добавка E304II (Ascorbyl stearate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E304II (Ascorbyl stearate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E226",
    "name": "Calcium sulphite",
    "alternateNames": [
      "Wikidata: Q899410"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Calcium sulphite.",
      "en": "Preservatives: Calcium sulphite."
    },
    "description": {
      "ua": "Харчова добавка E226 (Calcium sulphite), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E226 (Calcium sulphite)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E110",
    "name": "Sunset yellow FCF",
    "alternateNames": [
      "Wikidata: Q410095"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Sunset yellow FCF.",
      "en": "Colors: Sunset yellow FCF."
    },
    "description": {
      "ua": "Харчова добавка E110 (Sunset yellow FCF), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E110 (Sunset yellow FCF)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160AIII",
    "name": "Beta-carotene from blakeslea trispora",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Beta-carotene from blakeslea trispora.",
      "en": "Colors: Beta-carotene from blakeslea trispora."
    },
    "description": {
      "ua": "Харчова добавка E160AIII (Beta-carotene from blakeslea trispora), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160AIII (Beta-carotene from blakeslea trispora)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E921",
    "name": "L-cysteine hydrochloride monohydrate",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: L-cysteine hydrochloride monohydrate.",
      "en": "Other: L-cysteine hydrochloride monohydrate."
    },
    "description": {
      "ua": "Харчова добавка E921 (L-cysteine hydrochloride monohydrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E921 (L-cysteine hydrochloride monohydrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E516",
    "name": "Calcium sulphate",
    "alternateNames": [
      "Сульфат кальцію",
      "Wikidata: Q407258"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Сульфат кальцію.",
      "en": "Thickeners & Emulsifiers: Calcium sulphate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E516 (Сульфат кальцію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E516 (Calcium sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1451",
    "name": "Acetylated oxidised starch",
    "alternateNames": [
      "Wikidata: Q2633813"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Acetylated oxidised starch.",
      "en": "Thickeners & Emulsifiers: Acetylated oxidised starch."
    },
    "description": {
      "ua": "Харчова добавка E1451 (Acetylated oxidised starch), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1451 (Acetylated oxidised starch)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E961",
    "name": "Neotame",
    "alternateNames": [
      "Wikidata: Q415698"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Neotame.",
      "en": "Sweeteners: Neotame."
    },
    "description": {
      "ua": "Харчова добавка E961 (Neotame), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E961 (Neotame)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E944",
    "name": "Propane",
    "alternateNames": [
      "Wikidata: Q131189"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Propane.",
      "en": "Other: Propane."
    },
    "description": {
      "ua": "Харчова добавка E944 (Propane), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E944 (Propane)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E904",
    "name": "Shellac",
    "alternateNames": [
      "Шелак",
      "Wikidata: Q429659"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "caution",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Інші добавки: Шелак.",
      "en": "Other: Shellac."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E904 (Шелак).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E904 (Shellac)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E266",
    "name": "Sodium dehydroacetate",
    "alternateNames": [
      "Wikidata: Q7553316"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium dehydroacetate.",
      "en": "Preservatives: Sodium dehydroacetate."
    },
    "description": {
      "ua": "Харчова добавка E266 (Sodium dehydroacetate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E266 (Sodium dehydroacetate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160AI",
    "name": "Beta-carotene",
    "alternateNames": [
      "Бета-каротин",
      "Wikidata: Q306135"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Бета-каротин.",
      "en": "Colors: Beta-carotene."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E160AI (Бета-каротин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160AI (Beta-carotene)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E143",
    "name": "Fast Green FCF",
    "alternateNames": [
      "Wikidata: Q898299"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Fast Green FCF.",
      "en": "Colors: Fast Green FCF."
    },
    "description": {
      "ua": "Харчова добавка E143 (Fast Green FCF), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E143 (Fast Green FCF)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E14XX",
    "name": "Modified Starch",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Modified Starch.",
      "en": "Thickeners & Emulsifiers: Modified Starch."
    },
    "description": {
      "ua": "Харчова добавка E14XX (Modified Starch), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E14XX (Modified Starch)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E150A",
    "name": "Plain caramel",
    "alternateNames": [
      "Wikidata: Q227816"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Plain caramel.",
      "en": "Colors: Plain caramel."
    },
    "description": {
      "ua": "Харчова добавка E150A (Plain caramel), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Caramel color or caramel coloring is a water-soluble food coloring."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E103",
    "name": "Alkannin",
    "alternateNames": [
      "Wikidata: Q418237"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Alkannin.",
      "en": "Colors: Alkannin."
    },
    "description": {
      "ua": "Харчова добавка E103 (Alkannin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E103 (Alkannin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E341I",
    "name": "Monocalcium phosphate",
    "alternateNames": [
      "Wikidata: Q414673"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Monocalcium phosphate.",
      "en": "Antioxidants: Monocalcium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E341I (Monocalcium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E341I (Monocalcium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E468",
    "name": "Cross-linked sodium carboxymethylcellulose",
    "alternateNames": [
      "Wikidata: Q18967204"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Cross-linked sodium carboxymethylcellulose.",
      "en": "Thickeners & Emulsifiers: Cross-linked sodium carboxymethylcellulose."
    },
    "description": {
      "ua": "Харчова добавка E468 (Cross-linked sodium carboxymethylcellulose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E468 (Cross-linked sodium carboxymethylcellulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E508",
    "name": "Potassium chloride",
    "alternateNames": [
      "Wikidata: Q184630"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Potassium chloride.",
      "en": "Preservatives: Potassium chloride."
    },
    "description": {
      "ua": "Харчова добавка E508 (Potassium chloride), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E508 (Potassium chloride)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1413",
    "name": "Phosphated distarch phosphate",
    "alternateNames": [
      "Wikidata: Q7187438"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Phosphated distarch phosphate.",
      "en": "Thickeners & Emulsifiers: Phosphated distarch phosphate."
    },
    "description": {
      "ua": "Харчова добавка E1413 (Phosphated distarch phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1413 (Phosphated distarch phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E181",
    "name": "Tannin",
    "alternateNames": [
      "Танін",
      "Wikidata: Q187607"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Танін.",
      "en": "Colors: Tannin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E181 (Танін).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E181 (Tannin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1518",
    "name": "Glyceryl triacetate",
    "alternateNames": [
      "Wikidata: Q83253"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Glyceryl triacetate.",
      "en": "Thickeners & Emulsifiers: Glyceryl triacetate."
    },
    "description": {
      "ua": "Харчова добавка E1518 (Glyceryl triacetate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1518 (Glyceryl triacetate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161G",
    "name": "Canthaxanthin",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Canthaxanthin.",
      "en": "Colors: Canthaxanthin."
    },
    "description": {
      "ua": "Харчова добавка E161G (Canthaxanthin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E161G (Canthaxanthin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E304",
    "name": "Fatty acid esters of ascorbic acid",
    "alternateNames": [
      "Ефіри жирних кислот аскорбінової кислоти",
      "Wikidata: Q43303953"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ефіри жирних кислот аскорбінової кислоти.",
      "en": "Antioxidants: Fatty acid esters of ascorbic acid."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E304 (Ефіри жирних кислот аскорбінової кислоти).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E304 (Fatty acid esters of ascorbic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E344",
    "name": "Lecithin citrate",
    "alternateNames": [
      "Wikidata: Q13081794"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Lecithin citrate.",
      "en": "Antioxidants: Lecithin citrate."
    },
    "description": {
      "ua": "Харчова добавка E344 (Lecithin citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E344 (Lecithin citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E336II",
    "name": "Dipotassium tartrate",
    "alternateNames": [
      "Wikidata: Q418064"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Dipotassium tartrate.",
      "en": "Antioxidants: Dipotassium tartrate."
    },
    "description": {
      "ua": "Харчова добавка E336II (Dipotassium tartrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E336II (Dipotassium tartrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E153",
    "name": "Vegetable carbon",
    "alternateNames": [
      "Wikidata: Q59557415"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Vegetable carbon.",
      "en": "Colors: Vegetable carbon."
    },
    "description": {
      "ua": "Харчова добавка E153 (Vegetable carbon), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E153 (Vegetable carbon)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E150D",
    "name": "Sulphite ammonia caramel",
    "alternateNames": [
      "Wikidata: Q13081740"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Sulphite ammonia caramel.",
      "en": "Colors: Sulphite ammonia caramel."
    },
    "description": {
      "ua": "Харчова добавка E150D (Sulphite ammonia caramel), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E150D (Sulphite ammonia caramel)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E285",
    "name": "Sodium tetraborate (borax)",
    "alternateNames": [
      "Wikidata: Q5319"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium tetraborate (borax).",
      "en": "Preservatives: Sodium tetraborate (borax)."
    },
    "description": {
      "ua": "Харчова добавка E285 (Sodium tetraborate (borax)), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E285 (Sodium tetraborate (borax))."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E478",
    "name": "Lactylated fatty acid esters of glycerol and propane-1",
    "alternateNames": [
      "Wikidata: Q18935693"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Lactylated fatty acid esters of glycerol and propane-1.",
      "en": "Thickeners & Emulsifiers: Lactylated fatty acid esters of glycerol and propane-1."
    },
    "description": {
      "ua": "Харчова добавка E478 (Lactylated fatty acid esters of glycerol and propane-1), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E478 (Lactylated fatty acid esters of glycerol and propane-1)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E558",
    "name": "Bentonite",
    "alternateNames": [
      "Бентоніт",
      "Wikidata: Q380149"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Бентоніт.",
      "en": "Other: Bentonite."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E558 (Бентоніт).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E558 (Bentonite)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E321",
    "name": "Butylated hydroxytoluene",
    "alternateNames": [
      "Wikidata: Q221945"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Butylated hydroxytoluene.",
      "en": "Antioxidants: Butylated hydroxytoluene."
    },
    "description": {
      "ua": "Харчова добавка E321 (Butylated hydroxytoluene), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E321 (Butylated hydroxytoluene)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E428",
    "name": "Gelatine",
    "alternateNames": [
      "Желатин",
      "Wikidata: Q179254"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Желатин.",
      "en": "Thickeners & Emulsifiers: Gelatine."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E428 (Желатин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E428 (Gelatine)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E231",
    "name": "Orthophenyl phenol",
    "alternateNames": [
      "Ортофенілфенол"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Ортофенілфенол.",
      "en": "Preservatives: Orthophenyl phenol."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E231 (Ортофенілфенол).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E231 (Orthophenyl phenol)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E354",
    "name": "Calcium tartrate",
    "alternateNames": [
      "Wikidata: Q416767"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium tartrate.",
      "en": "Antioxidants: Calcium tartrate."
    },
    "description": {
      "ua": "Харчова добавка E354 (Calcium tartrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E354 (Calcium tartrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E314",
    "name": "Guaiacum",
    "alternateNames": [
      "Гваяк"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Гваяк.",
      "en": "Antioxidants: Guaiacum."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E314 (Гваяк).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E314 (Guaiacum)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E518",
    "name": "Magnesium sulphate",
    "alternateNames": [
      "Wikidata: Q288266"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Magnesium sulphate.",
      "en": "Other: Magnesium sulphate."
    },
    "description": {
      "ua": "Харчова добавка E518 (Magnesium sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E518 (Magnesium sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E450V",
    "name": "Tetrapotassium diphosphate",
    "alternateNames": [
      "Wikidata: Q290828"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Tetrapotassium diphosphate.",
      "en": "Thickeners & Emulsifiers: Tetrapotassium diphosphate."
    },
    "description": {
      "ua": "Харчова добавка E450V (Tetrapotassium diphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E450V (Tetrapotassium diphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E140II",
    "name": "Chlorophyllins",
    "alternateNames": [
      "Wikidata: Q82182"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Chlorophyllins.",
      "en": "Colors: Chlorophyllins."
    },
    "description": {
      "ua": "Харчова добавка E140II (Chlorophyllins), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E140II (Chlorophyllins)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E452V",
    "name": "Ammonium polyphosphate",
    "alternateNames": [
      "Поліфосфат амонію",
      "Wikidata: Q11867365"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Поліфосфат амонію.",
      "en": "Thickeners & Emulsifiers: Ammonium polyphosphate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E452V (Поліфосфат амонію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E452V (Ammonium polyphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E485",
    "name": "Sodium stearoyl fumarate",
    "alternateNames": [
      "Wikidata: Q18967283"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium stearoyl fumarate.",
      "en": "Thickeners & Emulsifiers: Sodium stearoyl fumarate."
    },
    "description": {
      "ua": "Харчова добавка E485 (Sodium stearoyl fumarate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E485 (Sodium stearoyl fumarate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E338",
    "name": "Phosphoric acid",
    "alternateNames": [
      "Ортофосфорна кислота",
      "Wikidata: Q184782"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ортофосфорна кислота.",
      "en": "Antioxidants: Phosphoric acid."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E338 (Ортофосфорна кислота).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E338 (Phosphoric acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E431",
    "name": "Polyoxyethylene (40) stearate",
    "alternateNames": [
      "Wikidata: Q18636149"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyoxyethylene (40) stearate.",
      "en": "Thickeners & Emulsifiers: Polyoxyethylene (40) stearate."
    },
    "description": {
      "ua": "Харчова добавка E431 (Polyoxyethylene (40) stearate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E431 (Polyoxyethylene (40) stearate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E228",
    "name": "Potassium bisulphite",
    "alternateNames": [
      "Wikidata: Q412524"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Potassium bisulphite.",
      "en": "Preservatives: Potassium bisulphite."
    },
    "description": {
      "ua": "Харчова добавка E228 (Potassium bisulphite), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E228 (Potassium bisulphite)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E140",
    "name": "Chlorophylls and Chlorophyllins",
    "alternateNames": [
      "Wikidata: Q26806091"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Chlorophylls and Chlorophyllins.",
      "en": "Colors: Chlorophylls and Chlorophyllins."
    },
    "description": {
      "ua": "Харчова добавка E140 (Chlorophylls and Chlorophyllins), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E140 (Chlorophylls and Chlorophyllins)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E624",
    "name": "Monoammonium glutamate",
    "alternateNames": [
      "Wikidata: Q8213959"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Monoammonium glutamate.",
      "en": "Flavor Enhancers: Monoammonium glutamate."
    },
    "description": {
      "ua": "Харчова добавка E624 (Monoammonium glutamate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E624 (Monoammonium glutamate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E905CII",
    "name": "Paraffin wax",
    "alternateNames": [
      "Парафін",
      "Wikidata: Q579764"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Парафін.",
      "en": "Other: Paraffin wax."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E905CII (Парафін).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E905CII (Paraffin wax)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E100",
    "name": "Curcumin",
    "alternateNames": [
      "Куркумін",
      "Wikidata: Q312266"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Куркумін.",
      "en": "Colors: Curcumin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E100 (Куркумін).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E100 (Curcumin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E426",
    "name": "Soybean hemicellulose",
    "alternateNames": [
      "Wikidata: Q18967285"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Soybean hemicellulose.",
      "en": "Thickeners & Emulsifiers: Soybean hemicellulose."
    },
    "description": {
      "ua": "Харчова добавка E426 (Soybean hemicellulose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E426 (Soybean hemicellulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E152",
    "name": "Black 7984",
    "alternateNames": [
      "Wikidata: Q764245"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Black 7984.",
      "en": "Colors: Black 7984."
    },
    "description": {
      "ua": "Харчова добавка E152 (Black 7984), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E152 (Black 7984)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E172I",
    "name": "Black iron oxide",
    "alternateNames": [
      "Wikidata: Q3045754"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Black iron oxide.",
      "en": "Colors: Black iron oxide."
    },
    "description": {
      "ua": "Харчова добавка E172I (Black iron oxide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E172I (Black iron oxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E938",
    "name": "Argon",
    "alternateNames": [
      "Wikidata: Q696"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Argon.",
      "en": "Other: Argon."
    },
    "description": {
      "ua": "Харчова добавка E938 (Argon), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E938 (Argon)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E556",
    "name": "Calcium aluminium silicate",
    "alternateNames": [
      "Wikidata: Q18967182"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Calcium aluminium silicate.",
      "en": "Other: Calcium aluminium silicate."
    },
    "description": {
      "ua": "Харчова добавка E556 (Calcium aluminium silicate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E556 (Calcium aluminium silicate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E476",
    "name": "Polyglycerol polyricinoleate",
    "alternateNames": [
      "Wikidata: Q168686"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyglycerol polyricinoleate.",
      "en": "Thickeners & Emulsifiers: Polyglycerol polyricinoleate."
    },
    "description": {
      "ua": "Харчова добавка E476 (Polyglycerol polyricinoleate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E476 (Polyglycerol polyricinoleate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E340III",
    "name": "Tripotassium phosphate",
    "alternateNames": [
      "Wikidata: Q423852"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Tripotassium phosphate.",
      "en": "Antioxidants: Tripotassium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E340III (Tripotassium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E340III (Tripotassium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E150",
    "name": "Caramel",
    "alternateNames": [
      "Карамель",
      "Wikidata: Q183440"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Карамель.",
      "en": "Colors: Caramel."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E150 (Карамель).",
      "en": "CARAMEL is a medium to dark-orange confectionery product made by heating a variety of sugars."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E170I",
    "name": "Calcium carbonate",
    "alternateNames": [
      "Карбонат кальцію",
      "Wikidata: Q23767"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Карбонат кальцію.",
      "en": "Colors: Calcium carbonate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E170I (Карбонат кальцію).",
      "en": "CALCIUM CARBONATE is a chemical compound with the formula CaCO3."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E914",
    "name": "Oxidised polyethylene wax",
    "alternateNames": [
      "Окислений поліетиленовий віск",
      "Wikidata: Q18608846"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Окислений поліетиленовий віск.",
      "en": "Other: Oxidised polyethylene wax."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E914 (Окислений поліетиленовий віск).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E914 (Oxidised polyethylene wax)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161H",
    "name": "Zeaxanthin",
    "alternateNames": [
      "Зеаксантин"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Зеаксантин.",
      "en": "Colors: Zeaxanthin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E161H (Зеаксантин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E161H (Zeaxanthin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E641",
    "name": "Leucine",
    "alternateNames": [
      "Лейцин",
      "Wikidata: Q483745"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Лейцин.",
      "en": "Other: Leucine."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E641 (Лейцин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E641 (Leucine)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E142",
    "name": "Green s",
    "alternateNames": [
      "Зелений S",
      "Wikidata: Q420143"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Зелений S.",
      "en": "Colors: Green s."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E142 (Зелений S).",
      "en": "GREEN S is a green synthetic coal tar triarylmethane dye with the molecular formula C27H25N2O7S2Na. It can be used in mint sauce, desserts, gravy granules, sweets, ice creams, and tinned peas."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E466",
    "name": "Sodium carboxy methyl cellulose",
    "alternateNames": [
      "Карбоксиметилцелюлоза",
      "Wikidata: Q411030"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Карбоксиметилцелюлоза.",
      "en": "Thickeners & Emulsifiers: Sodium carboxy methyl cellulose."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E466 (Карбоксиметилцелюлоза).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E466 (Sodium carboxy methyl cellulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E102",
    "name": "Tartrazine",
    "alternateNames": [
      "Тартразин",
      "Wikidata: Q407158"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Тартразин.",
      "en": "Colors: Tartrazine."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E102 (Тартразин).",
      "en": "TARTRAZINE is a synthetic lemon yellow azo dye primarily used as a food coloring."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E546",
    "name": "Magnesium pyrophosphate",
    "alternateNames": [
      "Пірофосфат магнію"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Пірофосфат магнію.",
      "en": "Other: Magnesium pyrophosphate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E546 (Пірофосфат магнію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E546 (Magnesium pyrophosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E490",
    "name": "Propylene glycol",
    "alternateNames": [
      "Пропіленгліколь",
      "Wikidata: Q161495"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Пропіленгліколь.",
      "en": "Thickeners & Emulsifiers: Propylene glycol."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E490 (Пропіленгліколь).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E490 (Propylene glycol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E297",
    "name": "Fumaric acid",
    "alternateNames": [
      "Wikidata: Q139857"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Fumaric acid.",
      "en": "Preservatives: Fumaric acid."
    },
    "description": {
      "ua": "Харчова добавка E297 (Fumaric acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E297 (Fumaric acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161C",
    "name": "Cryptoaxanthin",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Cryptoaxanthin.",
      "en": "Colors: Cryptoaxanthin."
    },
    "description": {
      "ua": "Харчова добавка E161C (Cryptoaxanthin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E161C (Cryptoaxanthin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E440A",
    "name": "Pectin",
    "alternateNames": [
      "Wikidata: Q188154"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Pectin.",
      "en": "Thickeners & Emulsifiers: Pectin."
    },
    "description": {
      "ua": "Харчова добавка E440A (Pectin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E440A (Pectin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E573",
    "name": "Aluminium stearate",
    "alternateNames": [
      "Стеарат алюмінію"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Стеарат алюмінію.",
      "en": "Other: Aluminium stearate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E573 (Стеарат алюмінію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E573 (Aluminium stearate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E128",
    "name": "Red 2G",
    "alternateNames": [
      "Wikidata: Q417819"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "banned",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Red 2G.",
      "en": "Colors: Red 2G."
    },
    "description": {
      "ua": "Харчова добавка E128 (Red 2G), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E128 (Red 2G)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E523",
    "name": "Aluminium ammonium sulphate",
    "alternateNames": [
      "Wikidata: Q419370"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Aluminium ammonium sulphate.",
      "en": "Thickeners & Emulsifiers: Aluminium ammonium sulphate."
    },
    "description": {
      "ua": "Харчова добавка E523 (Aluminium ammonium sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E523 (Aluminium ammonium sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1402",
    "name": "Alkaline modified starch",
    "alternateNames": [
      "Лужно-модифікований крохмаль",
      "Wikidata: Q18636175"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Лужно-модифікований крохмаль.",
      "en": "Thickeners & Emulsifiers: Alkaline modified starch."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E1402 (Лужно-модифікований крохмаль).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1402 (Alkaline modified starch)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E481",
    "name": "Sodium stearoyl-2-lactylate",
    "alternateNames": [
      "Wikidata: Q416058"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium stearoyl-2-lactylate.",
      "en": "Thickeners & Emulsifiers: Sodium stearoyl-2-lactylate."
    },
    "description": {
      "ua": "Харчова добавка E481 (Sodium stearoyl-2-lactylate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E481 (Sodium stearoyl-2-lactylate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1422",
    "name": "Acetylated distarch adipate",
    "alternateNames": [
      "Wikidata: Q2824457"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Acetylated distarch adipate.",
      "en": "Thickeners & Emulsifiers: Acetylated distarch adipate."
    },
    "description": {
      "ua": "Харчова добавка E1422 (Acetylated distarch adipate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1422 (Acetylated distarch adipate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E503I",
    "name": "Ammonium carbonate",
    "alternateNames": [
      "Wikidata: Q204873"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ammonium carbonate.",
      "en": "Other: Ammonium carbonate."
    },
    "description": {
      "ua": "Харчова добавка E503I (Ammonium carbonate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "AMMONIUM CARBONATE is a salt with the chemical formula -NH4-2CO3. Since it readily degrades to gaseous ammonia and carbon dioxide upon heating, it is used as a leavening agent. It is also known as baker's ammonia and was a predecessor to the more modern leavening agents baking soda and baking powder."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E403",
    "name": "Ammonium alginate",
    "alternateNames": [
      "Wikidata: Q16038948"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Ammonium alginate.",
      "en": "Thickeners & Emulsifiers: Ammonium alginate."
    },
    "description": {
      "ua": "Харчова добавка E403 (Ammonium alginate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E403 (Ammonium alginate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E388",
    "name": "Thiodipropionic acid",
    "alternateNames": [
      "Wikidata: Q2823309"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Thiodipropionic acid.",
      "en": "Antioxidants: Thiodipropionic acid."
    },
    "description": {
      "ua": "Харчова добавка E388 (Thiodipropionic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E388 (Thiodipropionic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E443",
    "name": "Brominated vegetable oil",
    "alternateNames": [
      "Бромована рослинна олія",
      "Wikidata: Q4164502"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Бромована рослинна олія.",
      "en": "Thickeners & Emulsifiers: Brominated vegetable oil."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E443 (Бромована рослинна олія).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E443 (Brominated vegetable oil)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E435",
    "name": "Polyoxyethylene sorbitan monostearate",
    "alternateNames": [
      "Wikidata: Q2103104"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyoxyethylene sorbitan monostearate.",
      "en": "Thickeners & Emulsifiers: Polyoxyethylene sorbitan monostearate."
    },
    "description": {
      "ua": "Харчова добавка E435 (Polyoxyethylene sorbitan monostearate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E435 (Polyoxyethylene sorbitan monostearate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E213",
    "name": "Calcium benzoate",
    "alternateNames": [
      "Wikidata: Q414509"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Calcium benzoate.",
      "en": "Preservatives: Calcium benzoate."
    },
    "description": {
      "ua": "Харчова добавка E213 (Calcium benzoate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E213 (Calcium benzoate)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E492",
    "name": "Sorbitan tristearate",
    "alternateNames": [
      "Wikidata: Q1416224"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sorbitan tristearate.",
      "en": "Thickeners & Emulsifiers: Sorbitan tristearate."
    },
    "description": {
      "ua": "Харчова добавка E492 (Sorbitan tristearate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E492 (Sorbitan tristearate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E637",
    "name": "Ethyl maltol",
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Ethyl maltol.",
      "en": "Flavor Enhancers: Ethyl maltol."
    },
    "description": {
      "ua": "Харчова добавка E637 (Ethyl maltol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E637 (Ethyl maltol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E412",
    "name": "Guar gum",
    "alternateNames": [
      "Wikidata: Q422071"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Guar gum.",
      "en": "Thickeners & Emulsifiers: Guar gum."
    },
    "description": {
      "ua": "Харчова добавка E412 (Guar gum), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E412 (Guar gum)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E307C",
    "name": "DL-Alpha-tocopherol",
    "alternateNames": [
      "Wikidata: Q158348"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: DL-Alpha-tocopherol.",
      "en": "Antioxidants: DL-Alpha-tocopherol."
    },
    "description": {
      "ua": "Харчова добавка E307C (DL-Alpha-tocopherol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E307C (DL-Alpha-tocopherol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E960B",
    "name": "Rebaudioside from multiple gene donors expressed in Yarrowia lipolityca",
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Rebaudioside from multiple gene donors expressed in Yarrowia lipolityca.",
      "en": "Sweeteners: Rebaudioside from multiple gene donors expressed in Yarrowia lipolityca."
    },
    "description": {
      "ua": "Харчова добавка E960B (Rebaudioside from multiple gene donors expressed in Yarrowia lipolityca), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E960B (Rebaudioside from multiple gene donors expressed in Yarrowia lipolityca)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E577",
    "name": "Potassium gluconate",
    "alternateNames": [
      "Wikidata: Q1122870"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Potassium gluconate.",
      "en": "Other: Potassium gluconate."
    },
    "description": {
      "ua": "Харчова добавка E577 (Potassium gluconate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E577 (Potassium gluconate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E457",
    "name": "Alpha-Cyclodextrine",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Alpha-Cyclodextrine.",
      "en": "Thickeners & Emulsifiers: Alpha-Cyclodextrine."
    },
    "description": {
      "ua": "Харчова добавка E457 (Alpha-Cyclodextrine), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E457 (Alpha-Cyclodextrine)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E250",
    "name": "Sodium nitrite",
    "alternateNames": [
      "Нітрит натрію",
      "Wikidata: Q339975"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Нітрит натрію.",
      "en": "Preservatives: Sodium nitrite."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E250 (Нітрит натрію).",
      "en": "SODIUM NITRITE is an inorganic compound with the chemical formula NaNO2. it is a food additive used in processed meats and -in some countries- in fish products."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E579",
    "name": "Iron gluconate",
    "alternateNames": [
      "Wikidata: Q421291"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Iron gluconate.",
      "en": "Other: Iron gluconate."
    },
    "description": {
      "ua": "Харчова добавка E579 (Iron gluconate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "IRONII GLUCONATE, or ferrous gluconate,[1] is a black compound often used as an iron supplement. It is the iron-II- salt of gluconic acid."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E459",
    "name": "Beta-cyclodextrine",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Beta-cyclodextrine.",
      "en": "Thickeners & Emulsifiers: Beta-cyclodextrine."
    },
    "description": {
      "ua": "Харчова добавка E459 (Beta-cyclodextrine), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E459 (Beta-cyclodextrine)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1200",
    "name": "Polydextrose",
    "alternateNames": [
      "Wikidata: Q424616"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polydextrose.",
      "en": "Thickeners & Emulsifiers: Polydextrose."
    },
    "description": {
      "ua": "Харчова добавка E1200 (Polydextrose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1200 (Polydextrose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E262I",
    "name": "Sodium acetate",
    "alternateNames": [
      "Ацетат натрію",
      "Wikidata: Q339940"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Ацетат натрію.",
      "en": "Preservatives: Sodium acetate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E262I (Ацетат натрію).",
      "en": "SODIUM ACETATE, also abbreviated NaOAc,[8] is the sodium salt of acetic acid."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E470AIII",
    "name": "Calcium salts of fatty acids",
    "alternateNames": [
      "Кальцієві солі жирних кислот"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Кальцієві солі жирних кислот.",
      "en": "Thickeners & Emulsifiers: Calcium salts of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E470AIII (Кальцієві солі жирних кислот).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E470AIII (Calcium salts of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E202",
    "name": "Potassium sorbate",
    "alternateNames": [
      "Сорбат калію",
      "Wikidata: Q410744"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Сорбат калію.",
      "en": "Preservatives: Potassium sorbate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E202 (Сорбат калію).",
      "en": "POTASSIUM SORBATE is the potassium salt of sorbic acid, chemical formula CH3CH=CH−CH=CH−CO2K."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E242",
    "name": "Dimethyl dicarbonate",
    "alternateNames": [
      "Wikidata: Q416985"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Dimethyl dicarbonate.",
      "en": "Preservatives: Dimethyl dicarbonate."
    },
    "description": {
      "ua": "Харчова добавка E242 (Dimethyl dicarbonate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "DIMETHYL DICARBONATE is an organic compound which is a colorless liquid with a pungent odor at high concentration at room temperature. It is primarily used as a beverage preservative, processing aid, or sterilant"
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E529",
    "name": "Calcium oxide",
    "alternateNames": [
      "Оксид кальцію",
      "Wikidata: Q185006"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Оксид кальцію.",
      "en": "Other: Calcium oxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E529 (Оксид кальцію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E529 (Calcium oxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E527",
    "name": "Ammonium hydroxide",
    "alternateNames": [
      "Wikidata: Q421888"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ammonium hydroxide.",
      "en": "Other: Ammonium hydroxide."
    },
    "description": {
      "ua": "Харчова добавка E527 (Ammonium hydroxide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E527 (Ammonium hydroxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1207",
    "name": "Anionic methacrylate copolymer",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Anionic methacrylate copolymer.",
      "en": "Other: Anionic methacrylate copolymer."
    },
    "description": {
      "ua": "Харчова добавка E1207 (Anionic methacrylate copolymer), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1207 (Anionic methacrylate copolymer)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E332I",
    "name": "Monopotassium citrate",
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Monopotassium citrate.",
      "en": "Antioxidants: Monopotassium citrate."
    },
    "description": {
      "ua": "Харчова добавка E332I (Monopotassium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E332I (Monopotassium citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E409",
    "name": "Arabinogalactan",
    "alternateNames": [
      "Wikidata: Q4783458"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Arabinogalactan.",
      "en": "Thickeners & Emulsifiers: Arabinogalactan."
    },
    "description": {
      "ua": "Харчова добавка E409 (Arabinogalactan), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E409 (Arabinogalactan)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E200",
    "name": "Sorbic acid",
    "alternateNames": [
      "Сорбінова кислота",
      "Wikidata: Q407131"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Сорбінова кислота.",
      "en": "Preservatives: Sorbic acid."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E200 (Сорбінова кислота).",
      "en": "SORBIC ACID, or 2‚4-hexadienoic acid, is a natural organic compound used as a food preservative. It has the chemical formula CH3-CH-4CO2H."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E407",
    "name": "Carrageenan",
    "alternateNames": [
      "Каррагінан",
      "Wikidata: Q421991"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Каррагінан.",
      "en": "Thickeners & Emulsifiers: Carrageenan."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E407 (Каррагінан).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E407 (Carrageenan)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E240",
    "name": "Formaldehyde",
    "alternateNames": [
      "Wikidata: Q161210"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Formaldehyde.",
      "en": "Preservatives: Formaldehyde."
    },
    "description": {
      "ua": "Харчова добавка E240 (Formaldehyde), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E240 (Formaldehyde)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1414",
    "name": "Acetylated distarch phosphate",
    "alternateNames": [
      "Wikidata: Q18967180"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Acetylated distarch phosphate.",
      "en": "Thickeners & Emulsifiers: Acetylated distarch phosphate."
    },
    "description": {
      "ua": "Харчова добавка E1414 (Acetylated distarch phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1414 (Acetylated distarch phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E252",
    "name": "Potassium nitrate",
    "alternateNames": [
      "Нітрат калію",
      "Wikidata: Q177836"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Нітрат калію.",
      "en": "Preservatives: Potassium nitrate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E252 (Нітрат калію).",
      "en": "POTASSIUM NITRATE is a chemical compound with the chemical formula KNO3. The widespread adoption of nitrate use is more recent and is linked to the development of large-scale meat processing. potassium nitrate is still used in some food applications, such as salami, dry-cured ham, charcuterie."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E470AI",
    "name": "Sodium salts of fatty acids",
    "alternateNames": [
      "Натрієві солі жирних кислот"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Натрієві солі жирних кислот.",
      "en": "Thickeners & Emulsifiers: Sodium salts of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E470AI (Натрієві солі жирних кислот).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E470AI (Sodium salts of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E219",
    "name": "Sodium methyl p-hydroxybenzoate",
    "alternateNames": [
      "Wikidata: Q3963920"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium methyl p-hydroxybenzoate.",
      "en": "Preservatives: Sodium methyl p-hydroxybenzoate."
    },
    "description": {
      "ua": "Харчова добавка E219 (Sodium methyl p-hydroxybenzoate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E219 (Sodium methyl p-hydroxybenzoate)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E633",
    "name": "Calcium inosinate",
    "alternateNames": [
      "Wikidata: Q725037"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Calcium inosinate.",
      "en": "Flavor Enhancers: Calcium inosinate."
    },
    "description": {
      "ua": "Харчова добавка E633 (Calcium inosinate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E633 (Calcium inosinate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E410",
    "name": "Locust bean gum",
    "alternateNames": [
      "Камедь рожкового дерева",
      "Wikidata: Q221391"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Камедь рожкового дерева.",
      "en": "Thickeners & Emulsifiers: Locust bean gum."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E410 (Камедь рожкового дерева).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E410 (Locust bean gum)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E217",
    "name": "Sodium propyl para-hydroxybenzoate",
    "alternateNames": [
      "Wikidata: Q18967279"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium propyl para-hydroxybenzoate.",
      "en": "Preservatives: Sodium propyl para-hydroxybenzoate."
    },
    "description": {
      "ua": "Харчова добавка E217 (Sodium propyl para-hydroxybenzoate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E217 (Sodium propyl para-hydroxybenzoate)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E212",
    "name": "Potassium benzoate",
    "alternateNames": [
      "Wikidata: Q413611"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Potassium benzoate.",
      "en": "Preservatives: Potassium benzoate."
    },
    "description": {
      "ua": "Харчова добавка E212 (Potassium benzoate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E212 (Potassium benzoate)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E493",
    "name": "Sorbitan monolaurate",
    "alternateNames": [
      "Wikidata: Q15427856"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sorbitan monolaurate.",
      "en": "Thickeners & Emulsifiers: Sorbitan monolaurate."
    },
    "description": {
      "ua": "Харчова добавка E493 (Sorbitan monolaurate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E493 (Sorbitan monolaurate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E450",
    "name": "Diphosphates",
    "alternateNames": [
      "Дифосфати",
      "Wikidata: Q55506489"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Дифосфати.",
      "en": "Thickeners & Emulsifiers: Diphosphates."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E450 (Дифосфати).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E450 (Diphosphates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E570",
    "name": "Fatty acids",
    "alternateNames": [
      "Wikidata: Q61476"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Fatty acids.",
      "en": "Other: Fatty acids."
    },
    "description": {
      "ua": "Харчова добавка E570 (Fatty acids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E570 (Fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E339I",
    "name": "Monosodium phosphate",
    "alternateNames": [
      "Wikidata: Q415877"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Monosodium phosphate.",
      "en": "Preservatives: Monosodium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E339I (Monosodium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "MONOSODIUM PHOSPHATE, also known as monobasic sodium phosphate and sodium dihydrogen phosphate, is an inorganic compound of sodium with a dihydrogen phosphate -H2PO4−- anion."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E402",
    "name": "Potassium alginate",
    "alternateNames": [
      "Wikidata: Q16038950"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Potassium alginate.",
      "en": "Thickeners & Emulsifiers: Potassium alginate."
    },
    "description": {
      "ua": "Харчова добавка E402 (Potassium alginate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E402 (Potassium alginate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E442",
    "name": "Ammonium phosphatides",
    "alternateNames": [
      "Wikidata: Q695975"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Ammonium phosphatides.",
      "en": "Thickeners & Emulsifiers: Ammonium phosphatides."
    },
    "description": {
      "ua": "Харчова добавка E442 (Ammonium phosphatides), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E442 (Ammonium phosphatides)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E925",
    "name": "Chlorine",
    "alternateNames": [
      "Wikidata: Q688"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "banned",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Chlorine.",
      "en": "Other: Chlorine."
    },
    "description": {
      "ua": "Харчова добавка E925 (Chlorine), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E925 (Chlorine)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E166",
    "name": "Sandalwood",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Sandalwood.",
      "en": "Colors: Sandalwood."
    },
    "description": {
      "ua": "Харчова добавка E166 (Sandalwood), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E166 (Sandalwood)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E420I",
    "name": "D-sorbitol",
    "alternateNames": [
      "Wikidata: Q245280"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: D-sorbitol.",
      "en": "Sweeteners: D-sorbitol."
    },
    "description": {
      "ua": "Харчова добавка E420I (D-sorbitol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E420I (D-sorbitol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E520",
    "name": "Aluminium sulphate",
    "alternateNames": [
      "Wikidata: Q421857"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Aluminium sulphate.",
      "en": "Other: Aluminium sulphate."
    },
    "description": {
      "ua": "Харчова добавка E520 (Aluminium sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E520 (Aluminium sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1411",
    "name": "Distarch glycerol",
    "alternateNames": [
      "Wikidata: Q18967214"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Distarch glycerol.",
      "en": "Thickeners & Emulsifiers: Distarch glycerol."
    },
    "description": {
      "ua": "Харчова добавка E1411 (Distarch glycerol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1411 (Distarch glycerol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E522",
    "name": "Aluminium potassium sulphate",
    "alternateNames": [
      "Wikidata: Q411309"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Aluminium potassium sulphate.",
      "en": "Other: Aluminium potassium sulphate."
    },
    "description": {
      "ua": "Харчова добавка E522 (Aluminium potassium sulphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E522 (Aluminium potassium sulphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E500I",
    "name": "Sodium carbonate",
    "alternateNames": [
      "Карбонат натрію",
      "Wikidata: Q190227"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Карбонат натрію.",
      "en": "Thickeners & Emulsifiers: Sodium carbonate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E500I (Карбонат натрію).",
      "en": "SODIUM CARBONATE, -also known as washing soda, soda ash and soda crystals, and in the monohydrate form as crystal carbonate- is the water-soluble sodium salt of carbonic acid."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E460I",
    "name": "Microcrystalline cellulose",
    "alternateNames": [
      "Wikidata: Q80294"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Microcrystalline cellulose.",
      "en": "Thickeners & Emulsifiers: Microcrystalline cellulose."
    },
    "description": {
      "ua": "Харчова добавка E460I (Microcrystalline cellulose), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E460I (Microcrystalline cellulose)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E560",
    "name": "Potassium silicate",
    "alternateNames": [
      "Силікат калію",
      "Wikidata: Q419390"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Силікат калію.",
      "en": "Other: Potassium silicate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E560 (Силікат калію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E560 (Potassium silicate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E400",
    "name": "Alginic acid",
    "alternateNames": [
      "Wikidata: Q422092"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Alginic acid.",
      "en": "Thickeners & Emulsifiers: Alginic acid."
    },
    "description": {
      "ua": "Харчова добавка E400 (Alginic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E400 (Alginic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E209",
    "name": "Heptylparaben",
    "alternateNames": [
      "Wikidata: Q5732121"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Heptylparaben.",
      "en": "Preservatives: Heptylparaben."
    },
    "description": {
      "ua": "Харчова добавка E209 (Heptylparaben), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E209 (Heptylparaben)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E440",
    "name": "Pectins",
    "alternateNames": [
      "Pektin",
      "Wikidata: Q188154"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Pektin.",
      "en": "Thickeners & Emulsifiers: Pectins."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E440 (Pektin).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E440 (Pectins)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E249",
    "name": "Potassium nitrite",
    "alternateNames": [
      "Wikidata: Q409179"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Potassium nitrite.",
      "en": "Preservatives: Potassium nitrite."
    },
    "description": {
      "ua": "Харчова добавка E249 (Potassium nitrite), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E249 (Potassium nitrite)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E927B",
    "name": "Carbamide",
    "alternateNames": [
      "Сечовина",
      "Wikidata: Q48318"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Сечовина.",
      "en": "Other: Carbamide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E927B (Сечовина).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E927B (Carbamide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E452",
    "name": "Polyphosphates",
    "alternateNames": [
      "Поліфосфати",
      "Wikidata: Q11867365"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Поліфосфати.",
      "en": "Thickeners & Emulsifiers: Polyphosphates."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E452 (Поліфосфати).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E452 (Polyphosphates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E572",
    "name": "Magnesium stearate",
    "alternateNames": [
      "Wikidata: Q416713"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Magnesium stearate.",
      "en": "Other: Magnesium stearate."
    },
    "description": {
      "ua": "Харчова добавка E572 (Magnesium stearate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E572 (Magnesium stearate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E210",
    "name": "Benzoic acid",
    "alternateNames": [
      "Wikidata: Q191700"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Benzoic acid.",
      "en": "Preservatives: Benzoic acid."
    },
    "description": {
      "ua": "Харчова добавка E210 (Benzoic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E210 (Benzoic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E419",
    "name": "Gum ghatti",
    "alternateNames": [
      "Камедь гатті",
      "Wikidata: Q18967232"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Камедь гатті.",
      "en": "Thickeners & Emulsifiers: Gum ghatti."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E419 (Камедь гатті).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E419 (Gum ghatti)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E417",
    "name": "Tara gum",
    "alternateNames": [
      "Wikidata: Q15089452"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Tara gum.",
      "en": "Thickeners & Emulsifiers: Tara gum."
    },
    "description": {
      "ua": "Харчова добавка E417 (Tara gum), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E417 (Tara gum)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E632",
    "name": "Dipotassium inosinate",
    "alternateNames": [
      "Wikidata: Q18608832"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Dipotassium inosinate.",
      "en": "Flavor Enhancers: Dipotassium inosinate."
    },
    "description": {
      "ua": "Харчова добавка E632 (Dipotassium inosinate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E632 (Dipotassium inosinate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E290",
    "name": "Carbon dioxide",
    "alternateNames": [
      "Діоксид вуглецю",
      "Wikidata: Q1997"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Діоксид вуглецю.",
      "en": "Preservatives: Carbon dioxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E290 (Діоксид вуглецю).",
      "en": "CARBON DIOXIDE chemical formula CO2- is a colorless gas with a density about 60% higher than that of dry air."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E365",
    "name": "Sodium fumarate",
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Sodium fumarate.",
      "en": "Antioxidants: Sodium fumarate."
    },
    "description": {
      "ua": "Харчова добавка E365 (Sodium fumarate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E365 (Sodium fumarate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E407A",
    "name": "Processed eucheuma seaweed",
    "alternateNames": [
      "Wikidata: Q421991"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Processed eucheuma seaweed.",
      "en": "Thickeners & Emulsifiers: Processed eucheuma seaweed."
    },
    "description": {
      "ua": "Харчова добавка E407A (Processed eucheuma seaweed), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E407A (Processed eucheuma seaweed)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1102",
    "name": "Glucose oxidase",
    "alternateNames": [
      "Wikidata: Q419321"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Glucose oxidase.",
      "en": "Antioxidants: Glucose oxidase."
    },
    "description": {
      "ua": "Харчова добавка E1102 (Glucose oxidase), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1102 (Glucose oxidase)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E333I",
    "name": "Monocalcium citrate",
    "alternateNames": [
      "Wikidata: Q420280"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Monocalcium citrate.",
      "en": "Antioxidants: Monocalcium citrate."
    },
    "description": {
      "ua": "Харчова добавка E333I (Monocalcium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E333I (Monocalcium citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E450II",
    "name": "Trisodium diphosphate",
    "alternateNames": [
      "Wikidata: Q290828"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Trisodium diphosphate.",
      "en": "Thickeners & Emulsifiers: Trisodium diphosphate."
    },
    "description": {
      "ua": "Харчова добавка E450II (Trisodium diphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E450II (Trisodium diphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E131",
    "name": "Patent blue v",
    "alternateNames": [
      "Синій патентований V",
      "Wikidata: Q420087"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Синій патентований V.",
      "en": "Colors: Patent blue v."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E131 (Синій патентований V).",
      "en": "PATENT BLUE V is a dark bluish synthetic triphenylmethane dye used as a food coloring. It is not widely used, but in Europe it can be found in Scotch eggs, certain jelly sweets, blue Curaçao, certain jello varieties -though not in actual Jell-O brand products-, among others."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E281",
    "name": "Sodium propionate",
    "alternateNames": [
      "Wikidata: Q420130"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Sodium propionate.",
      "en": "Preservatives: Sodium propionate."
    },
    "description": {
      "ua": "Харчова добавка E281 (Sodium propionate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Sodium propanoate or SODIUM PROPIONATE is the sodium salt of propionic acid."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E203",
    "name": "Calcium sorbate",
    "alternateNames": [
      "Wikidata: Q425183"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Calcium sorbate.",
      "en": "Preservatives: Calcium sorbate."
    },
    "description": {
      "ua": "Харчова добавка E203 (Calcium sorbate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "CALCIUM SORBATE  is the calcium salt of sorbic acid. Calcium sorbate is a polyunsaturated fatty acid salt."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E452II",
    "name": "Potassium polyphosphate",
    "alternateNames": [
      "Wikidata: Q42807555"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Potassium polyphosphate.",
      "en": "Thickeners & Emulsifiers: Potassium polyphosphate."
    },
    "description": {
      "ua": "Харчова добавка E452II (Potassium polyphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E452II (Potassium polyphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E243",
    "name": "Ethyl lauroyl arginate",
    "alternateNames": [
      "Wikidata: Q18608852"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Ethyl lauroyl arginate.",
      "en": "Preservatives: Ethyl lauroyl arginate."
    },
    "description": {
      "ua": "Харчова добавка E243 (Ethyl lauroyl arginate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E243 (Ethyl lauroyl arginate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E375",
    "name": "Nicotinic acid",
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Nicotinic acid.",
      "en": "Antioxidants: Nicotinic acid."
    },
    "description": {
      "ua": "Харчова добавка E375 (Nicotinic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E375 (Nicotinic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E440II",
    "name": "Amidated pectin",
    "alternateNames": [
      "Wikidata: Q42807519"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Amidated pectin.",
      "en": "Thickeners & Emulsifiers: Amidated pectin."
    },
    "description": {
      "ua": "Харчова добавка E440II (Amidated pectin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E440II (Amidated pectin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E235",
    "name": "Natamycin",
    "alternateNames": [
      "Натаміцин",
      "Wikidata: Q248466"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Натаміцин.",
      "en": "Preservatives: Natamycin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E235 (Натаміцин).",
      "en": "NATAMYCIN is used in the food industry as a preservative."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E553AI",
    "name": "Synthetic magnesium silicate",
    "alternateNames": [
      "Синтетичний силікат магнію"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Синтетичний силікат магнію.",
      "en": "Thickeners & Emulsifiers: Synthetic magnesium silicate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E553AI (Синтетичний силікат магнію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E553AI (Synthetic magnesium silicate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E331III",
    "name": "Trisodium citrate",
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Trisodium citrate.",
      "en": "Antioxidants: Trisodium citrate."
    },
    "description": {
      "ua": "Харчова добавка E331III (Trisodium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E331III (Trisodium citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E630",
    "name": "Inosinic acid",
    "alternateNames": [
      "Wikidata: Q255933"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Inosinic acid.",
      "en": "Flavor Enhancers: Inosinic acid."
    },
    "description": {
      "ua": "Харчова добавка E630 (Inosinic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E630 (Inosinic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E413",
    "name": "Tragacanth",
    "alternateNames": [
      "Трагакант",
      "Wikidata: Q423901"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Трагакант.",
      "en": "Thickeners & Emulsifiers: Tragacanth."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E413 (Трагакант).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E413 (Tragacanth)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E325",
    "name": "Sodium lactate",
    "alternateNames": [
      "Wikidata: Q418235"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Sodium lactate.",
      "en": "Antioxidants: Sodium lactate."
    },
    "description": {
      "ua": "Харчова добавка E325 (Sodium lactate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "SODIUM LACTATE is the sodium salt of lactic acid, and has a mild saline taste. It is produced by fermentation of a sugar source, such as corn or beets. It acts as a preservative, acidity regulator, and bulking agent."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E965II",
    "name": "Maltitol syrup",
    "alternateNames": [
      "Мальтитоловий сироп",
      "Wikidata: Q423882"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Мальтитоловий сироп.",
      "en": "Sweeteners: Maltitol syrup."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E965II (Мальтитоловий сироп).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E965II (Maltitol syrup)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E170II",
    "name": "Calcium hydrogen carbonate",
    "alternateNames": [
      "Калійні солі",
      "Wikidata: Q415144"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Калійні солі.",
      "en": "Colors: Calcium hydrogen carbonate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E170II (Калійні солі).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E170II (Calcium hydrogen carbonate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E434",
    "name": "Polyoxyethylene sorbitan monopalmitate",
    "alternateNames": [
      "Wikidata: Q1413823"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Polyoxyethylene sorbitan monopalmitate.",
      "en": "Thickeners & Emulsifiers: Polyoxyethylene sorbitan monopalmitate."
    },
    "description": {
      "ua": "Харчова добавка E434 (Polyoxyethylene sorbitan monopalmitate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E434 (Polyoxyethylene sorbitan monopalmitate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E924A",
    "name": "Potassium bromate",
    "alternateNames": [
      "Бромат калію",
      "Wikidata: Q409241"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "banned",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Бромат калію.",
      "en": "Other: Potassium bromate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E924A (Бромат калію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E924A (Potassium bromate)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E636",
    "name": "Maltol",
    "alternateNames": [
      "Мальтол"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Мальтол.",
      "en": "Flavor Enhancers: Maltol."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E636 (Мальтол).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E636 (Maltol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E585",
    "name": "Ferrous lactate",
    "alternateNames": [
      "Wikidata: Q836793"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Ferrous lactate.",
      "en": "Other: Ferrous lactate."
    },
    "description": {
      "ua": "Харчова добавка E585 (Ferrous lactate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E585 (Ferrous lactate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E311",
    "name": "Octyl gallate",
    "alternateNames": [
      "Wikidata: Q2317929"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Octyl gallate.",
      "en": "Antioxidants: Octyl gallate."
    },
    "description": {
      "ua": "Харчова добавка E311 (Octyl gallate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E311 (Octyl gallate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E296",
    "name": "Malic acid",
    "alternateNames": [
      "Яблучна кислота",
      "Wikidata: Q190143"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Яблучна кислота.",
      "en": "Preservatives: Malic acid."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E296 (Яблучна кислота).",
      "en": "MALIC ACID is an organic compound with the molecular formula C4H6O5. Malic acid has two stereoisomeric forms -L- and D-enantiomers-, though only the L-isomer exists naturally."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E418",
    "name": "Gellan gum",
    "alternateNames": [
      "Wikidata: Q416694"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Gellan gum.",
      "en": "Thickeners & Emulsifiers: Gellan gum."
    },
    "description": {
      "ua": "Харчова добавка E418 (Gellan gum), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "GELLAN GUM is a water-soluble anionic polysaccharide produced by the bacterium Sphingomonas elodea. It is used in plant-based milks to keep plant protein suspended in the milk."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E172II",
    "name": "Red iron oxide",
    "alternateNames": [
      "Червоний оксид заліза",
      "Wikidata: Q3045754"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Червоний оксид заліза.",
      "en": "Colors: Red iron oxide."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E172II (Червоний оксид заліза).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E172II (Red iron oxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E120",
    "name": "Cochineal",
    "alternateNames": [
      "Кармін",
      "Wikidata: Q416860"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "caution",
    "origin": "animal",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": false,
      "halal": false,
      "kosher": false
    },
    "purpose": {
      "ua": "Барвники: Кармін.",
      "en": "Colors: Cochineal."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E120 (Кармін).",
      "en": "CARMINE, also called cochineal, cochineal extract, crimson lake or carmine lake, natural red 4, C.I. 75470, or E120, is a pigment of a bright-red color obtained from the aluminium salt of carminic acid"
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E446",
    "name": "Succistearin",
    "alternateNames": [
      "Сукцистеарин",
      "Wikidata: Q18601547"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Сукцистеарин.",
      "en": "Thickeners & Emulsifiers: Succistearin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E446 (Сукцистеарин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E446 (Succistearin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E911",
    "name": "Fatty acid methyl ester",
    "alternateNames": [
      "Wikidata: Q18944655"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Fatty acid methyl ester.",
      "en": "Other: Fatty acid methyl ester."
    },
    "description": {
      "ua": "Харчова добавка E911 (Fatty acid methyl ester), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E911 (Fatty acid methyl ester)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E452VI",
    "name": "Sodium tripolyphosphate",
    "alternateNames": [
      "Триполіфосфат натрію",
      "Wikidata: Q11867365"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Триполіфосфат натрію.",
      "en": "Thickeners & Emulsifiers: Sodium tripolyphosphate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E452VI (Триполіфосфат натрію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E452VI (Sodium tripolyphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E162",
    "name": "Beetroot red",
    "alternateNames": [
      "Wikidata: Q420138"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Beetroot red.",
      "en": "Colors: Beetroot red."
    },
    "description": {
      "ua": "Харчова добавка E162 (Beetroot red), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E162 (Beetroot red)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E406",
    "name": "Agar",
    "alternateNames": [
      "Агар-агар",
      "Wikidata: Q177998"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Агар-агар.",
      "en": "Thickeners & Emulsifiers: Agar."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E406 (Агар-агар).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E406 (Agar)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E905F",
    "name": "Mineral oil medium and low viscosity class II",
    "alternateNames": [
      "Wikidata: Q579764"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Mineral oil medium and low viscosity class II.",
      "en": "Other: Mineral oil medium and low viscosity class II."
    },
    "description": {
      "ua": "Харчова добавка E905F (Mineral oil medium and low viscosity class II), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E905F (Mineral oil medium and low viscosity class II)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E450VIII",
    "name": "E450viii",
    "alternateNames": [
      "Димагнієвий дифосфат",
      "Wikidata: Q290828"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Димагнієвий дифосфат.",
      "en": "Thickeners & Emulsifiers: E450viii."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E450VIII (Димагнієвий дифосфат).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E450VIII (E450viii)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E163A",
    "name": "Cyanidin",
    "alternateNames": [
      "Ціанідин",
      "Wikidata: Q417606"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Ціанідин.",
      "en": "Colors: Cyanidin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E163A (Ціанідин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E163A (Cyanidin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E170",
    "name": "Calcium carbonates",
    "alternateNames": [
      "Карбонати кальцію"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Карбонати кальцію.",
      "en": "Colors: Calcium carbonates."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E170 (Карбонати кальцію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E170 (Calcium carbonates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E216",
    "name": "Propyl para-hydroxybenzoate",
    "alternateNames": [
      "Пропіл пара-гідроксибензоат",
      "Wikidata: Q511627"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Пропіл пара-гідроксибензоат.",
      "en": "Preservatives: Propyl para-hydroxybenzoate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E216 (Пропіл пара-гідроксибензоат).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E216 (Propyl para-hydroxybenzoate)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E391",
    "name": "Phytic acid",
    "alternateNames": [
      "Wikidata: Q409679"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Phytic acid.",
      "en": "Antioxidants: Phytic acid."
    },
    "description": {
      "ua": "Харчова добавка E391 (Phytic acid), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E391 (Phytic acid)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E472F",
    "name": "Mixed acetic and tartaric acid esters of mono- and diglycerides of fatty acids",
    "alternateNames": [
      "Змішані естери гліцерину та винної",
      "Wikidata: Q18967248"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Змішані естери гліцерину та винної.",
      "en": "Thickeners & Emulsifiers: Mixed acetic and tartaric acid esters of mono- and diglycerides of fatty acids."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E472F (Змішані естери гліцерину та винної).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E472F (Mixed acetic and tartaric acid esters of mono- and diglycerides of fatty acids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E161E",
    "name": "Violaxanthin",
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Violaxanthin.",
      "en": "Colors: Violaxanthin."
    },
    "description": {
      "ua": "Харчова добавка E161E (Violaxanthin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E161E (Violaxanthin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E576",
    "name": "Sodium gluconate",
    "alternateNames": [
      "Wikidata: Q264552"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sodium gluconate.",
      "en": "Thickeners & Emulsifiers: Sodium gluconate."
    },
    "description": {
      "ua": "Харчова добавка E576 (Sodium gluconate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E576 (Sodium gluconate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E172",
    "name": "Iron oxides and iron hydroxides",
    "alternateNames": [
      "Wikidata: Q3045754"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Iron oxides and iron hydroxides.",
      "en": "Colors: Iron oxides and iron hydroxides."
    },
    "description": {
      "ua": "Харчова добавка E172 (Iron oxides and iron hydroxides), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E172 (Iron oxides and iron hydroxides)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E621",
    "name": "Monosodium glutamate",
    "alternateNames": [
      "Глутамат натрію",
      "Wikidata: Q179678"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Глутамат натрію.",
      "en": "Flavor Enhancers: Monosodium glutamate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E621 (Глутамат натрію).",
      "en": "MONOSODIUM GLUTAMATE -MSG, also known as sodium glutamate- is the sodium salt of glutamic acid, one of the most abundant naturally occurring non-essential amino acids."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1206",
    "name": "Neutral methacrylate copolymer",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Neutral methacrylate copolymer.",
      "en": "Other: Neutral methacrylate copolymer."
    },
    "description": {
      "ua": "Харчова добавка E1206 (Neutral methacrylate copolymer), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1206 (Neutral methacrylate copolymer)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160",
    "name": "Carotenoids",
    "alternateNames": [
      "Wikidata: Q191907"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Carotenoids.",
      "en": "Colors: Carotenoids."
    },
    "description": {
      "ua": "Харчова добавка E160 (Carotenoids), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160 (Carotenoids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E450VI",
    "name": "Dicalcium diphosphate",
    "alternateNames": [
      "Wikidata: Q4363460"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Dicalcium diphosphate.",
      "en": "Thickeners & Emulsifiers: Dicalcium diphosphate."
    },
    "description": {
      "ua": "Харчова добавка E450VI (Dicalcium diphosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E450VI (Dicalcium diphosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E163D",
    "name": "Pelargonidin",
    "alternateNames": [
      "Пеларгонідин",
      "Wikidata: Q2522451"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Пеларгонідин.",
      "en": "Colors: Pelargonidin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E163D (Пеларгонідин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E163D (Pelargonidin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E140I",
    "name": "Chlorophylls",
    "alternateNames": [
      "Хлорофіл",
      "Wikidata: Q82182"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Хлорофіл.",
      "en": "Colors: Chlorophylls."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E140I (Хлорофіл).",
      "en": "CHLOROPHYLL is any of several related green pigments found in cyanobacteria and the chloroplasts of algae and plants. Chefs use chlorophyll to color a variety of foods and beverages green, such as pasta and spirits."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E122",
    "name": "Azorubine",
    "alternateNames": [
      "Wikidata: Q409676"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Azorubine.",
      "en": "Colors: Azorubine."
    },
    "description": {
      "ua": "Харчова добавка E122 (Azorubine), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E122 (Azorubine)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E380",
    "name": "Triammonium citrate",
    "alternateNames": [
      "Wikidata: Q13081798"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Triammonium citrate.",
      "en": "Antioxidants: Triammonium citrate."
    },
    "description": {
      "ua": "Харчова добавка E380 (Triammonium citrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E380 (Triammonium citrate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E526",
    "name": "Calcium hydroxide",
    "alternateNames": [
      "Wikidata: Q182849"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Calcium hydroxide.",
      "en": "Other: Calcium hydroxide."
    },
    "description": {
      "ua": "Харчова добавка E526 (Calcium hydroxide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E526 (Calcium hydroxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E129",
    "name": "Allura red",
    "alternateNames": [
      "Wikidata: Q419895"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Allura red.",
      "en": "Colors: Allura red."
    },
    "description": {
      "ua": "Харчова добавка E129 (Allura red), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E129 (Allura red)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E964",
    "name": "Polyglycitol syrup",
    "alternateNames": [
      "Сироп полігліцитолу",
      "Wikidata: Q18601425"
    ],
    "category": "sweeteners",
    "categoryLabel": {
      "ua": "Підсолоджувачі",
      "en": "Sweeteners"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсолоджувачі: Сироп полігліцитолу.",
      "en": "Sweeteners: Polyglycitol syrup."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E964 (Сироп полігліцитолу).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E964 (Polyglycitol syrup)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E127",
    "name": "Erythrosine",
    "alternateNames": [
      "Еритрозин",
      "Wikidata: Q420101"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Еритрозин.",
      "en": "Colors: Erythrosine."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E127 (Еритрозин).",
      "en": "ERYTHROSINE, is an organoiodine compound, specifically a derivative of fluorone. It is cherry or melon-pink synthetic, primarily used for food coloring."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E246",
    "name": "Glycolipids",
    "alternateNames": [
      "Гліколіпіди"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Гліколіпіди.",
      "en": "Preservatives: Glycolipids."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E246 (Гліколіпіди).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E246 (Glycolipids)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E901",
    "name": "White and yellow beeswax",
    "alternateNames": [
      "Бджолиний віск",
      "Wikidata: Q143739"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": false,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Бджолиний віск.",
      "en": "Thickeners & Emulsifiers: White and yellow beeswax."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E901 (Бджолиний віск).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E901 (White and yellow beeswax)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E416",
    "name": "Karaya-gum",
    "alternateNames": [
      "Wikidata: Q421167"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Karaya-gum.",
      "en": "Thickeners & Emulsifiers: Karaya-gum."
    },
    "description": {
      "ua": "Харчова добавка E416 (Karaya-gum), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E416 (Karaya-gum)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E941",
    "name": "Nitrogen",
    "alternateNames": [
      "Азот",
      "Wikidata: Q627"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Азот.",
      "en": "Other: Nitrogen."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E941 (Азот).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E941 (Nitrogen)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E341II",
    "name": "Dicalcium phosphate",
    "alternateNames": [
      "Wikidata: Q414619"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Dicalcium phosphate.",
      "en": "Antioxidants: Dicalcium phosphate."
    },
    "description": {
      "ua": "Харчова добавка E341II (Dicalcium phosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E341II (Dicalcium phosphate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E638",
    "name": "E638",
    "alternateNames": [
      "Аспартам натрію"
    ],
    "category": "flavor_enhancers",
    "categoryLabel": {
      "ua": "Підсилювачі смаку",
      "en": "Flavor Enhancers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Підсилювачі смаку: Аспартам натрію.",
      "en": "Flavor Enhancers: E638."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E638 (Аспартам натрію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E638 (E638)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E924",
    "name": "Potassium bromate",
    "alternateNames": [
      "Бромат калію",
      "Wikidata: Q409241"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Бромат калію.",
      "en": "Other: Potassium bromate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E924 (Бромат калію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E924 (Potassium bromate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E336I",
    "name": "Monopotassium tartrate",
    "alternateNames": [
      "Wikidata: Q18745"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Monopotassium tartrate.",
      "en": "Antioxidants: Monopotassium tartrate."
    },
    "description": {
      "ua": "Харчова добавка E336I (Monopotassium tartrate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "POTASSIUM BITARTRATE, also known as potassium hydrogen tartrate, with formula KC4H5O6, is a byproduct of winemaking."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1519",
    "name": "Benzyl alcohol",
    "alternateNames": [
      "Wikidata: Q52353"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Benzyl alcohol.",
      "en": "Other: Benzyl alcohol."
    },
    "description": {
      "ua": "Харчова добавка E1519 (Benzyl alcohol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1519 (Benzyl alcohol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E123",
    "name": "Amaranth",
    "alternateNames": [
      "Wikidata: Q421074"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "banned",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Amaranth.",
      "en": "Colors: Amaranth."
    },
    "description": {
      "ua": "Харчова добавка E123 (Amaranth), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E123 (Amaranth)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E528",
    "name": "Magnesium hydroxide",
    "alternateNames": [
      "Wikidata: Q407548"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Magnesium hydroxide.",
      "en": "Other: Magnesium hydroxide."
    },
    "description": {
      "ua": "Харчова добавка E528 (Magnesium hydroxide), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E528 (Magnesium hydroxide)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E351",
    "name": "Potassium malate",
    "alternateNames": [
      "Wikidata: Q408755"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Potassium malate.",
      "en": "Antioxidants: Potassium malate."
    },
    "description": {
      "ua": "Харчова добавка E351 (Potassium malate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E351 (Potassium malate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E173",
    "name": "Aluminium",
    "alternateNames": [
      "Wikidata: Q663"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Aluminium.",
      "en": "Colors: Aluminium."
    },
    "description": {
      "ua": "Харчова добавка E173 (Aluminium), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E173 (Aluminium)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160A",
    "name": "Carotene",
    "alternateNames": [
      "Wikidata: Q190156"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Carotene.",
      "en": "Colors: Carotene."
    },
    "description": {
      "ua": "Харчова добавка E160A (Carotene), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160A (Carotene)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E234",
    "name": "Nisin",
    "alternateNames": [
      "Нізин",
      "Wikidata: Q415798"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Нізин.",
      "en": "Preservatives: Nisin."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E234 (Нізин).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E234 (Nisin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E578",
    "name": "Calcium gluconate",
    "alternateNames": [
      "Глюконат кальцію",
      "Wikidata: Q413739"
    ],
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: Глюконат кальцію.",
      "en": "Other: Calcium gluconate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E578 (Глюконат кальцію).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E578 (Calcium gluconate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E458",
    "name": "Gamma-Cyclodextrine",
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Gamma-Cyclodextrine.",
      "en": "Thickeners & Emulsifiers: Gamma-Cyclodextrine."
    },
    "description": {
      "ua": "Харчова добавка E458 (Gamma-Cyclodextrine), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E458 (Gamma-Cyclodextrine)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E324",
    "name": "Ethoxyquin",
    "alternateNames": [
      "Wikidata: Q1657816"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ethoxyquin.",
      "en": "Antioxidants: Ethoxyquin."
    },
    "description": {
      "ua": "Харчова добавка E324 (Ethoxyquin), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E324 (Ethoxyquin)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E1443",
    "name": "Hydroxy propyl distarch glycerol",
    "alternateNames": [
      "Wikidata: Q18967237"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Hydroxy propyl distarch glycerol.",
      "en": "Thickeners & Emulsifiers: Hydroxy propyl distarch glycerol."
    },
    "description": {
      "ua": "Харчова добавка E1443 (Hydroxy propyl distarch glycerol), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E1443 (Hydroxy propyl distarch glycerol)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E496",
    "name": "Sorbitan trioleate",
    "alternateNames": [
      "Wikidata: Q18601524"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Sorbitan trioleate.",
      "en": "Thickeners & Emulsifiers: Sorbitan trioleate."
    },
    "description": {
      "ua": "Харчова добавка E496 (Sorbitan trioleate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E496 (Sorbitan trioleate)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E304I",
    "name": "Ascorbyl palmitate",
    "alternateNames": [
      "Wikidata: Q424521"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Ascorbyl palmitate.",
      "en": "Antioxidants: Ascorbyl palmitate."
    },
    "description": {
      "ua": "Харчова добавка E304I (Ascorbyl palmitate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "ASCORBYL PALMITATE is an ester formed from ascorbic acid and palmitic acid creating a fat-soluble form of vitamin C."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E218",
    "name": "Methyl p-hydroxybenzoate",
    "alternateNames": [
      "Wikidata: Q229987"
    ],
    "category": "preservatives",
    "categoryLabel": {
      "ua": "Консерванти",
      "en": "Preservatives"
    },
    "risk": "avoid",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Консерванти: Methyl p-hydroxybenzoate.",
      "en": "Preservatives: Methyl p-hydroxybenzoate."
    },
    "description": {
      "ua": "Харчова добавка E218 (Methyl p-hydroxybenzoate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E218 (Methyl p-hydroxybenzoate)."
    },
    "sideEffects": {
      "ua": "Потенційно небажана добавка. Рекомендується уникати регулярного споживання.",
      "en": "Potentially controversial additive. Prudent reduction advised."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E15X",
    "name": "E15x food additive",
    "category": "other",
    "categoryLabel": {
      "ua": "Інші добавки",
      "en": "Other"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Інші добавки: E15x food additive.",
      "en": "Other: E15x food additive."
    },
    "description": {
      "ua": "Харчова добавка E15X (E15x food additive), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E15X (E15x food additive)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E341",
    "name": "Calcium phosphates",
    "alternateNames": [
      "Wikidata: Q426664"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "caution",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium phosphates.",
      "en": "Antioxidants: Calcium phosphates."
    },
    "description": {
      "ua": "Харчова добавка E341 (Calcium phosphates), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E341 (Calcium phosphates)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E163",
    "name": "Anthocyanins",
    "alternateNames": [
      "Антоціани",
      "Wikidata: Q262547"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Антоціани.",
      "en": "Colors: Anthocyanins."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E163 (Антоціани).",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E163 (Anthocyanins)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E160D",
    "name": "Lycopene",
    "alternateNames": [
      "Wikidata: Q208130"
    ],
    "category": "colors",
    "categoryLabel": {
      "ua": "Барвники",
      "en": "Colors"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Барвники: Lycopene.",
      "en": "Colors: Lycopene."
    },
    "description": {
      "ua": "Харчова добавка E160D (Lycopene), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E160D (Lycopene)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E301",
    "name": "Sodium ascorbate",
    "alternateNames": [
      "Аскорбат натрію",
      "Wikidata: Q424551"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Аскорбат натрію.",
      "en": "Antioxidants: Sodium ascorbate."
    },
    "description": {
      "ua": "Харчова добавка, зареєстрована в європейському реєстрі як E301 (Аскорбат натрію).",
      "en": "SODIUM ASCORBATE is one of a number of mineral salts of ascorbic acid -vitamin C-."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [
      "EFSA evaluated"
    ],
    "regulatoryStatus": {
      "ua": "Оцінено Європейським агентством з безпеки харчових продуктів (EFSA)",
      "en": "Evaluated by EFSA (European Food Safety Authority)"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E408",
    "name": "Bakers yeast glycan",
    "alternateNames": [
      "Wikidata: Q18935685"
    ],
    "category": "emulsifiers",
    "categoryLabel": {
      "ua": "Загусники та емульгатори",
      "en": "Thickeners & Emulsifiers"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Загусники та емульгатори: Bakers yeast glycan.",
      "en": "Thickeners & Emulsifiers: Bakers yeast glycan."
    },
    "description": {
      "ua": "Харчова добавка E408 (Bakers yeast glycan), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "Food additive registered under EFSA and Codex Alimentarius as E408 (Bakers yeast glycan)."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  },
  {
    "code": "E383",
    "name": "Calcium glycerophosphate",
    "alternateNames": [
      "Wikidata: Q4119838"
    ],
    "category": "antioxidants",
    "categoryLabel": {
      "ua": "Антиоксиданти",
      "en": "Antioxidants"
    },
    "risk": "safe",
    "origin": "plant",
    "dietaryFlags": {
      "vegan": true,
      "vegetarian": true,
      "halal": true,
      "kosher": true
    },
    "purpose": {
      "ua": "Антиоксиданти: Calcium glycerophosphate.",
      "en": "Antioxidants: Calcium glycerophosphate."
    },
    "description": {
      "ua": "Харчова добавка E383 (Calcium glycerophosphate), сертифікована в стандартах EFSA та Codex Alimentarius.",
      "en": "CALCIUM GLYCERYLPHOSPHATE -or calcium glycerophosphate- is a mineral supplement for Calcium."
    },
    "sideEffects": {
      "ua": "Безпечно при звичайному харчовому вживанні в межах встановлених норм.",
      "en": "Generally recognized as safe within established dietary intake limits."
    },
    "healthWarnings": [],
    "regulatoryStatus": {
      "ua": "Зареєстровано в Codex Alimentarius",
      "en": "Standardized in Codex Alimentarius"
    },
    "commonProducts": {
      "ua": "Промислові харчові продукти відповідно до категорії",
      "en": "Commercial food products according to technological category"
    }
  }
];

/**
 * Searches E-Numbers database by code, name, alternate names, or keywords
 */
export function searchEAdditives(query: string): EAdditiveItem[] {
  if (!query || query.trim() === '') return E_NUMBERS_DATABASE;

  const rawQ = query.trim().toLowerCase();
  const q = rawQ.replace(/^e-?/i, 'e'); // Normalize E-102 -> E102
  const numOnly = rawQ.replace(/^e-?/i, '');

  return E_NUMBERS_DATABASE.filter(item => {
    const itemCodeLower = item.code.toLowerCase();
    const itemNum = itemCodeLower.replace('e', '');

    const codeMatch = 
      itemCodeLower.includes(q) || 
      itemNum === numOnly ||
      itemCodeLower.startsWith(q);

    const nameMatch = item.name.toLowerCase().includes(rawQ);
    const altNamesMatch = item.alternateNames?.some(alt => alt.toLowerCase().includes(rawQ));
    const catMatch = item.category.toLowerCase().includes(rawQ) || 
                     item.categoryLabel.ua.toLowerCase().includes(rawQ) || 
                     item.categoryLabel.en.toLowerCase().includes(rawQ);
    const descMatch = item.description.ua.toLowerCase().includes(rawQ) || 
                      item.description.en.toLowerCase().includes(rawQ) ||
                      item.purpose.ua.toLowerCase().includes(rawQ) ||
                      item.purpose.en.toLowerCase().includes(rawQ);

    return codeMatch || nameMatch || altNamesMatch || catMatch || descMatch;
  });
}

/**
 * Parses a raw ingredient text string and extracts all matching E-numbers and additive names.
 */
export function batchDecodeIngredientText(text: string): BatchDecodeResult {
  if (!text || text.trim() === '') {
    return {
      detectedEAdditives: [],
      unknownEAdditives: [],
      safetyScore: 100,
      verdict: { ua: 'Склад порожній', en: 'No ingredients provided' },
      summary: { ua: 'Введіть текст складу для аналізу', en: 'Enter ingredient list to decode' },
      highRiskCount: 0,
      cautionCount: 0,
      safeCount: 0
    };
  }

  const foundCodes = new Set<string>();

  // 1. Match E / Е prefix (both Latin E and Cyrillic Е) with optional dash/space
  const eRegex = /(?:[EЕeе][- ]?|\bE|\bЕ)(\d{3,4}[a-zа-яA-ZА-Я0-9]{0,3})\b/gi;
  const matches = Array.from(text.matchAll(eRegex));
  matches.forEach(m => {
    let suffix = m[1].toUpperCase();
    if (suffix.endsWith('D') || suffix.endsWith('Д')) suffix = suffix.slice(0, -1) + 'd';
    if (suffix.endsWith('A') || suffix.endsWith('А')) suffix = suffix.slice(0, -1) + 'a';
    if (suffix.endsWith('B') || suffix.endsWith('Б')) suffix = suffix.slice(0, -1) + 'b';
    if (suffix.endsWith('C') || suffix.endsWith('В')) suffix = suffix.slice(0, -1) + 'c';
    const code = `E${suffix}`;
    foundCodes.add(code);
  });

  // 2. Match INS / SIN prefix (International Numbering System, common in Asia/Americas/Europe)
  const insRegex = /\b(?:INS|SIN)[- ]?(\d{3,4}[a-zа-яA-ZА-Я0-9]{0,3})\b/gi;
  const insMatches = Array.from(text.matchAll(insRegex));
  insMatches.forEach(m => {
    let suffix = m[1].toUpperCase();
    foundCodes.add(`E${suffix}`);
  });

  // 3. Match bracketed codes like (E300), (330), (621), (E965), (INS 500)
  const bracketRegex = /\((?:[EЕeе][- ]?|INS[- ]?|SIN[- ]?)?(\d{3,4}[a-zа-яA-ZА-Я0-9]{0,3})\)/gi;
  const bracketMatches = Array.from(text.matchAll(bracketRegex));
  bracketMatches.forEach(m => {
    let suffix = m[1].toUpperCase();
    if (suffix.endsWith('D') || suffix.endsWith('Д')) suffix = suffix.slice(0, -1) + 'd';
    if (suffix.endsWith('A') || suffix.endsWith('А')) suffix = suffix.slice(0, -1) + 'a';
    if (suffix.endsWith('B') || suffix.endsWith('Б')) suffix = suffix.slice(0, -1) + 'b';
    if (suffix.endsWith('C') || suffix.endsWith('В')) suffix = suffix.slice(0, -1) + 'c';
    const code = `E${suffix}`;
    foundCodes.add(code);
  });

  const detected: EAdditiveItem[] = [];
  const unknownCodes: string[] = [];

  // Match found codes against database
  foundCodes.forEach(code => {
    const baseCode = code.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const dbEntry = E_NUMBERS_DATABASE.find(item => 
      item.code.toUpperCase() === baseCode ||
      item.code.toUpperCase().replace(/[^A-Za-z0-9]/g, '') === baseCode ||
      item.alternateNames?.some(alt => alt.toUpperCase() === baseCode)
    );
    if (dbEntry) {
      if (!detected.some(d => d.code === dbEntry.code)) {
        detected.push(dbEntry);
      }
    } else {
      unknownCodes.push(code);
    }
  });

  // Also check for additive names in text
  const lowerText = text.toLowerCase();
  E_NUMBERS_DATABASE.forEach(item => {
    if (!detected.some(d => d.code === item.code)) {
      const nameMatch = item.name.length > 3 && lowerText.includes(item.name.toLowerCase());
      const altMatch = item.alternateNames?.some(alt => {
        const altLower = alt.toLowerCase();
        if (altLower.length <= 3 || altLower.startsWith('wikidata')) return false;
        return lowerText.includes(altLower);
      });
      if (nameMatch || altMatch) {
        detected.push(item);
      }
    }
  });

  // Calculate score & counts
  let penalty = 0;
  let highRiskCount = 0;
  let cautionCount = 0;
  let safeCount = 0;

  detected.forEach(item => {
    if (item.risk === 'banned' || item.risk === 'avoid') {
      penalty += 30;
      highRiskCount++;
    } else if (item.risk === 'caution') {
      penalty += 12;
      cautionCount++;
    } else {
      safeCount++;
    }
  });

  const safetyScore = Math.max(0, Math.min(100, 100 - penalty));

  let verdictUa = 'Високий рівень безпеки';
  let verdictEn = 'High Safety Level';

  if (safetyScore < 40) {
    verdictUa = '🔴 Високий ризик! Містить шкідливі або заборонені добавки';
    verdictEn = '🔴 High Risk! Contains harmful or banned additives';
  } else if (safetyScore < 75) {
    verdictUa = '🟡 Помірний ризик! Вживати з обережністю';
    verdictEn = '🟡 Moderate Caution! Consume with awareness';
  } else {
    verdictUa = '🟢 Безпечний склад';
    verdictEn = '🟢 Safe Ingredients';
  }

  const summaryUa = `Знайдено ${detected.length} харчових добавок (${highRiskCount} високого ризику, ${cautionCount} підвищеної уваги, ${safeCount} безпечних).`;
  const summaryEn = `Detected ${detected.length} food additives (${highRiskCount} high risk, ${cautionCount} caution, ${safeCount} safe).`;

  return {
    detectedEAdditives: detected,
    unknownEAdditives: unknownCodes,
    safetyScore,
    verdict: { ua: verdictUa, en: verdictEn },
    summary: { ua: summaryUa, en: summaryEn },
    highRiskCount,
    cautionCount,
    safeCount
  };
}
